import { contentFlowHu } from './contentFlow';
import { websiteAnalyticsHu } from './websiteAnalytics';
import { playAnalyticsHu } from './playAnalytics';
import { contentWizardHu } from './contentWizard';
/**
 * Hungarian UI text — THE SOURCE dictionary.
 *
 * Its shape defines `Messages`; `en.ts` must match it key for key, or
 * `tsc` fails. Text moved here must stay letter-for-letter identical to
 * what the screen showed before (plan: docs/ai/terv-2026-10-06-i18n.md).
 *
 * Parameterised text is a function. Numbers arrive already formatted
 * (`formatNumber`, `formatArea`…) unless the text depends on the count.
 */
export const hu = {
  websiteAnalytics: websiteAnalyticsHu,
  playAnalytics: playAnalyticsHu,
  // Empty Hungarian bodies preserve the original reviewed JSX verbatim.
  legalDocuments: {
  "privacyTitle": "Adatkezelési tájékoztató",
  "termsTitle": "Általános Szerződési Feltételek",
  termsUpdated: 'Hatályos: 2026. november 2-től.',
  privacyUpdated: 'Hatályos: 2026. november 2-től.',
  termsFeesTitle: '6. A szolgáltatás díja',
  termsFeesNote: 'A GRUNDO jelenleg ingyenes. A regisztrációért és az összes jelenleg elérhető felhasználói funkcióért nem kérünk díjat. Az alkalmazáson belül és azon kívül sem kínálunk megvásárolható digitális tartalmat vagy szolgáltatást.',
  privacyPaymentTitle: '1.6 Fizetési adatok',
  privacyPaymentNote: 'A GRUNDO jelenleg ingyenes. Nem kezelünk fizetési, bankkártya- vagy számlázási adatokat.',
  "supportTitle": "Támogatás",
  "deletionTitle": "Fiók törlése",
  "signIn": "Belépés a törléshez",
  "loading": "Betöltés…",
  "privacy": "",
  "terms": "",
  "support": "",
  "deletionIntro": "",
  "deletionHelp": ""
},
  time: {
    now: 'most',
    minutesAgo: (minutes: number) => `${minutes} perce`,
    hoursAgo: (hours: number) => `${hours} órája`,
    yesterday: 'tegnap',
    daysAgo: (days: number) => `${days} napja`,
  },

  effort: {
    pace: 'tempó',
    speed: 'seb.',
  },

  dock: {
    pause: 'Szünet',
    resume: 'Folytatás',
    newRecording: 'Új rögzítés',
    startsIn: (seconds: number) => `Indul: ${seconds}`,
    chooseActivityType: 'Válassz mozgásformát',
    startActivity: 'Aktivitás indítása',
    newLap: 'Új kör',
    go: 'RAJT!',
    discardUnconfirmedActivity:
      'A mentés nem igazolódott vissza a szerverről. Ha az aktivitás már látszik a profilodon, nyugodtan elvetheted ezt a helyi példányt. Elveted, és új rögzítést kezdesz?',
    recordingControls: 'Rögzítés vezérlése',
    mainNavigationAndRecording: 'Fő navigáció és rögzítés',
    home: 'Kezdőlap',
    grund: 'Grund',
    community: 'Közösség',
    profile: 'Profil',
  },

  home: {
    search: 'Keresés',
    messages: 'Üzenetek',
    messagesUnread: (count: number) => `Üzenetek, ${count} olvasatlan`,
    messageRequests: (count: number) => `Üzenetek, ${count} üzenetkérelem`,
    notifications: 'Értesítések',
    notificationsUnread: (count: number) => `Értesítések, ${count} olvasatlan`,
    verifyEmail: {
      title: 'Hitelesítsd az e-mail-címed',
      body: 'Hét napod van rá. Utána a közösségi funkciók zárolódnak — a rögzítés és a területfoglalás soha.',
    },
    greeting: 'Szia,',
    guestName: 'sportoló',
    metrics: {
      grund: 'Grund',
      cells: (_count: number, formatted: string) => `${formatted} mező`,
      activity: 'Aktivitás',
      streak: 'Sorozat',
      days: (_count: number) => 'nap',
    },
    refhexLoading: 'REFHEX betöltése…',
    assistantPlaceholder: 'Mihez van ma kedved?',
    banners: {
      label: 'Ajánlók',
      crews: {
        label: 'BANDÁK',
        text: 'Alapíts bandát vagy csatlakozz egybe és küzdjetek együtt a területekért!',
        cta: 'Bandák',
      },
      rivals: {
        label: 'RIVÁLISOK',
        text: 'Nézd meg kik raboltak tőled és kiktől raboltál Te a legtöbb területet és vágj vissza!',
        cta: 'Riválisok',
      },
      routes: {
        label: 'ÚTVONALTERVEZÉS',
        text: 'Tervezz oda-vissza útvonalakat, különböző kerülőket, fedezz fel új helyeket!',
        cta: 'Útvonalak',
      },
      ariaLabel: (label: string, cta: string) => `${label} — ${cta}`,
    },
    mission: {
      open: 'A mai küldetésed megnyitása',
      request: 'Küldetés kérése',
      label: 'A mai küldetésed',
      empty: 'Nem tudod merre menj? Mutatunk pár útvonalat ami érdekes lehet.',
      cta: 'Útvonalak',
      raidWithVictim: (area: string, victim: string) => `Elvehetsz ${area}-t ${victim} grundjából.`,
      raidLocal: (area: string) => `Elvehetsz ${area}-t egy helyi játékostól.`,
      fortify: (cells: string) => `Megerősítheted a meglévő grundodat — ${cells} mező védelme nő.`,
      explore: (blocks: string) => `${blocks} körzet, ahol még egyetlen meződ sincs.`,
      conquest: (area: string) => `Megszerezhetsz ${area} új területet.`,
    },
  },

  tracking: {
    closeMessage: 'Üzenet bezárása',
    remoteTitle: 'Mobilos aktivitásod legutóbbi állapota',
    remoteRecording: ' folyamatban',
    remotePaused: ' szünetel',
    remoteFinished: ' befejezve',
    syncNow: 'éppen most frissült',
    syncSeconds: (seconds: number) => `${seconds} mp-e frissült`,
    syncMinutes: (minutes: number) => `${minutes} perce frissült`,
    syncHours: (hours: number) => `${hours} órája frissült`,
    wakeNote: 'Tartsd bekapcsolva a képernyőt. Böngészőben a rögzítés megáll, ha a telefon lezáródik vagy másik appra váltasz.',
    wakeActive: ' A képernyőt ébren tartjuk.',
    wakeFailed: ' A képernyő ébren tartása nem sikerült — állítsd hosszabbra a képernyő-időkorlátot.',
    tooShortTitle: 'Ez a rögzítés túl rövid lett.',
    tooShortBody: (meters: number, _distance: string) => `Legalább ${meters} méter kell ahhoz, hogy az aktivitás számítson — terület és pont nem jár érte.`,
    mute: 'Hangok némítása',
    unmute: 'Hangok bekapcsolása',
    start: 'Indítás',
    closePicker: 'Mozgásforma-választó bezárása',
    routeSelected: 'Útvonal kiválasztva',
    minutes: (minutes: number) => `${minutes} perc`,
    startWith: 'Indítsd a',
    playButton: 'gombbal.',
    modify: 'Módosítás',
    discard: 'Elvetés',
    routeGpx: 'GPX',
    routeGpxAria: 'Útvonal letöltése GPX-fájlként a fejegységre vagy az órára',
    routeGpxName: (distance: string) => `GRUNDO-kör, ${distance}`,
    routeGpxHint: 'Kövesd a fejegységeden vagy az órádon — de a GRUNDO-val rögzíts, hogy számítson.',
    routeGpxFailed: 'Az útvonal letöltése nem sikerült.',
    assistantTitle: 'Mondd el, hová mennél',
    assistantPlaceholder: 'Pl. sík futókör a Margitszigeten',
    chooseType: 'Válassz mozgásformát!',
    activityType: 'Mozgásforma',
    run: 'Futás',
    walk: 'Séta',
    ride: 'Bringa',
    modeTutorial: 'Ha csak területet foglalnál útvonal nélkül, válaszd a Barangolást; ha konkrét célod van, próbáld ki az Útvonaltervezést.',
    startMode: 'Hogyan indulsz',
    roam: 'Barangolás',
    route: 'Útvonal',
    typeRequired: 'Előbb válassz mozgásformát — az útvonal ettől függ.',
    savedRoutes: 'Mentett útvonalak',
    cancel: 'Mégse',
    savingSections: (done: number, total: number, elapsed: string) => `${done} / ${total} szakasz · ${elapsed}`,
    locallySaved: 'Nyugodtan bezárhatod az appot — az aktivitás megmarad ezen az eszközön, és értesítünk, ha elkészült.',
    localSavePending: 'A helyi biztonsági mentés még készül — egy pillanatig ne zárd be az appot.',
    reconnecting: 'A mentést biztonságban tartjuk…',
    showLaps: (count: number) => `Körök megjelenítése (${count})`,
    hideLaps: 'Körök elrejtése',
    lap: (number: number) => `${number}. kör`,
    fewerLaps: 'Kevesebb',
    allLaps: (count: number) => `Mind a ${count} kör`,
    mapFallback: 'A nyomvonalad itt jelenik meg, amint elindulsz. Utcatérkép csak beállított Mapbox-tokennel látszik.',
    closeNavigation: 'Navigáció leállítása és az útvonal törlése',
    openNavigation: (title: string) => `${title}. Navigáció megnyitása.`,
    routeNavigation: 'Útvonal-navigáció',
    recordingView: 'Rögzítési nézet',
    navigation: 'Navigáció',
    completed: 'Megtett',
    remaining: 'Hátra',
    arrival: 'Érkezés',
    pickStart: 'Koppints a rajtra',
    pickDestination: 'Koppints a célra',
    pickStop: 'Koppints a megállóra',
    followRoute: 'Útvonal követése',
    arrive: 'Érkezés a célhoz',
    depart: 'Indulás',
    continue: 'Haladj tovább',
    time: 'idő',
    pace: 'tempó',
    cells: 'mező',
    pause: 'Szünet',
    automatic: '(automatikus)',
    autoPauseHint: 'Megálltál — induláskor magától folytatódik.',
    pauseHint: 'A mérés áll — a PLAY gombbal folytathatod.',
    collapseStats: 'Adatok összecsukása',
    expandStats: 'Adatok kinyitása',
    speed: 'sebesség',
    distanceCovered: 'megtett táv',
    fullScreen: 'Teljes képernyős nézet',
    exitFullScreen: 'Kilépés a teljes képernyős nézetből',
    saveFailed: 'A mentés nem sikerült.',
    retry: 'Újrapróbálom',
    summaryPending: 'Mentve — az összegzés még nem érkezett meg.',
    reviewTitle: 'Mentés előtt',
    reviewBody: 'Elfelejtetted időben leállítani? Vágd le a végét vagy az elejét odáig, ahol még valóban mozogtál.',
    reviewSave: 'Mentés',
    reviewTrim: 'Vágás',
    reviewDiscard: 'Elvetés',
    reviewDiscardConfirm: 'Elveted ezt a rögzítést? Nem kerül mentésre, és nem kapsz érte se területet, se pontot.',
    trimTitle: 'Nyomvonal vágása',
    trimHint: 'A levágott szakasz szürkén, szaggatottan látszik a térképen. Nem kerül mentésre, és nem számít bele se a területbe, se a pontokba.',
    trimStart: 'Eleje',
    trimEnd: 'Vége',
    trimStartAria: 'A megtartott rész eleje',
    trimEndAria: 'A megtartott rész vége',
    trimRemoved: (distance: string, duration: string) => `levágva ${distance} · ${duration}`,
    trimNothingRemoved: 'nincs levágva',
    trimReset: 'Visszaállítás',
    trimDone: 'Kész',
    trimTooShort: (distance: string) => `A megtartott rész rövidebb, mint ${distance} — így nem menthető.`,
    duplicate: 'Ez a rögzítés már mentve volt.',
    saved: 'Mentve.',
    distance: 'táv',
    area: 'terület',
    loops: 'bezárás',
    liveStream: 'Élő közvetítés',
    liveEnabled: 'Nyilvános linken követhető. Bekapcsolva marad, amíg ki nem kapcsolod.',
    liveDisabled: 'Kikapcsolva. Késleltetés és link: Beállítások → Élő közvetítés.',
  },

  badges: {
    title: 'Jelvények',
    fewer: 'Kevesebb',
    show: 'Mutasd a badge-eket',
    all: 'Összes jelvény',
    emptyTitle: 'Még nincs jelvényed',
    emptyHint: 'Mozogj, foglalj területet, és az első jelvényed hamarosan megérkezik.',
    locked: 'még nincs meg',
    earned: (date: string) => `megszerezve: ${date}`,
    newBadge: 'Új jelvény',
    new: 'ÚJ',
    progress: (value: string) => `Haladás: ${value}`,
    reward: (gp: string) => `Jutalom: ${gp}`,
    pageLeft: (title: string) => `${title}: lapozás balra`,
    pageRight: (title: string) => `${title}: lapozás jobbra`,
    ladders: {
      distance_foot: 'Gyalogos táv', distance_bike: 'Bringás táv', territory: 'Birodalom',
      conqueror: 'Hódító', streak: 'Napi sorozat', week_streak: 'Heti sorozat', loyalty: 'Hűség', explorer: 'Felfedező',
      comments: 'Nagy dumás', likes_received: 'LOVE', likes_given: 'Cupido',
    },
    unique: {
      first_activity: { name: 'Első lépés', description: 'Rögzítetted az első aktivitásodat.' },
      first_loop: { name: 'Első bezárt kör', description: 'Bezártál egy kört, és terület lett a tiéd.' },
      first_steal: { name: 'Első hódítás', description: 'Elvettél egy darab területet egy másik játékostól.' },
      first_photo: { name: 'Hi there!', description: 'Feltöltötted az első profilképedet.' },
      first_push: { name: 'FOMO', description: 'Bekapcsoltad a PUSH értesítéseket.' },
      first_banda_owner: { name: 'Finucci', description: 'Alapítottál egy bandát.' },
      first_banda_member: { name: 'Toretto', description: 'Beléptél valaki bandájába.' },
      first_rival: { name: 'PvP', description: 'Megvan az első riválisod.' },
      first_route: { name: 'Forrest', description: 'Tervezett útvonalból indítottad az első rögzítést.' },
      first_mission: { name: 'Top Gun', description: 'Küldetésből indítottad az első rögzítést.' },
    },
    distanceName: (label: string, distance: string) => `${label} — ${distance}`,
    distanceDescription: (label: string, distance: string) => `Összesen ${distance}-t tettél meg ${label.toLowerCase()}.`,
    territoryName: (area: string) => `Birodalom — ${area}`,
    territoryDescription: (area: string) => `Összesen ${area} terület a tiéd, valaha egyszerre birtokolva.`,
    conquerorName: (count: number) => `Hódító — ${count}×`,
    conquerorDescription: (count: number) => `${count} sikeres területrablás áll mögötted.`,
    days: (count: number) => `${count} nap`,
    daysUnit: (_count: number) => 'nap',
    weeksUnit: (_count: number) => 'hét',
    dayStreakName: (count: number) => `${count} napos sorozat`,
    dayStreakDescription: (count: number) => `${count} egymást követő napon voltál aktív.`,
    weeks: (count: number) => `${count} hét`,
    weekStreakName: (count: number) => `${count} hetes sorozat`,
    weekStreakDescription: (count: number) => `${count} egymást követő héten voltál aktív.`,
    loyaltyPeriods: ['1 hónap', '6 hónap', '1 év', '2 év', '3 év', '4 év', '5 év', '10 év', '15 év', '20 év'],
    loyaltyName: (period: string) => `${period} a GRUNDO-ban`,
    loyaltyDescription: (period: string) => `${period} telt el a csatlakozásod óta.`,
    countries: (count: number) => `${count} ország`,
    countriesUnit: (_count: number) => 'ország',
    explorerName: (count: number) => `Felfedező — ${count} ország`,
    explorerDescription: (count: number) => `${count} ország van az útleveledben.`,
    comments: (count: number) => `${count} komment`,
    hearts: (count: number) => `${count} szív`,
    socialName: (label: string, count: string) => `${label} — ${count}`,
    commentsDescription: (count: number) => `${count} hozzászólást írtál mások aktivitásaihoz.`,
    likesReceivedDescription: (count: number) => `${count} szívecskét kaptál az aktivitásaidra.`,
    likesGivenDescription: (count: number) => `${count} szívecskét adtál mások aktivitásaira.`,
  },

  liveViewer: {
    loading: 'Betöltés…',
    unknownChannel: 'Nincs ilyen csatorna',
    unknownUser: (username: string) => `„${username}" néven nem találtunk felhasználót.`,
    offline: 'Most nincs élő rögzítés',
    offlineHint: 'Ha elindul egy menet, ez az oldal magától megjelenik.',
    finished: 'A rögzítés véget ért',
    mapUnavailable: 'A térkép nem érhető el.',
    run: 'Futás',
    walk: 'Séta',
    ride: 'Kerékpár',
    distance: 'Táv',
    time: 'Idő',
    cells: 'Mezők',
    pace: 'Tempó',
    paused: 'A rögzítés szünetel. ',
    delayBefore: 'A nézet szándékosan',
    delaySeconds: (seconds: number) => `${seconds} másodperccel`,
    delayAfter: ' késik.',
  },

  routePlanner: {
    title: 'Útvonal tervezése',
    assistantApplied: 'Grout beállította',
    points: 'Pontok',
    start: 'Rajt',
    stop: (index: number) => `Megálló ${index}`,
    destination: 'Cél',
    addStop: '+ Megálló hozzáadása',
    planning: 'Tervezés',
    routeType: 'Útvonaltípus',
    loop: 'Oda-vissza',
    direct: 'Csak oda',
    directHint: 'Nem zár kört, ezért területet nem ad — csak a távért járó pontot.',
    detour: 'Kerülő mérete',
    small: 'Kis',
    medium: 'Közepes',
    large: 'Nagy',
    detourHint: 'A kerülő mérete a bezárt terület mérete.',
    busy: 'Tervezés…',
    busyHint: 'Ez néhány másodpercig tart.',
    noPoint: 'Nincs kijelölve',
    removePoint: (title: string) => `${title} törlése`,
    searchPlaceholder: 'Keress címre…',
    searchPoint: (title: string) => `${title} keresése cím alapján`,
    pickPoint: (title: string) => `${title} kijelölése a térképen`,
    pickOnMap: 'Kijelölés a térképen',
    usePosition: 'Jelenlegi pozícióm használata',
    position: 'Jelenlegi pozícióm',
    searching: 'Keresés…',
    positionFailedHint: 'Nem sikerült meghatározni a helyzetedet. Engedélyezd a helymeghatározást, vagy jelöld ki a rajtot a térképen.',
    positionFailed: 'Nem sikerült meghatározni a helyzetedet.',
    planFailed: 'Nem sikerült megtervezni az útvonalat.',
    sameStartConfirm: 'A rajt és a cél ugyanott van. Ugyanide szeretnél visszaérni, ahonnan indulsz?',
    loopNeedsStop: 'Kört egy megállón át tudunk tervezni: adj hozzá legalább egy megállót, amerre menni szeretnél.',
  },
  routeQuality: {
    preference: 'Jelleg',
    fast: 'Gyors',
    protected: 'Védett út',
    quiet: 'Nyugalom',
    terrain: 'Terep',
    soon: 'Hamarosan',
    flat: 'Sík',
    balanced: 'Vegyes',
    hilly: 'Dombos',
    cycleways: 'Kerékpárutak előnyben',
    off: 'Ki',
    on: 'Be',
  },
  routeReward: {
    title: 'Zsákmány',
    noMatch: 'Nincs pontos egyezés',
    partial: 'Részben teljesült',
    warning: 'Erre figyelj',
    variants: 'Útvonal-változatok',
    requested: 'Ahogy kérted',
    unmetHint: 'Próbálj másik célt, vagy módosítsd a beállításokat.',
    warningHint: 'A terv így is járható — ha zavar, próbálj kisebb kerülőt vagy másik célt.',
    claimableCells: 'megszerezhető mező',
    stolenCells: 'ebből elvett',
    freeCells: (cells: string) => `${cells} szabad`,
    cells: (_count: number, formatted: string) => `${formatted} mező`,
    area: 'terület',
    areaGp: 'GP a területből',
    rivals: 'Tőlük veszel el a legtöbbet',
    edgeEstimate: 'Becslés — ekkora területnél a mezők tulajdonosát csak a szélen néztük meg, és menet közben más is mozoghat itt.',
    estimate: 'Becslés — a tényleges zsákmány attól függ, hogyan haladsz, és közben más is mozoghat itt.',
    noArea: 'Ez az útvonal nem zár be területet — a távért járó pontot viszont megkapod.',
    directNoArea: 'A „Csak oda” útvonal nem zár kört, ezért területet nem ad — a távért járó pontot viszont megkapod.',
    showRoute: 'Mutasd az útvonalat',
    modify: 'Útvonal módosítása',
    quiet: (percent: string) => `${percent} csendes`,
    protected: (percent: string) => `${percent} védett`,
    cycleway: (percent: string) => `${percent} bringabarát`,
    steep: (distance: string) => `${distance} meredek`,
    elevation: (rate: string) => `${rate} emelkedés`,
  },
  routePreview: {
    unavailable: 'A térkép most nem érhető el.',
    title: 'A teljes útvonal',
    gestureHint: 'Csippents a nagyításhoz, két ujjal forgasd el.',
    go: 'Gyerünk!',
    back: 'Vissza',
  },
  savedRoutes: {
    title: 'Mentett útvonalak',
    empty: 'Még nincs mentett útvonalad. A Küldetések fülön generálhatsz ajánlatot, ott a kártyán a „Mentés" gombbal teheted el.',
    start: 'Indítás',
    remove: 'Törlés',
  },
  missionMeta: {
    conquest: 'Hódítás',
    raid: 'Rajtaütés',
    fortify: 'Erősítés',
    explore: 'Felfedezés',
    reinforced: 'Megerősített',
    newArea: 'Új terület',
  },

  /** Nyilvános aktivitásnézet kijelentkezve (#105) — a megosztott link látogatójának. */
  publicActivity: {
    stats: { distance: 'táv', time: 'idő', area: 'terület', gp: 'pont' },
    tagline: 'Fuss, sétálj, bringázz — és foglald el a környéked a GRUNDO-ban.',
    signIn: 'Belépés',
    appStore: 'Letöltés: App Store',
    googlePlay: 'Letöltés: Google Play',
    whatIs: 'De mi az a GRUNDO?',
    unavailableTitle: 'Ez az aktivitás nem elérhető',
    unavailableLead: 'Lehet, hogy törölték, vagy csak a gazdája követői láthatják. Ha van fiókod, lépj be.',
  },

  publicProfile: {
    streak: (days: number) => `${days} napos sorozat`,
    unmuteUser: (username: string) => `@${username} némításának feloldása`,
    muteUser: (username: string) => `@${username} némítása`,
    unblockUser: (username: string) => `@${username} tiltásának feloldása`,
    blockUser: (username: string) => `@${username} letiltása`,
    reportUser: (username: string) => `${username} jelentése`,
    memberSince: (date: string) => `${date} óta tag`,
    apiUnavailable: "A háttérszolgáltatás nincs beállítva, így a profil nem tölthető be.",
    notFound: "Nincs ilyen felhasználó.",
    loadFailed: "Nem sikerült betölteni a profilt.",
    actionFailed: "A művelet nem sikerült.",
    back: "Vissza",
    title: "Profil",
    more: "További lehetőségek",
    retry: "Újrapróbálom",
    loading: "Betöltés…",
    followRequested: "Elküldtük a követési kérést.",
    unblocked: "A tiltást feloldottad.",
    message: "Üzenet",
    followsYou: "Követ téged",
    virtualProfile: "Virtuális profil",
    virtualProfileHint: "A játék indulásakor virtuális profilok segítik, hogy legyen mit elfoglalni. Ahogy egyre több játékos csatlakozik, eltűnnek.",
    blocked: "Letiltottad ezt a felhasználót",
    private: "Privát fiók",
    blockedHint: "Nem látod az aktivitásait, és ő sem a tieidet. A tiltás feloldásával újra láthatóvá válik.",
    privateHint: "A profil tartalmát csak az elfogadott követők látják. Küldj követési kérést, ha kíváncsi vagy rá.",
    followers: "követő",
    followingLabel: "követett",
    grund: "grund",
    totalDistance: "összes táv",
    activities: "aktivitás",
    cells: "mező",
    recent: "Legutóbbi aktivitások",
    close: "Bezárás",
    options: "Lehetőségek",
    report: "Felhasználó jelentése",
    unmuted: "A némítást feloldottad.",
    mutedHint: "Némítottad: az aktivitásai nem jelennek meg a feededben. A követés megmarad, és ő nem értesül róla.",
    blockedNotice: "Letiltottad ezt a felhasználót.",
    cancel: "Mégse",
    unblock: "Tiltás feloldása",
    following: "Követed",
    requested: "Kérés elküldve",
    requestFollow: "Követés kérése",
    follow: "Követés",
  },

  crews: {
    members: (_count: number, formatted: string) => `${formatted} tag`,
    invitedBy: (username: string) => `${username} hívott meg`,
    privateLower: 'privát',
    publicLower: 'publikus',
    created: (name: string) => `„${name}" létrejött`,
    inviteCodeBefore: 'Meghívókód:',
    inviteCodeAfter: ' — ezt oszd meg azokkal, akiket meg akarsz hívni.',
    loadFailed: "A bandák most nem tölthetők be.",
    browse: "Bandák böngészése",
    popular: "Népszerű Bandák",
    newCrews: "Új Bandák",
    loading: "Betöltés…",
    empty: "Még nincs megjeleníthető banda",
    checkLater: "Nézz vissza később.",
    acceptFailed: "A meghívó most nem fogadható el.",
    declineFailed: "A meghívó most nem utasítható el.",
    invites: "Meghívóim",
    decline: "Elutasítás",
    accept: "Elfogadás",
    myLoadFailed: "A bandáid most nem tölthetők be.",
    myCrews: "Saját bandáim",
    noMembership: "Még nem vagy egy bandában sem",
    noMembershipHint: "Csatlakozz meghívókóddal, keress rá egy publikus bandára, vagy hozz létre egy sajátot.",
    joinFailed: "A csatlakozás most nem sikerült.",
    joinByCode: "Csatlakozás meghívókóddal",
    code: "Meghívókód",
    codePlaceholder: "8 karakter",
    enter: "Belépés",
    createFailed: "A létrehozás most nem sikerült.",
    publicHint: "A banda publikus — bárki megtalálja a keresésben, és azonnal csatlakozhat.",
    open: "Megnyitás",
    createCrew: "+ Banda létrehozása",
    newCrew: "Új banda",
    name: "Név",
    description: "Leírás (nem kötelező)",
    visibility: "Láthatóság",
    public: "Publikus",
    private: "Privát",
    cancel: "Mégse",
    create: "Létrehozás",
    searchFailed: "A keresés most nem működik.",
    searchTitle: "Publikus bandák keresése",
    search: "Banda keresése",
    searchPlaceholder: "Banda neve",
    clearSearch: "Keresés törlése",
    searching: "Keresés…",
    noResults: "Nincs ilyen nevű publikus banda.",
    alreadyMember: " · már tagja vagy",
    join: "Csatlakozás",
  },

  banda: {
    shareText: (name: string) => `Csatlakozz a(z) ${name} bandához!`,
    cover: (name: string) => `${name} borítóképe`,
    codeCopied: (code: string) => `${code}, kód kimásolva`,
    copyCode: (code: string) => `${code}, kód másolása`,
    reportTitle: (name: string) => `${name} jelentése`,
    today: "Mai",
    week: "Heti",
    month: "Havi",
    allTime: "Mindenkori",
    run: "Futás",
    walk: "Séta",
    ride: "Bringa",
    joinFailed: "A csatlakozás most nem sikerült.",
    notifyFailed: "Az értesítés most nem állítható át.",
    leaveConfirm: "Biztosan kilépsz ebből a bandából?",
    removeContentConfirm: "Szeretnéd a bandában írt összes posztodat és kommentedet is eltávolítani?\n\nOK: tartalmak eltávolítása\nMégse: tartalmak megtartása",
    leaveFailed: "Most nem sikerült kilépni a bandából.",
    loadFailed: "A banda most nem tölthető be.",
    crew: "Banda",
    ourCrew: "bandánk",
    title: "BANDÁK",
    loading: "Betöltés…",
    notifyOff: "Banda-értesítések kikapcsolása",
    notifyOn: "Banda-értesítések bekapcsolása",
    settings: "Banda beállításai",
    private: "Privát",
    public: "Publikus",
    code: "Meghívókód",
    invite: "Meghívás",
    copied: "Link másolva",
    share: "Banda megosztása",
    pending: "Jóváhagyásra vár",
    join: "Csatlakozás a bandához",
    sport: "Sportág",
    totalArea: "Terület összesen",
    totalGp: "GP összesen",
    rankLoading: "Ranglista betöltése…",
    noResults: "Még nincs eredmény",
    noResultsHint: "Ebben az időszakban és sportágban még nincs rangsorolható aktivitás.",
    members: "Tagok",
    allMembers: "Összes tag",
    membersOnly: "A hírfolyam és az üzenőfal csak a banda tagjainak látszik — csatlakozz, hogy megnézhesd.",
    leave: "Kilépés a bandából",
    report: "A banda jelentése",
    close: "Bezárás",
  },

  bandaSettings: {
    approved: (username: string) => `${username} csatlakozását jóváhagytad.`,
    rejected: (username: string) => `${username} kérését elutasítottad.`,
    madeModerator: (username: string) => `${username} mostantól moderátor.`,
    madeMember: (username: string) => `${username} mostantól tag.`,
    removeConfirm: (username: string) => `Biztosan kirúgod ${username} felhasználót a bandából?`,
    removed: (username: string) => `${username} eltávolítva a bandából.`,
    transferConfirm: (username: string) => `Biztosan átadod ${username} felhasználónak a banda tulajdonjogát? Te moderátor maradsz.`,
    everyone: "Mindenki",
    moderators: "Moderátorok",
    onlyMe: "Csak én",
    instant: "Azonnali",
    approval: "Jóváhagyással",
    restricted: "Csak a banda alapítója vagy egy moderátor nyithatja meg ezt a képernyőt.",
    loadFailed: "A banda beállításai most nem tölthetők be.",
    profileUpdated: "A banda profilképe frissült.",
    coverUpdated: "A banda borítóképe frissült.",
    uploadFailed: "A képet most nem sikerült feltölteni.",
    reviewFailed: "A csatlakozási kérést most nem sikerült elbírálni.",
    saved: "A banda beállításai elmentve.",
    saveFailed: "A beállításokat most nem sikerült menteni.",
    roleFailed: "A szerepkört most nem sikerült módosítani.",
    removeFailed: "A tagot most nem sikerült eltávolítani.",
    transferFailed: "A tulajdonjogot most nem sikerült átadni.",
    title: "Banda beállítások",
    save: "Mentés",
    loading: "Betöltés…",
    publicJoin: "Csatlakozás publikus bandához",
    publicJoinHint: "Azonnali belépést vagy alapítói/moderátori jóváhagyást kérhetsz.",
    joinMode: "Publikus csatlakozás módja",
    active: "Aktív beállítás: ",
    unsaved: "Kiválasztva, még nincs mentve: ",
    requests: "Jóváhagyásra várók",
    requestsHint: "Az alapító és a moderátorok is elbírálhatják a kéréseket.",
    noRequests: "Nincs függő csatlakozási kérés.",
    approve: "Jóváhagyás",
    reject: "Elutasítás",
    images: "Banda képei",
    imagesHint: "A képek feltöltéskor tömörítve, EXIF-adatok nélkül kerülnek fel.",
    changeProfile: "Profilkép cseréje",
    cover: "Banda borítókép",
    noCover: "Nincs borítókép",
    changeCover: "Borítókép cseréje",
    whoInvites: "Ki hívhat meg tagokat?",
    inviteHint: "Az appon belüli meghívás jogosultsága.",
    invitePermission: "Meghívási jogosultság",
    whoSeesCode: "Ki láthatja a meghívókódot?",
    codeHint: "Privát bandánál szabályozza a kód megjelenítését.",
    codeVisibility: "Meghívókód láthatósága",
    whoPosts: "Ki posztolhat a hírfolyamba?",
    postHint: "A chat falra továbbra is minden tag írhat.",
    postPermission: "Hírfolyam-posztolási jogosultság",
    manageMembers: "Tagok kezelése",
    makeMember: "Legyen tag",
    makeModerator: "Legyen moderátor",
    remove: "Kirúgás",
    transferTitle: "Tulajdonjog átadása",
    transferHint: "Az átadás után az új alapító kezeli a beállításokat, te pedig moderátor maradsz.",
    transfer: "Átadás",
    leaveTitle: "Kilépés a bandából",
    leaveHint: "Alapítóként nem léphetsz ki közvetlenül. Előbb add át egy tagnak az alapítói rangot; ezután moderátorként kiléphetsz a banda adatlapjáról. Ha senki nem viszi tovább, lent feloszlathatod a bandát.",
    transferFirst: "Előbb add át az alapítói rangot",
    profileTitle: "Név és leírás",
    profileHint: "A tagok és a kereső azonnal az új nevet látják.",
    profileSave: "Név és leírás mentése",
    profileSaved: "A banda neve és leírása elmentve.",
    profileFailed: "A nevet és a leírást most nem sikerült menteni.",
    city: "Város (nem kötelező)",
    visibilityTitle: "Láthatóság",
    visibilityHint: "Publikus bandát bárki megtalál és csatlakozhat hozzá; privátba csak kóddal vagy meghívással lehet belépni.",
    visibilityLabel: "A banda láthatósága",
    codeTitle: "Meghívókód",
    codeRotateHint: "Ha a kód rossz kezekbe került, cseréld le: a régi kód és a vele megosztott link azonnal érvénytelen lesz.",
    rotateCode: "Kód cseréje",
    rotateConfirm: "Biztosan lecseréled a meghívókódot? A régi kód azonnal érvénytelen lesz.",
    codeRotated: (code: string) => `Új meghívókód: ${code}`,
    rotateFailed: "A kódot most nem sikerült lecserélni.",
    removeProfile: "Profilkép levétele",
    removeCover: "Borítókép levétele",
    banConfirm: (username: string) => `${username} kitiltásra is kerüljön? Kitiltva akkor sem léphet vissza, ha a banda publikus, vagy ismeri a kódot.`,
    bannedRemoved: (username: string) => `${username} eltávolítva és kitiltva.`,
    moderatorHint: "Moderátorként tagot rúghatsz ki és tilthatsz ki; a szerepköröket az alapító kezeli.",
    bansTitle: "Kitiltott felhasználók",
    bansHint: "A feloldás után újra csatlakozhatnak.",
    noBans: "Nincs kitiltott felhasználó.",
    unban: "Feloldás",
    unbanned: (username: string) => `${username} kitiltása feloldva.`,
    unbanFailed: "A kitiltást most nem sikerült feloldani.",
    invitesTitle: "Függő meghívók",
    invitesHint: "A még el nem fogadott meghívók. Visszavonás után a meghívott nem csatlakozhat velük.",
    noInvites: "Nincs függő meghívó.",
    invitedBy: (username: string) => `Meghívta: ${username}`,
    revokeInvite: "Visszavonás",
    inviteRevoked: (username: string) => `${username} meghívója visszavonva.`,
    revokeFailed: "A meghívót most nem sikerült visszavonni.",
    dissolveTitle: "Banda feloszlatása",
    dissolveHint: "Minden tag kikerül, a hírfolyam, a chat és a képek véglegesen törlődnek. Nem vonható vissza.",
    dissolve: "Feloszlatás",
    dissolveConfirm: (name: string) => `Biztosan feloszlatod a(z) „${name}” bandát? Minden tag kikerül, és minden tartalom véglegesen törlődik.`,
    dissolveConfirmFinal: "Ez nem vonható vissza. Tényleg feloszlatod?",
    dissolveFailed: "A bandát most nem sikerült feloszlatni.",
  },

  bandaInvite: {
    loadFailed: "A követett-lista most nem tölthető be. Próbáld később.",
    title: "Meghívás a bandába",
    invite: "Meghívás",
    close: "Bezárás",
    search: "Keresés a követettek között",
    loading: "Betöltés…",
    empty: "Mindenki, akit követsz, már tagja ennek a bandának — vagy még nem követsz senkit.",
    notFound: "Nincs ilyen nevű követett.",
    invited: "Meghívva",
    retry: "Próbáld újra",
  },

  bandaRoles: {
    owner: 'Alapító', moderator: 'Moderátor', member: 'Tag', chat: 'Banda chat',
    unreadChat: (count: number) => `Banda chat, ${count} olvasatlan üzenet`,
  },

  crewChat: {
    mutedUntil: (date: string) => `A banda chatjében némítva vagy eddig: ${date}. Olvasni és szívet adni tudsz.`,
    deleteOtherConfirm: (username: string) => `Biztosan törlöd ${username} üzenetét?`,
    muteTitle: (username: string) => `${username} némítása`,
    cooldown: (seconds: number) => `Túl gyorsan írsz — ${seconds} másodperc múlva írhatsz újra.`,
    sendAfter: (seconds: number) => `Küldés ${seconds} másodperc múlva`,
    mutedForever: "A banda chatjében a feloldásig némítva vagy. Olvasni és szívet adni tudsz.",
    chat: "Banda chat",
    membersOnly: "A banda chatjét csak a tagok látják.",
    privateChat: "Banda chat · csak a tagok látják",
    older: "Korábbi üzenetek",
    loading: "Betöltés…",
    empty: "Még nincs üzenet a banda chatjében. Köszönj be!",
    report: "Üzenet jelentése",
    deletedUser: "Törölt felhasználó",
    you: "Te",
    deleted: "Törölt üzenet",
    unheart: "Szív visszavonása",
    heart: "Szív",
    heartFailed: "A szívet most nem sikerült menteni.",
    reply: "Válasz",
    delete: "Üzenet törlése",
    deleteOwnConfirm: "Biztosan törlöd az üzenetedet?",
    deleteFailed: "Az üzenetet most nem sikerült törölni.",
    mute: "Némítás",
    oneHour: "1 órára",
    oneDay: "24 órára",
    untilLifted: "Feloldásig",
    member: "Tag",
    close: "Bezárás",
    muteFailed: "A némítás most nem sikerült.",
    unmuteFailed: "A némítást most nem sikerült feloldani.",
    unmute: "Némítás feloldása",
    sendFailed: "Az üzenet nem ment el. Próbáld újra.",
    replyTo: "Válasz neki:",
    discardReply: "Válasz elvetése",
    placeholder: "Üzenet a bandának…",
    messageLabel: "Üzenet a bandának",
    send: "Küldés",
  },

  crewFeed: {
    reportPostTitle: (username: string) => `${username} posztjának jelentése`,
    reportCommentTitle: (username: string) => `${username} kommentjének jelentése`,
    placeholder: "Mi újság a bandában?",
    empty: "Még nincs poszt a hírfolyamban.",
    denied: "Ebben a bandában a beállítás szerint most nem te posztolhatsz a hírfolyamba.",
    loadFailed: "Most nem tölthető be.",
    signInImage: "Jelentkezz be a kép feltöltéséhez.",
    sendFailed: "Most nem sikerült elküldeni.",
    deletePostConfirm: "Biztosan törlöd ezt a posztot?",
    deletePostFailed: "A posztot most nem sikerült törölni.",
    imageOnly: "Csak képet lehet feltölteni.",
    imageSize: "A kiválasztott kép legfeljebb 5 MB lehet.",
    editPost: "Poszt szerkesztése",
    cancel: "Mégse",
    formatPost: "Poszt formázása",
    bold: "Félkövér",
    italic: "Dőlt",
    underline: "Aláhúzott",
    bulletList: "Felsorolás",
    numberList: "Számozott felsorolás",
    quote: "Idézet",
    insertLink: "Link beszúrása",
    linkAddress: "Link címe (https://…):",
    invalidLink: "Érvényes http:// vagy https:// linket adj meg.",
    imagePreview: "Csatolandó kép előnézete",
    removeImage: "Csatolt kép eltávolítása",
    addImage: "Kép hozzáadása",
    save: "Mentés",
    publish: "Közzététel",
    loading: "Betöltés…",
    loadMore: "Továbbiak betöltése",
    deleteCommentConfirm: "Biztosan törlöd ezt a kommentet?",
    deleteCommentFailed: "A kommentet most nem sikerült törölni.",
    you: "Te",
    edited: " · szerkesztve",
    edit: "Szerkesztés",
    deletePost: "Poszt törlése",
    reportPost: "Poszt jelentése",
    like: "Kedvelés",
    comment: "Komment",
    replyPrefix: "Válasz —",
    reply: "Válasz",
    deleteComment: "Komment törlése",
    report: "Jelentés",
    commentPlaceholder: "Írj kommentet…",
    send: "Küldés",
    imageAlt: "A poszthoz csatolt kép",
    imageLoading: "Kép betöltése…",
  },

  missions: {
    timeLimits: (minutes: number, hours: number) => `Az időkeret ${minutes} perc és ${hours} óra között lehet.`,
    distanceLimits: (min: string, max: string, unit: string) => `A célhossz ${min} és ${max} ${unit} között lehet.`,
    speedLimits: (min: string, max: string, unit: string) => `Adj meg ${min} és ${max} ${unit} közötti sebességet.`,
    distanceQuestionMiles: 'Hány mérföldet mennél?',
    paceKmUnit: 'perc/km', paceMilesUnit: 'perc/mi',
    quota: (used: number, limit: number) => `Ezen a héten ${used}/${limit} ingyenes generálás.`,
    conquestHeadline: (area: string) => `Ezzel a körrel ${area} új területet szerezhetsz.`,
    raidHeadline: (area: string, username: string) => `Ha erre mész, elvehetsz ${area}-t ${username} grundjából.`,
    raidAnonymousHeadline: (area: string) => `Ha erre mész, elvehetsz ${area}-t egy helyi játékostól.`,
    fortifyHeadline: (cells: string) => `Ez a kör megerősíti a meglévő grundodat — ${cells} mező védelme nő.`,
    exploreHeadline: (blocks: string) => `${blocks} olyan körzet, ahol még egyetlen meződ sincs.`,
    lookingForLoops: 'Köröket keresünk a környéken…',
    checkingArea: 'Megnézzük, mennyi területet zárnak be…',
    choosingBest: 'Kiválasztjuk a legjobbakat…',
    nearlyReady: 'Mindjárt megvan — ez a környék bőven ad lehetőséget.',
    run: "Futás",
    walk: "Séta",
    ride: "Bringa",
    minute: "perc",
    hour: "óra",
    notConfigured: "A háttérszolgáltatás nincs beállítva, a küldetés nem generálható.",
    paceExample: "A tempó formátuma például 6:15.",
    generateFailed: "A küldetés-generálás most nem működik.",
    time: "Idő",
    distance: "Távolság",
    planningBasis: "Tervezés alapja",
    timeQuestion: "Mennyi időd van?",
    distanceQuestion: "Hány kilométert mennél?",
    timeBudget: "Időkeret",
    custom: "egyedi",
    hours: "Óra",
    minutes: "Perc",
    units: "Mértékegység",
    target: "Célhossz",
    sport: "Mozgásforma",
    optionalSpeed: "Tervezett átlagsebesség (opcionális)",
    optionalPace: "Tervezett átlagtempó (opcionális)",
    offerQuestion: "Hány ajánlatot kérsz?",
    offers: "Kért ajánlatok száma",
    countUnit: "db",
    priority: "Elsődleges cél",
    balanced: "Legjobb ajánlat",
    conquest: "Új terület",
    raid: "Rablás",
    fortify: "Grund erősítése",
    explore: "Felfedezés",
    regenerate: "Újragenerálás",
    generate: "Küldetéseket kérek",
    overlayNote: "Valódi útvonalakat keresünk a környéken, nem köröket rajzolunk a térképre.",
    savedMissions: "Mentett küldetések",
    simple: "Egyszerű keresés",
    advanced: "Részletes keresés",
    today: "A ma generált küldetéseid.",
    decrease: "Csökkentés",
    increase: "Növelés",
    plannedSpeed: "Tervezett átlagsebesség",
    plannedPace: "Tervezett átlagtempó",
    targetPrefix: "Célhossz:",
    fromPace: "— a saját tempódból számolva.",
    noRoutes: "Az útvonaltervező most nem adott vissza útvonalat erről a pontról. Próbáld újra, vagy indulj el egy közeli utcáról.",
    noLoops: "Innen most nem jött ki bezárható kör — errefelé kevés az átkötő utca. Próbáld másik mozgásformával, vagy indulj el egy másik pontról.",
    noFit: "Találtunk köröket, de egyik sem fért bele ebbe az időkeretbe. Próbáld hosszabb idővel.",
    noClean: "Találtunk köröket, de mindegyikben fölösleges visszafordulás volt. Próbáld újra, vagy válassz másik irányt.",
    noMissions: "Most nincs ajánlható küldetés.",
    calculating: "Számítás",
    calculatingTerritory: "Megvan az útvonal — most számoljuk ki, mennyi területet ér.",
    area: "Terület",
    areaCalculation: "Területszámítás",
    estimatedGp: "Becsült GP",
    gpCalculation: "Pontszámítás",
    cells: "Mező",
    cellCalculation: "Cellakalkuláció",
    start: "Indítás most",
    save: "Mentés",
    saved: "Mentve",
  },

  inbox: {
    teamName: 'Admin',
    requestCount: (count: number) => `${count} kérelem`,
    unreadCount: (count: number) => `${count} olvasatlan`,
    ownPreview: (body: string) => `Te: ${body}`,
    title: "Üzenetek",
    view: "Üzenetek nézet",
    private: "Privát",
    requests: "Kérelmek",
    crews: "Bandák",
    unreadMessage: "olvasatlan üzenet",
    loading: "Betöltés…",
    noCrew: "Még nem vagy tagja bandának",
    noCrewNote: "A bandád tagjaival itt, a banda chatjében beszélgethetsz. Keress vagy alapíts egy bandát a Közösség oldalon.",
    noConversations: "Még nincs beszélgetésed",
    noRequests: "Nincs üzenetkérelmed",
    noConversationsNote: "Egy felhasználó profiljáról tudsz neki üzenetet írni.",
    noRequestsNote: "Ide kerülnek azoknak az üzenetei, akiktől a beállításaid szerint nem fogadsz közvetlenül üzenetet.",
    image: "Kép",
    deletedUser: "Törölt felhasználó",
    official: "Hivatalos",
    muted: "Némítva",
    crewChat: "Banda chat",
    sayHello: "Még nincs üzenet — köszönj be!",
    deletedMessage: "Törölt üzenet",
  },

  conversation: {
    lastActive: (time: string) => `Utoljára aktív: ${time}`,
    userMuted: (username: string) => `${username} némítva — a feededben sem jelenik meg.`,
    userBlocked: (username: string) => `${username} letiltva.`,
    active: "Aktív most",
    title: "Beszélgetés",
    cannotSend: "Ennek a felhasználónak most nem küldhetsz üzenetet.",
    messages: "Üzenetek",
    notFound: "Nincs ilyen beszélgetés.",
    actionFailed: "A művelet nem sikerült. Próbáld újra.",
    more: "További lehetőségek",
    closeMenu: "Menü bezárása",
    unmuted: "A beszélgetés értesítései újra bekapcsolva.",
    muted: "A beszélgetést elnémítottad.",
    unmute: "Beszélgetés némításának feloldása",
    mute: "Beszélgetés némítása",
    viewProfile: "Profil megtekintése",
    muteUser: "Felhasználó némítása",
    block: "Tiltás",
    deletedAccount: "Törölt fiók",
    official: "Hivatalos üzenet az Admintól",
    older: "Korábbi üzenetek",
    loading: "Betöltés…",
    empty: "Még nincs üzenet. Köszönj be!",
    seen: "Látta",
    requestNote: "üzenetet küldött neked. Ha elfogadod, válaszolhatsz, és a beszélgetéseid közé kerül. A küldő nem tudja meg, ha törlöd.",
    delete: "Törlés",
    accept: "Elfogadás",
    deletedAccountNote: "Ez a felhasználó törölte a fiókját, nem tudsz neki írni.",
    report: "Üzenet jelentése",
    heartFailed: "A szívet most nem sikerült menteni.",
    deletedMessage: "Törölt üzenet",
    you: "Te",
    image: "Kép",
    reply: "Válasz",
    unheart: "Szív visszavonása",
    heart: "Szív",
    expiredImage: "A kép lejárt (24 óráig látható).",
    attachedImage: "Csatolt kép",
    sendFailed: "Az üzenet nem ment el. Próbáld újra.",
    replyPrefix: "Válasz:",
    discardReply: "Válasz elvetése",
    imagePreview: "Csatolandó kép előnézete",
    removeImage: "Csatolt kép eltávolítása",
    attachImage: "Kép csatolása",
    placeholder: "Üzenet…",
    messageLabel: "Üzenet",
    send: "Küldés",
    edit: "Szerkesztés",
    deleteMessage: "Üzenet törlése",
    deleteConfirm: "Biztosan törlöd ezt az üzenetet? A helyén mindkettőtöknél egy „törölt üzenet” jelzés marad.",
    edited: "módosítva",
    deletedByOther: "Ezt az üzenetet törölték.",
    deletedByMe: "Törölted ezt az üzenetet.",
    editing: "Üzenet szerkesztése",
    discardEdit: "Szerkesztés elvetése",
    saveEdit: "Mentés",
    editFailed: "A módosítást most nem sikerült menteni.",
    deleteFailed: "Az üzenetet most nem sikerült törölni.",
    thumbsUp: "Tetszik",
    unThumbsUp: "Tetszik visszavonása",
    reactionFailed: "A reakciót most nem sikerült menteni.",
    messageActions: "Üzenet műveletei",
  },

  settingsHome: {
    auto: "Automatikus",
    light: "Világos",
    dark: "Sötét",
    system: "Rendszer",
    ownPosition: "Saját helyzet",
    title: "Beállítások",
    appearance: "Megjelenés",
    legal: "Jogi",
    appearanceNote: "Világos vagy sötét téma, automatikus váltás",
    graphics: "Grafika",
    graphicsNote: "Térképrészletesség és kirajzolási távolság",
    location: "Lokáció",
    locationNote: "Az időjárás és a pontos idő helye a Kezdőlapon",
    recording: "Rögzítés",
    sounds: "Hangok és rezgések",
    soundsNote: "Rögzítés közbeni hangeffektek, hangerő és rezgés",
    behavior: "Működés",
    behaviorNote: "Automatikus szünet, aktivitás befejezése",
    swipe: "Húzás",
    hold: "Nyomva tartás",
    live: "Élő közvetítés",
    liveNote: "A rögzítésed nyilvános linken követhető — streamhez, overlayhez",
    devices: "Összekapcsolt eszközök",
    devicesNote: "Órák, fejegységek és egészségügyi appok",
    privacyCommunity: "Adatvédelem és közösség",
    emailVerification: "E-mail-hitelesítés",
    emailNote: "Kódot küldünk e-mailben — enélkül hét nap után a közösségi funkciók zárolódnak",
    verified: "Hitelesítve",
    notVerified: "Nincs hitelesítve",
    activityPrivacy: "Aktivitás-adatvédelem",
    activityPrivacyNote: "A megosztott útvonal elejének és végének elrejtése",
    editProfile: "Profil szerkesztése",
    editProfileNote: "Megjelenített név és bemutatkozás",
    privateAccount: "Privát fiók",
    privateAccountNote: "Ki láthatja az aktivitásaidat, és a követési kérések",
    messages: "Privát üzenetek",
    messagesNote: "Kitől fogadsz üzenetet, kéretlen üzenetek, aktivitási állapot",
    blocked: "Tiltott felhasználók",
    blockedNote: "Kiket tiltottál le — innen oldható fel",
    muted: "Némított felhasználók",
    mutedNote: "Akiknek az aktivitásai nem jelennek meg a feededben",
    profileGame: "Profil és játék",
    body: "Fizikai jellemzők",
    bodyNote: "Testsúly, magasság, mértékegységek",
    rules: "Játékszabályok",
    rulesNote: "A jelenleg érvényes pontszámítás és az aktív akciók",
    privacyPolicy: "Adatkezelési tájékoztató",
    terms: "Általános Szerződési Feltételek",
    support: "Támogatás és kapcsolat",
    export: "Adataim letöltése",
    exportNote: "A teljes adatcsomagod JSON és GPX formátumban",
    account: "Fiók",
    notifications: "Értesítések",
    notificationsNote: "Típusonként kapcsolható, alkalmazáson belül és push-ban is",
    lock: "Biometrikus zár",
    lockNote: "Arc, ujjlenyomat, minta vagy jelkód az app megnyitásához",
    loginMethods: "Bejelentkezési módok",
    loginMethodsNote: "E-mail/jelszó, Google és Apple hozzákapcsolása",
    signedIn: "Bejelentkezve",
    signOut: "Kijelentkezés",
    danger: "Veszélyzóna",
    deleteAccount: "Fiók törlése",
    deleting: "Törlés folyamatban",
    irreversible: "Ez a lépés visszafordíthatatlan",
    operations: "Üzemeltetés",
    admin: "Admin felület",
    adminNote: "Játékszabályok, akciók, aktivitás-audit",
    testing: "Tesztelés",
    mode: "Üzemmód",
    debugNote: "Debug módban lebegő hibabejelentő gomb és napló",
    normal: "Normál",
    askOnStart: "Kérdezzen induláskor",
    app: "Alkalmazás",
    version: "Verzió",
    on: "Be",
    off: "Ki",
  },

  appearanceSettings: {
    current: "Jelenleg",
    dark: "Sötét",
    light: "Világos",
    system: "Rendszer",
    auto: "Automatikus",
    sun: "Napnyugta szerint",
    fixed: "Fix időpontok",
    autoHint: "Nappal világos, este sötét.",
    lightHint: "Mindig világos, a napszaktól és a telefon beállításától függetlenül.",
    darkHint: "Mindig sötét, a napszaktól és a telefon beállításától függetlenül.",
    systemHint: "A telefonod rendszerbeállítását követi.",
    colorFailed: "Nem sikerült elmenteni a színt. Próbáld meg újra.",
    color: "A területed színe",
    title: "Megjelenés",
    theme: "Téma",
    switch: "Automatikus váltás",
    switchMode: "Automatikus váltás módja",
    sunHint: "A valódi napkeltét és napnyugtát követi a helyzeted alapján, ezért nyáron később vált sötétre, mint télen. Ha nincs helyadat, a fix időpontok lépnek életbe.",
    darkFrom: "Sötét ettől",
    darkUntil: "Sötét eddig",
    midnight: "Az időszak átnyúlhat éjfélen.",
    recording: "Rögzítés közben",
    claim: "Grund foglalás",
    claimHint: "Felugró visszajelzés és konfetti, amikor bezársz egy kört.",
    claimDuration: "Öt másodpercig látszik, és bármikor bezárható. A hozzá tartozó hangokat a Beállítások → Hangok és rezgések oldalon kapcsolhatod.",
    activeTheme: "Aktív téma",
    recordingHint: "Rögzítés közben nem váltunk témát — ha futás közben megy le a nap, a váltás a mentés után történik meg.",
  },

  finishSettings: {
    title: "Működés",
    recording: "Rögzítés",
    autoPause: "Automatikus szünet",
    autoPauseHint: "Megálláskor magától szünetel, induláskor folytat — hanggal és rezgéssel. Kikapcsolva csak a Szünet gombbal állíthatod meg a mérést.",
    finish: "Aktivitás befejezése",
    finishHint: "Melyik gesztussal szeretnéd befejezni a rögzítést a dokkban? Mindkettőt kipróbálhatod itt, mielőtt választasz.",
    hold: "Nyomva tartás",
    holdHint: "Tartsd nyomva a gombot kb. egy másodpercig — közben a gomb és egy nagy, középre kitett visszajelzés is pirosra töltődik.",
    swipe: "Húzás",
    swipeHint: "Húzd a kerek fogantyút a sáv végéig, jobbra — mint a régi iPhone „csúsztasd a feloldáshoz” gesztusa.",
    tried: "✓ Kipróbálva — pontosan ezt érzed majd rögzítés közben is.",
  },

  graphicsSettings: {
    lowHint: "Minimális térképi animáció és részletesség, hosszú rögzítéshez vagy gyengébb telefonhoz.",
    mediumHint: "Kevesebb térképi részlet és ritkább útvonalfrissítés, jó üzemidővel.",
    highHint: "Részletes térkép és folyamatos mozgás, kiegyensúlyozott alapbeállítás.",
    ultraHint: "Legnagyobb részletesség és előtöltés erős készülékhez, magasabb fogyasztással.",
    title: "Grafika",
    quality: "Grafikai minőség",
    fovTitle: "FOV - LÁTÓMEZŐ",
    fov: "FOV - Látómező",
    fovHint: "Rögzítés közben legfeljebb ekkora sugarú terület készül elő a pozíciód körül. A képernyőn kívül kis ráhagyást tartunk, hogy forduláskor már készen álljon a térkép.",
    viewTitle: "LÁTÓTÁVOLSÁG (3D NÉZET)",
    view: "Látótávolság 3D nézetben",
    viewHint: "A bedöntött 3D térkép eddig a távolságig tölt be. A 2D nézetet ez nem befolyásolja.",
    note: "Ezek a beállítások csak a térkép animációjára, részletességére, valamint a cellák és az útvonal kirajzolására hatnak. A menük, szövegek és a GRUNDO megjelenése nem változik.",
  },

  soundSettings: {
    activity: "Aktivitásvezérlés",
    activityHint: "Szünet, folytatás, új kör, befejezés és sikeres mentés.",
    start: "Indítás",
    startHint: "A 3-2-1 visszaszámlálás és a „RAJT!” hangja.",
    territory: "Területszerzés",
    territoryHint: "Amikor bezárod a kört, és megszerzed a területet.",
    cells: "Mezők",
    cellsHint: "Menet közben, minden új mezőnél — aszerint, hogy szabad, a tiéd, maximumon áll, vagy egy másik játékosé.",
    title: "Hangok és rezgések",
    sound: "Hangeffektek",
    soundHint: "Rögzítés közbeni visszajelzés hanggal. Kikapcsolva minden néma marad.",
    vibration: "Rezgések",
    vibrationHint: "Rögzítés közbeni rezgés (automatikus szünet, játékok). Kikapcsolva nem rezeg semmi.",
    deviceHint: "A beállítás ehhez az eszközhöz tartozik — a telefonodon és a böngésződben külön állítható.",
    volume: "Hangerő",
    previewHint: "Elengedéskor lejátszunk egy mintát, hogy hallható legyen, mit állítottál be.",
    autoPause: "Automatikus szünet",
    autoPauseSound: "Automatikus szünet hangja",
    autoPauseHint: "A rendszer által indított szünet és folytatás hangjelzése. A kézi szünet hangja az Aktivitásvezérlésnél kapcsolható.",
  },

  soundLabels: {
    countdown: "Visszaszámlálás (3-2-1)",
    go: "RAJT!",
    claim: "Szabad cella elfoglalva",
    reinforce: "Saját cella megerősítve",
    takeOver: "Cella elvéve egy játékostól",
    max: "Cella maximális védelmen",
    loop: "Hurok bezárva — terület megszerezve",
    pause: "Aktivitás megállítva",
    resume: "Aktivitás folytatva",
    lap: "Új kör indítva",
    hold: "Befejezés nyomva tartva",
    finish: "Aktivitás befejezve",
    saved: "Aktivitás sikeresen elmentve",
  },

  messagingSettings: {
    title: "Privát üzenetek",
    who: "Kitől fogadsz üzenetet?",
    existingHint: "A már elfogadott beszélgetéseid a szabály szigorítása után is folytathatók. Ha valakitől egyáltalán nem kérsz üzenetet, némítsd vagy tiltsd le.",
    review: "Kéretlen üzenetek elbírálása",
    everyoneHint: "Bárkitől fogadsz üzenetet, ezért most nincs mit elbírálni.",
    reviewHint: "Aki a fenti szabálynak nem felel meg, annak az üzenete egy külön „Üzenetkérelmek\" listába kerül. Onnan elfogadhatod vagy törölheted.",
    denyHint: "Aki a fenti szabálynak nem felel meg, nem tud neked írni — azt látja, hogy kitől fogadsz üzenetet.",
    status: "Aktivitási állapot mutatása",
    statusHint: "A beszélgetőpartnereid látják, ha éppen aktív vagy, vagy mikor voltál utoljára. Ha kikapcsolod, te sem látod az övékét.",
  },

  messageRules: {
    everyone: "Bárkitől",
    following: "Akit követek, vagy aki követ engem",
    mutual: "Csak kölcsönös követés esetén",
    nobody: "Senkitől",
  },

  privateAccount: {
    accepted: (count: string) => `A fiókod nyilvános. ${count} várakozó kérést elfogadtunk.`,
    openProfile: (username: string) => `${username} profiljának megnyitása`,
    rejectRequest: (username: string) => `${username} kérésének elutasítása`,
    loadFailed: "A kérések most nem tölthetők be.",
    privateNotice: "A fiókod privát. Az aktivitásaidat mostantól csak a követőid látják, az új követés kérés lesz.",
    publicNotice: "A fiókod nyilvános.",
    saveFailed: "A beállítás nem sikerült. Próbáld újra.",
    actionFailed: "Nem sikerült. Próbáld újra.",
    title: "Privát fiók",
    hint: "Az aktivitásaidat csak az elfogadott követőid látják, a feedekben sem jelennek meg másnak. A profilodon a neved és a képed továbbra is látszik, és a területed is ott van a térképen.",
    requests: "Követési kérések",
    loading: "Betöltés…",
    empty: "Nincs várakozó kérés.",
    publicHint: "Nyilvános fióknál nincs kérés: bárki azonnal követhet.",
    accept: "Elfogadás",
  },

  blockedUsers: {
    blockedTitle: "Tiltott felhasználók",
    blockedEmpty: "Nincs letiltott felhasználód.",
    mutedTitle: "Némított felhasználók",
    mutedEmpty: "Nincs némított felhasználód.",
    loadFailed: "A lista most nem tölthető be. Próbáld később.",
    unblockFailed: "A feloldás nem sikerült. Próbáld újra.",
    loading: "Betöltés…",
    unblock: "Feloldás",
  },

  notificationSettings: {
    allOn: (count: number) => `Mind a ${count} értesítés be van kapcsolva.`,
    someOn: (on: number, total: number) => `${total}-ból ${on} értesítés van bekapcsolva.`,
    noVapid: "A push-értesítés nincs beállítva ebben a webes buildben (hiányzó VAPID-kulcs). Ez a mi hibánk, nem a tiéd — szólj nekünk.",
    unsupported: "Ez a böngésző vagy eszköz nem támogatja a push-értesítést.",
    permissionDenied: "Az értesítési engedély le van tiltva. Az eszköz vagy a böngésző értesítési beállításaiban engedélyezheted újra.",
    serviceFailed: "Nem sikerült elindítani a háttérszolgáltatást. Próbáld újratölteni az oldalt — privát böngészés közben ez nem működik.",
    tokenFailed: "Nem sikerült eszközazonosítót kérni az értesítéshez. A részletek a böngésző konzoljában láthatók.",
    title: "Értesítések",
    push: "Push-értesítés ezen az eszközön",
    deviceUnsupported: "Ez az eszköz nem tud ilyen értesítést küldeni.",
    pushHint: "Jelezzen-e ez az eszköz, amikor történik valami. Hogy miről szóljunk, azt a lenti kapcsolók döntik el.",
    live: "Élő mérés a zárolt képernyőn",
    iosHint: "A következő rögzítéstől az idő, táv és sebesség megjelenik a Live Activityben és a Dynamic Islanden.",
    androidHint: "A következő rögzítéstől az idő, táv és sebesség megjelenik a folyamatos rendszerértesítésben. Kikapcsolva a rögzítéshez kötelező egyszerű Android-értesítés megmarad.",
    topics: "Miről szóljunk",
    all: "Minden értesítés",
    noneOn: "Egyetlen értesítés sincs bekapcsolva.",
  },

  notificationLabels: {
    passport: "Új ország az útlevélben",
    gpActivity: "Pontok egy aktivitás után",
    saved: "Hosszú mentés elkészült",
    gpDaily: "Napi pont-összegzés",
    liked: "Kedvelés az aktivitásodon",
    commented: "Hozzászólás az aktivitásodon",
    replied: "Válasz a hozzászólásodra",
    boost: "Aktív akció indul",
    badge: "Új jelvény",
    followedActivity: "Követett felhasználó aktivitása",
    follower: "Új követő",
    followRequest: "Követési kérés",
    lost: "Elvették a grundod",
    defended: "Sikeresen megvédted a grundod",
    crewInvite: "Banda-meghívó",
    crewMembership: "Banda-tagsági események",
    crewPost: "Új poszt a bandában",
    wallReaction: "Válasz vagy szív az üzenőfalon",
    crewMessage: "Új üzenet a banda chatben",
    crewMention: "Említés vagy válasz a banda chatben",
    crewDaily: "Napi banda-összesítő",
    report: "Bejelentésed elbírálva",
    review: "Aktivitásod ellenőrzése lezárult",
    message: "Privát üzenet",
    request: "Üzenetkérelem (kéretlen üzenet)",
    streak: "Streak-emlékeztető",
    mention: "Említés hozzászólásban",
    record: "Egyéni rekord",
    news: "Új hír",
    gameGroup: "Grund és játék",
    pointsGroup: "Pontok és jelvények",
    communityGroup: "Közösség",
    messageGroup: "Privát üzenetek",
    crewGroup: "Banda",
    reviewGroup: "Bejelentés és ellenőrzés",
  },

  connectedDevices: {
    title: "Eszközök",
    intro: "Kösd össze a GRUNDO-t az órád, a fejegységed vagy a telefonod egészségügyi appjával, és a máshol rögzített mozgásod is ide kerül.",
    soon: "Hamarosan",
    connect: (name: string) => `${name} összekapcsolása`,
    appOnlyIos: "iOS-appban kapcsolható össze",
    appOnlyAndroid: "Android-appban kapcsolható össze",
    appleHealth: "Az iPhone-on és az Apple Watch-on rögzített edzéseid útvonallal együtt átjönnek, a GRUNDO-aktivitásaidat pedig az Egészség appba is mentheted.",
    healthConnect: "Androidon a Health Connecten át jönnek az edzéseid, a Samsung Health, a Google Fit és más appok rögzítéseivel együtt. A GRUNDO-aktivitásaidat oda is mentheted.",
    garmin: "A Garmin órán vagy Edge-en rögzített aktivitásaid maguktól átjönnek, a tervezett köreidet pedig pályaként az eszközödre küldheted.",
    wahoo: "A Wahoo csak a Wahoo-eszközön rögzített edzéseket osztja meg — a más appokból odakerültek nem jönnek át.",
    polar: "Az új Polar-edzéseid maguktól átjönnek. A Polar csak az összekapcsolás pillanatától oszt meg, a korábbiak ott maradnak.",
    hammerhead: "Az új Karoo-tekeréseid maguktól átjönnek. Az összekapcsolás előtti tekerések a Hammerheadnél maradnak.",
    strava: "A GRUNDO-ban rögzített aktivitásaidat feltöltheted a Stravára — kézzel vagy mentés után magától. A Stravából nem hozunk be semmit.",
    coros: "A COROS-fiókod minden órájának aktivitásai átjönnek, amint az óra szinkronizált a COROS appal.",
    rulesTitle: "Mi kerül át?",
    ruleWindow: "Csak a legfeljebb 72 órája befejezett aktivitás — a fiókod létrehozásánál régebbi soha.",
    rulePriority: "Ha ugyanazt a mozgást a GRUNDO-val is rögzítetted, a GRUNDO-rögzítés számít, a beimportált csak kiegészíti.",
    ruleNoDuplicate: "Ugyanaz az aktivitás két forrásból sem kerül be kétszer.",
    exportHint: "Egy aktivitást már most is kimenthetsz GPX- vagy TCX-fájlba: az aktivitás adatlapján, a letöltés ikonnal.",
    connected: "Összekapcsolva",
    exportOnlyNow: "Egyelőre egy irányba működik: a GRUNDO ide menti az aktivitásaidat. Az edzések behozatala később jön.",
    autoExport: "Automatikus mentés",
    autoExportNote: "Minden mentett aktivitásod magától átkerül. Kikapcsolva egyenként küldheted el őket.",
    manualHint: "Kézzel: az aktivitás adatlapján, a letöltés ikonnal.",
    disconnect: "Leválasztás",
    disconnectedIos: "Leválasztva — a GRUNDO nem ír több edzést. Az engedélyt az Egészség appban vonhatod vissza: Megosztás → Appok → GRUNDO.",
    healthDenied: {
      appleHealth: "A GRUNDO nem kapott engedélyt az edzések írására. Az Egészség appban kapcsolhatod be: Megosztás → Appok → GRUNDO.",
      healthConnect: "A GRUNDO nem kapott engedélyt az edzések írására. A Health Connectben kapcsolhatod be: Alkalmazásengedélyek → GRUNDO.",
    },
    healthUnavailable: "Ezen az eszközön nem érhető el.",
    updateApp: "Ehhez frissítsd a GRUNDO appot.",
    installNote: "A Health Connect app hiányzik vagy elavult — a Play Áruházból telepítheted.",
    install: "Health Connect telepítése",
    update: "Health Connect frissítése",
    workoutTitle: (type: 'run' | 'walk' | 'ride') =>
      `GRUNDO – ${type === 'run' ? 'Futás' : type === 'walk' ? 'Séta' : 'Kerékpár'}`,
    saveTo: { appleHealth: "Mentés az Egészség appba", healthConnect: "Mentés a Health Connectbe" },
    saveToHint: "Edzésként, útvonallal",
    savedTo: { appleHealth: "Elmentve az Egészség appba", healthConnect: "Elmentve a Health Connectbe" },
    alreadySaved: "Ez az aktivitás már bent van.",
    routeAdded: "Az útvonal pótolva: az edzés most már térképpel látszik.",
    routeFailed: (detail: string) =>
      `Az edzés bent van, de az útvonalat nem sikerült hozzáadni.${detail ? ` (${detail})` : ''}`,
    importNote: "Az összekapcsolás után befejezett aktivitásaid maguktól átjönnek, legkésőbb nagyjából hat órával a befejezésük után.",
    connectedNow: "Összekapcsolva. Az új aktivitásaid innentől maguktól átjönnek.",
    connectFailed: "Az összekapcsolás most nem sikerült. Próbáld újra.",
    stravaAutoExport: "Automatikus feltöltés a Stravára",
    stravaAutoExportNote: "Minden GRUNDO-val rögzített aktivitásod mentés után felkerül a Stravára. Az órádról behozottak nem — azokat az órád maga küldi.",
    stravaManualHint: "Kézzel: az aktivitás adatlapján, a letöltés ikonnal.",
    stravaUpload: "Feltöltés a Stravára",
    stravaUploadHint: "A teljes útvonallal",
    stravaUploaded: "Feltöltve a Stravára",
    stravaProcessing: "A Strava még feldolgozza — pár perc múlva megjelenik.",
    stravaSkipped: {
      imported: "Ez az aktivitás egy eszközödről jött; a Stravára az eszköz maga küldheti.",
      no_route: "Ehhez az aktivitáshoz nincs útvonal.",
      not_found: "Ez az aktivitás nem található.",
      disconnected: "A Strava nincs összekapcsolva.",
    },
    stravaFailed: (detail: string) => `A Strava nem fogadta el.${detail ? ` (${detail})` : ''}`,
    // Strava Brand Guidelines: the link text is exactly this, in every language.
    viewOnStrava: "View on Strava",
  },

  liveSettings: {
    seconds: (value: number) => `${value} mp`,
    none: "Nincs",
    title: "Élő közvetítés",
    intro: "Bekapcsolva minden rögzítésed nyilvános linken is követhető: bárki, aki megnyitja, élőben látja a térképet, a megtett távot és az érintett mezőket. A link streamhez, overlayhez is használható.",
    next: "Közvetítés a következő rögzítésnél",
    persist: "Bekapcsolva marad, amíg ki nem kapcsolod — az app újraindítása után is.",
    delay: "Késleltetés",
    delayBefore: "A nyilvános nézet ennyivel korábbi állapotot mutat. Egy élő adás maga is késik ennyit, tehát a nézőknek ez nem tűnik fel — viszont a link így nem mutatja meg, hol vagy",
    now: "éppen most",
    noDelay: "Késleltetés nélkül a pozíciód valós időben látszik.",
    link: "A nyilvános link",
    copied: "Másolva",
    copy: "Link másolása",
    overlayBefore: "Overlayhez (például OBS böngészőforrásba) a",
    overlayAfter: "végződéssel nyisd meg — akkor átlátszó háttérrel, keret nélkül jelenik meg.",
    usernameHint: "A link a felhasználónevedből áll össze; előbb add meg a profilodban.",
    warningBefore: "Az élő nézet a",
    wholeRoute: "teljes útvonalat",
    warningMiddle: "mutatja, a rajttól kezdve — az aktivitás-adatvédelmi beállításod erre nem vonatkozik. Aki megnyitja a linket, a késleltetés erejéig követni tudja, merre jársz. A közvetítés",
    everyRecording: "minden rögzítésednél megy",
    warningAfter: ", amíg fent ki nem kapcsolod.",
  },

  colorPicker: {
    previous: "Előző színek",
    title: "Területszín-választó",
    next: "Következő színek",
  },

  colorNames: {
    beige: "Bézs",
    goldenBrown: "Aranybarna",
    copper: "Réznarancs",
    raspberry: "Málnapiros",
    coral: "Korallrózsaszín",
    brick: "Téglapiros",
    burgundy: "Bordó",
    walnut: "Dióbarna",
    olive: "Olívazöld",
    turquoise: "Türkizzöld",
    steel: "Acélkék",
    plum: "Szilvalila",
    petrol: "Petrol",
    slate: "Palakék",
    gray: "Szürke",
    anthracite: "Antracit",
    electricBlue: "Elektromos kék",
    neonCyan: "Neoncián",
    neonMint: "Neonmenta",
    neonOrange: "Neonnarancs",
    neonGold: "Neon arany",
    acidGreen: "Savzöld",
    neonGreen: "Neonzöld",
    neonRed: "Neonpiros",
    neonPink: "Neonpink",
    deepGreen: "Mélyzöld",
    deepIndigo: "Mély indigó",
    neonPurple: "Neonlila",
    silver: "Ezüst",
    graphite: "Grafit",
    espresso: "Eszpresszó",
    sand: "Homok",
    khaki: "Khaki",
    oliveDrab: "Katonazöld",
    darkKhaki: "Sötétkhaki",
    peach: "Barack",
    rust: "Rozsda",
    blush: "Púderrózsa",
    wine: "Bor",
    orchid: "Orchidea",
    magenta: "Bíbor",
    darkMagenta: "Sötétbíbor",
    aubergine: "Padlizsán",
    lavender: "Levendula",
    grape: "Szőlő",
    periwinkle: "Meténg",
    ultramarine: "Ultramarin",
    royalIndigo: "Királyindigó",
    cornflower: "Búzavirágkék",
    navy: "Tengerészkék",
    iceBlue: "Jégkék",
    deepCyan: "Mélycián",
    lightTeal: "Világos kékeszöld",
    tealGreen: "Kékeszöld",
    darkTeal: "Sötét kékeszöld",
    deepTeal: "Mély kékeszöld",
    jade: "Jáde",
    mintGreen: "Mentazöld",
    forest: "Erdőzöld",
    yellowGreen: "Sárgászöld",
    moss: "Mohazöld",
    lemon: "Citrom",
    mustard: "Mustár",
    bronze: "Bronz",
  },

  ownMenu: {
    title: 'Az aktivitásod',
    edit: 'Szerkesztés',
    visibility: 'Láthatóság',
    visibilitySaved: 'A láthatóság átállítva.',
    delete: 'Törlés',
    deleteTitle: 'Biztosan törlöd ezt az aktivitást?',
    deleteLead: 'Az aktivitás azonnal eltűnik mások elől, az adatait 30 napig visszaállíthatóan megőrizzük. A már kiosztott GP és a területek történeti állapota nem változik.',
    deleteConfirm: 'Törlés',
    deleted: 'Az aktivitás törölve.',
    failed: 'A művelet nem sikerült.',
    back: 'Vissza',
    export: 'Exportálás fájlba',
  },

  /** CONNECTOR (#118) — a saját aktivitás exportja GPX- vagy TCX-fájlként. */
  activityExport: {
    open: 'Exportálás fájlba',
    title: 'Exportálás fájlba',
    lead: 'A teljes nyomvonal fájlként, más appokba és eszközökre: Garmin Connect, Strava (weben), Komoot, kerékpáros fejegység.',
    gpx: 'GPX',
    gpxHint: 'Szinte minden app és eszköz olvassa.',
    tcx: 'TCX',
    tcxHint: 'Garmin-formátum, idővel és halmozott távval.',
    downloaded: 'A fájl letöltve.',
    ready: 'A fájl kész.',
    failed: 'Az exportálás nem sikerült.',
    back: 'Vissza',
  },

  /** Aktivitás megosztása (#100) — a megosztó lap. */
  share: {
    open: 'Megosztás',
    title: 'Megosztás',
    close: 'Bezárás',
    imageMap: 'Térkép',
    imagePhoto: (n: number) => `${n}. fotó`,
    titleLabel: 'Cím',
    descriptionLabel: 'Leírás',
    descriptionPlaceholder: 'Pár szó róla (nem kötelező)',
    saveToActivity: 'Mentés az aktivitásba is',
    targets: {
      facebook: 'Facebook',
      instagram: 'Instagram',
      messenger: 'Messenger',
      system: 'Megosztás',
      copy: 'Link',
    },
    notPublic: 'Linket csak mindenki számára látható aktivitáshoz adhatunk.',
    makePublic: 'Nyilvánossá teszem (link is)',
    shareImage: 'Kép megosztása',
    instagramStory: 'Instagram Story',
    imageDownloaded: 'A kép letöltve.',
    preparing: 'Link készül…',
    copied: 'Link kimásolva.',
    failed: 'Nem sikerült. Próbáld újra.',
  },

  editProfile: {
    title: 'Profil szerkesztése',
    intro: 'Ez látszik a profilodon. A felhasználóneved nem változik.',
    displayName: 'Megjelenített név',
    displayNameHint: 'Ha üresen hagyod, a felhasználóneved lesz. A valódi nevedet csak akkor add meg, ha szeretnéd, hogy mások lássák.',
    bio: 'Bemutatkozás',
    bioPlaceholder: 'Pár szó rólad…',
    save: 'Mentés',
    saved: 'A profilod elmentve.',
    saveFailed: 'A mentés nem sikerült. Próbáld újra.',
  },

  country: {
    label: 'Ország',
    none: 'Nincs megadva',
    hint: 'A zászlaja a neved mellett látszik a profilodon — mások is látják.',
  },

  profileWizard: {
    title: 'Profilbeállító varázsló',
    progress: (step: number, total: number) => `${step}/${total}`,
    later: 'Később',
    next: 'Tovább',
    skip: 'Kihagyás',
    done: 'Kész',
    saveFailed: 'A mentés nem sikerült. Próbáld újra, vagy hagyd ki ezt a lépést.',
    settingsRow: 'Profilbeállító varázsló',
    settingsRowHint: 'Nyelv, ország, bemutatkozás és a többi — lépésenként',
    language: {
      title: 'Válassz nyelvet',
    },
    units: {
      title: 'Mértékegység',
    },
    country: {
      title: 'Melyik országban élsz?',
    },
    bio: {
      title: 'Mutatkozz be!',
    },
    body: {
      title: 'Magasság és testsúly',
    },
    push: {
      title: 'Értesítések',
      intro: 'Szólunk, ha valaki elfoglalja a meződet, követni kezd vagy üzenetet küld. Bármikor kikapcsolhatod.',
      enable: 'Bekapcsolom',
      enabled: 'Az értesítések be vannak kapcsolva ezen a készüléken.',
    },
    sounds: {
      title: 'Hangok és rezgések',
    },
  },

  tutorial: {
    question: 'Megnézed, mi hol van?',
    questionBody: 'Egy perc az egész: megmutatjuk a fő gombokat, a valódi felületen.',
    watch: 'Megnézem',
    decline: 'Kihagyom',
    next: 'Tovább',
    skip: 'Kihagyás',
    done: 'Kész',
    progress: (step: number, total: number) => `${step}/${total}`,
    settingsRow: 'Bemutató',
    settingsRowHint: 'Megmutatja, mi hol van az appban',
    steps: {
      start: {
        title: 'Indulás',
        body: 'Itt indítod a rögzítést: válassz sportágat, és mozgás közben mezőket foglalsz.',
      },
      summary: {
        title: 'Grund, GP, sorozat',
        body: 'A grundod a mezőid területe. GP-t magáért a mozgásért kapsz, akkor is, ha nem foglalsz. A sorozat a mozgással töltött egymást követő napjaid száma.',
      },
      grund: {
        title: 'Grund',
        body: 'A térképen látod a saját és a többiek mezőit.',
      },
      community: {
        title: 'Közösség',
        body: 'Hírfolyam, bandák és ranglista — itt látod, mit csinálnak a többiek.',
      },
      profile: {
        title: 'Profil',
        body: 'A szinted, a jelvényeid és a Beállítások. Innen a bemutató és a profilbeállító varázsló is újraindítható.',
      },
      header: {
        title: 'Keresés, üzenetek, értesítések',
        body: 'Itt keresel másokat, és itt találod az üzeneteidet és az értesítéseidet.',
      },
      assistant: {
        title: 'Grout',
        body: 'Írd ide, mihez van kedved — Grout útvonalat tervez veled.',
      },
    },
  },

  gpHistory: {
    open: 'Honnan jött a GP-m?',
    title: 'Honnan jött a GP-m?',
    intro: 'A GP-d minden jóváírása időrendben, a legfrissebb elöl. Az aktivitásokhoz a pont részei is látszanak.',
    total: 'Összes GP',
    openActivity: 'Aktivitás megnyitása',
    more: 'Továbbiak betöltése',
    end: 'Ez volt az összes bejegyzés.',
    empty: 'Még nincs jóváírás',
    emptyNote: 'Az első aktivitásod után itt látod, honnan jött a pontod.',
    failed: 'A lista most nem tölthető be',
    failedNote: 'Próbáld újra egy kicsit később.',
    sources: {
      activity: 'Aktivitás',
      hold: 'Tartás-bónusz',
      streak_milestone: 'Sorozat mérföldkő',
      badge: 'Jelvény-jutalom',
      refhex: 'Refhex játék',
      manual: 'Korrekció',
      admin_adjust: 'Korrekció',
      other: 'Egyéb jóváírás',
    },
    parts: { base: 'alap', claim: 'területfoglalás', steal: 'elvétel', breakthrough: 'áttörés' },
    boost: (factor: number) => `×${factor.toFixed(2).replace('.', ',')} akció-szorzó`,
    softCap: (amount: string) => `napi plafon: −${amount}`,
  },

  territoryHistory: {
    open: 'Területtörténet',
    title: 'Területtörténet',
    intro: 'Mit foglaltál, és mit vettek el tőled, időrendben, a legfrissebb elöl.',
    claimed: (area: string) => `Szereztél ${area} területet`,
    claimedFrom: (names: string) => `Ebből elvetted: ${names}`,
    lost: (actor: string, area: string) => `${actor} elvett ${area} területet`,
    unknown: 'Ismeretlen játékos',
    cells: (count: string) => `${count} cella`,
    openActivity: 'Aktivitás megnyitása',
    layerFoot: 'gyalogos réteg',
    layerBike: 'kerékpáros réteg',
    more: 'Továbbiak betöltése',
    end: 'Ez volt az összes bejegyzés.',
    empty: 'Még nincs területtörténet',
    emptyNote: 'Az első bezárt köröd után itt látod, mit foglaltál, és ha valaki elvesz belőle, azt is.',
    failed: 'A lista most nem tölthető be',
    failedNote: 'Próbáld újra egy kicsit később.',
  },

  activityVisibility: {
    label: "Ki láthatja?",
    everyone: "Mindenki",
    followers: "Követők",
    onlyMe: "Csak én",
    hintEveryone: "Bárki láthatja a feedben és a profilodon.",
    hintFollowers: "Csak a követőid látják.",
    hintOnlyMe: "Csak te látod.",
    defaultTitle: "Új aktivitások láthatósága",
    defaultIntro: "Ezzel indul minden új aktivitásod, és mentéskor egyenként is átállíthatod. A korábbi aktivitásaidat nem változtatja meg.",
    defaultSaved: "Az alapértelmezett láthatóság elmentve.",
    saveFailed: "Nem sikerült menteni a láthatóságot.",
    badgeFollowers: "Követőknek",
    badgeOnlyMe: "Csak neked",
  },

  activityPrivacy: {
    rebuilt: (count: number) => `${count} aktivitás megosztott térképe frissült.`,
    hide: (title: string) => `${title} elrejtése`,
    zoneRadius: (title: string) => `${title} védőkörének sugara`,
    saved: "Az adatvédelmi beállítások elmentve.",
    saveFailed: "Nem sikerült menteni a beállítást.",
    title: "Aktivitás-adatvédelem",
    intro: "A megosztott útvonal elejét és végét külön-külön elrejtheted. Te továbbra is a teljes nyomvonalat látod, a megszerzett területek pedig nyilvánosak maradnak.",
    start: "Kiindulási pont",
    end: "Befejezési pont",
    retroactive: "A módosítás visszamenőleg minden korábbi aktivitásodra érvényes. Ha a teljes útvonal a védőkörön belül van, mások „Az útvonal rejtve” feliratot látnak.",
    save: "Beállítások mentése",
    zoneHint: "A megadott sugarú körön belüli nyomvonal nem kerül a megosztott térképre.",
    radius: "Védőkör sugara",
  },

  locationSettings: {
    saveFailed: "A mentés nem sikerült. Próbáld újra.",
    title: "Lokáció",
    intro: "Melyik hely időjárása és pontos ideje látsszon a Kezdőlapon. Alapból a saját helyzeted — vagy válassz egy várost.",
    loading: "Betöltés…",
  },

  lockSettings: {
    unsupported: "Ez az eszköz nem ismer arc-, ujjlenyomat- vagy jelkód-feloldást.",
    noScreenLock: "Előbb állíts be képernyőzárat (jelkód, minta, ujjlenyomat vagy arc) az eszköz beállításaiban.",
    updateRequired: "Ez az alkalmazásverzió még nem tartalmazza a feloldást. Telepítsd a legújabb kiadást, és próbáld újra.",
    unavailable: "A feloldás most nem érhető el ezen az eszközön.",
    enablePrompt: "Erősítsd meg, hogy te kapcsolod be a zárat.",
    disablePrompt: "Erősítsd meg a zár kikapcsolását.",
    failed: "A feloldás nem sikerült.",
    title: "Biometrikus zár",
    onStart: "Kérjen feloldást indításkor",
    methods: "Arc, ujjlenyomat, minta vagy jelkód — amelyik az eszközödön be van állítva.",
    nativeOnly: "A zár csak a telefonos alkalmazásban működik, böngészőben nem.",
    note: "A zár az alkalmazás megnyitását kéri feloldani, a bejelentkezésedet nem változtatja meg: kijelentkezve maradsz vagy belépve, ahogy eddig. Csak hidegindításkor kérdez — ha közben más appba váltasz és visszajössz, nem kérdez újra.",
  },

  bodySettings: {
    age: (years: number) => ` — ${years} éves`,
    saveFailed: "Nem sikerült elmenteni. Próbáld meg újra.",
    title: "Fizikai jellemzők",
    intro: "Ezekkel pontosabb lesz a becsült teljesítmény és kalória az aktivitásaidban. Mindkettő átugorható — bármikor visszajöhetsz ide.",
    weight: "Testsúly",
    delete: "Törlés",
    weightUnit: "Testsúly mértékegysége",
    enterWeight: "Testsúly megadása",
    height: "Magasság",
    heightUnit: "Magasság mértékegysége",
    heightFeet: "Magasság — láb",
    heightInches: "Magasság — hüvelyk",
    enterHeight: "Magasság megadása",
    birthDate: "Születési dátum",
    birthDateNote: ". A regisztrációkori korhatár-ellenőrzés miatt itt nem módosítható.",
    save: "Mentés",
    saved: "Elmentve.",
  },

  dataExport: {
    ready: (date: string) => `A csomagod elkészült, a linkek ${date}-ig élnek.`,
    failed: "A kérés nem sikerült. Próbáld újra.",
    title: "Adataim letöltése",
    intro: "A teljes adatcsomagod két fájlt tartalmaz: a profilodhoz és a fiókodhoz köthető adatokat JSON formátumban, és a rögzített nyomvonalaidat GPX formátumban. A letöltési linkek 7 napig élnek, és e-mailben is megkapod őket. Óránként legfeljebb egy csomagot kérhetsz. Részletek: az",
    privacy: "Adatkezelési tájékoztató",
    privacySection: "8. pontja.",
    processing: "Az előző kérésed még feldolgozás alatt van.",
    json: "Adatok letöltése (JSON)",
    gpx: "Nyomvonalak letöltése (GPX)",
    request: "Adatcsomag kérése",
  },

  accountDeletion: {
    scheduled: (date: string) => ` A végleges törlés ${date}-kor esedékes — eddig visszavonhatod.`,
    failed: "A kérés nem sikerült. Próbáld újra.",
    appleFailed: "Az Apple-belépés nem sikerült.",
    signInFailed: "A bejelentkezés nem sikerült.",
    cancelFailed: "A visszavonás nem sikerült. Próbáld újra.",
    requestedBefore: "A fiókod törlését",
    requestedAfter: "-kor kérted.",
    deleting: " A végleges törlés már folyamatban van, innen nincs visszaút.",
    cancel: "Törlési kérelem visszavonása",
    intro: "A törlés kérésekor minden munkameneted kijelentkezik, a profilod és az aktivitásaid mások elől azonnal rejtve lesznek. Ezután",
    grace: "30 napig visszavonhatod",
    afterGrace: "— csak lépj be újra. A türelmi idő lejárta után a törlés",
    permanent: "végleges",
    consequences: ": a foglalt területed felszabadul, az aktivitásaid, fotóid és nyomvonalaid törlődnek, a hozzászólásaid helyén „Törölt felhasználó\" marad, a felhasználóneved felszabadul, és a fiókod megszűnik. Részletek: az",
    privacy: "Adatkezelési tájékoztató",
    privacySection: "9. pontja.",
    confirm: "Értem, és a fiókom törlését kérem.",
    reauth: "A törléshez frissen kell belépned. Erősítsd meg az azonosításodat, utána a kérés automatikusan folytatódik.",
    password: "Jelszó",
    request: "Fiók törlésének kérése",
    title: "Fiók törlése",
  },

  deletionReauth: {
    apple: "Belépés újra Apple-lel",
    google: "Belépés újra Google-lel",
    confirm: "Megerősítés",
  },

  linkedAccounts: {
    passwordProvider: "E-mail és jelszó",
    title: "Bejelentkezési módok",
    intro: "Ezekkel léphetsz be a fiókodba. Legalább egynek mindig meg kell maradnia — ha csak egy van összekapcsolva, azt nem választhatod le.",
    linked: "Összekapcsolva",
    notLinked: "Nincs összekapcsolva",
    unlink: "Leválasztás",
    link: "Összekapcsolás",
    newPassword: "Új jelszó",
    addPassword: "Jelszó hozzáadása",
    cancel: "Mégse",
  },

  gameRules: {
    slopeBelow: (max: number) => `Lejtő vagy ${max}% alatti emelkedő`,
    slopeAbove: (min: number) => `${min}% feletti emelkedő`,
    slopeBetween: (min: number, max: number) => `${min}–${max}% emelkedő`,
    activeBonus: (percent: number) => `A domborzati többlet legfeljebb az alap táv-GP ${percent}%-a lehet.`,
    measurementBonus: (percent: number) => `A rendszer ezt jelenleg még csak méri: a kiosztott GP-t nem módosítja. Aktiválás után a többlet legfeljebb az alap táv-GP ${percent}%-a lehet.`,
    gpMultiplier: "GP-szorzó",
    claimMultiplier: "Igénypont-szorzó",
    holdMultiplier: "Tartás-bónusz szorzó",
    global: "Az egész térképen",
    area: "Egy kijelölt területen",
    segment: "Visszatérőknek",
    on: "Be",
    off: "Ki",
    loadFailed: "Nem sikerült betölteni a szabályokat.",
    title: "Játékszabályok",
    intro: "Ezek a jelenleg érvényes számok — ha valamit hangolunk, itt azonnal a friss érték látszik.",
    loading: "Betöltés…",
    boosts: "Aktív akciók",
    until: "Eddig érvényes:",
    modified: "módosítva",
    terrain: "Domborzati GP",
    slopes: "Emelkedős szakaszok szorzója",
    active: "Aktív",
    measurement: "Mérési mód",
    terrainIntro: "A fent látható GP/km értékek az alapértékek. A domborzati modell szakaszonként, az emelkedés meredeksége alapján súlyozza ezt az alap GP-t.",
  },

  cityPicker: {
    minQuery: (count: number) => `Írd be a város nevét (legalább ${count} betű).`,
    title: "Lokáció módosítása",
    close: "Bezárás",
    failed: "A városkereső most nem elérhető. Próbáld később.",
    placeholder: "Város keresése…",
    search: "Város keresése",
    ownLocation: "Saját helyzetem",
    selected: "Jelenleg kiválasztva",
    searching: "Keresés…",
    empty: "Nincs ilyen nevű város.",
  },

  welcome: {
    page: (number: number) => `${number}. lap`,
    loop: "Zárd a kört",
    loopNote: "Fuss, sétálj vagy bringázz. Ha az útvonalad keresztezi önmagát, a körbezárt terület a tiéd.",
    grund: "Urald a GRUNDOT",
    grundNote: "A várost hatszögekre osztottuk. Foglald el, védd meg — és vedd el másokét.",
    distance: "Minden méter számít",
    distanceNote: "Akkor is pontokat kapsz, ha nem zársz be területet. A GRUNDO mindenkit jutalmaz.",
    tester: "Legyél Te is tesztelő",
    testerNote: "A GRUNDO teszt fázisban üzemel. Ha szeretnél iOS vagy Android tesztelő lenni és kipróbálni a natív appot, csatlakozz",
    testerAfter: "-on és jelentkezz!",
    tagline: "Ne csak ingázz,",
    start: "Kezdjük",
    next: "Tovább",
    haveAccount: "Van már fiókod?",
    signIn: "Lépj be",
  },

  welcomeVerbs: {
    ride: "Bringázz",
    run: "Fuss",
    walk: "Sétálj",
    ghost: "Bringázz!",
  },

  register: {
    minimumAge: (years: number) => `A regisztrációhoz legalább ${years} évesnek kell lenned.`,
    acceptRequired: "Az elfogadás kötelező.",
    intro: "Hozd létre a fiókod és foglald el a várost!",
    username: "Felhasználónév",
    usernameHint: "3–20 karakter: kisbetű, szám, pont, alulvonás.",
    email: "E-mail-cím",
    password: "Jelszó",
    birthDate: "Születési dátum",
    acceptBefore: "Elfogadom az",
    terms: "Általános Szerződési Feltételeket",
    and: "és az",
    privacy: "Adatvédelmi tájékoztatót",
    create: "Fiók létrehozása",
    google: "Folytatás Google-fiókkal",
    apple: "Folytatás Apple-fiókkal",
    haveAccount: "Van már fiókod?",
    signIn: "Lépj be",
  },

  login: {
    socialAccount: (provider: string) => `Szia! Ezt a fiókot ${provider}-fiókkal hoztad létre.`,
    socialSignIn: (provider: string) => `Belépés ${provider}-fiókkal`,
    noPasswordBefore: 'Jelszó nem tartozik hozzá, ezért a lenti',
    noPasswordAfter: 'gombbal tudsz bejelentkezni.',
    identifierRequired: "Add meg az e-mail-címed vagy a felhasználóneved.",
    passwordRequired: "Adj meg egy jelszót.",
    intro: "Üdv újra! Készen állsz egy új kalandra?",
    linkBefore: "Lépj be a jelszavaddal, majd a",
    linkSettings: "Beállítások → Bejelentkezési módok",
    linkAfter: "alatt kapcsold össze a Google- vagy Apple-fiókoddal. Utána bármelyikkel beléphetsz.",
    identifier: "E-mail-cím vagy felhasználónév",
    password: "Jelszó",
    forgot: "Elfelejtetted a jelszavad?",
    signIn: "Belépés",
    google: "Folytatás Google-fiókkal",
    apple: "Folytatás Apple-fiókkal",
    noAccount: "Még nincs fiókod?",
    register: "Regisztrálj",
  },

  forgotPassword: {
    title: "Jelszó visszaállítása",
    sentBefore: "Ha a(z)",
    sentAfter: "címhez jelszavas fiók tartozik, elküldtük a 6 jegyű kódot. Nézd meg a levélszemetet is.",
    back: "Vissza a belépéshez",
    email: "E-mail-cím",
    hint: "Küldünk egy 6 jegyű kódot, amivel új jelszót adhatsz meg.",
    send: "Kód küldése",
    code: "Kód",
    codeInvalid: "Add meg a levélben kapott 6 jegyű kódot.",
    newPassword: "Új jelszó",
    save: "Jelszó mentése",
    resend: "Kód újraküldése",
    resendWait: (seconds: number) => `Újraküldés ${seconds} mp múlva`,
    done: "Az új jelszavad él. Most már beléphetsz vele.",
    cancel: "Mégsem, vissza a belépéshez",
  },

  completeProfile: {
    signedInAs: 'Be vagy jelentkezve mint',
    acceptRequired: "Az elfogadás kötelező.",
    failed: "Nem sikerült létrehozni a profilt.",
    intro: "Már csak egy név kell.",
    nameHint: ", de még nincs felhasználóneved. Válassz egyet — ez látszik majd a ranglistán.",
    username: "Felhasználónév",
    usernameHint: "3–20 karakter: kisbetű, szám, pont, alulvonás. Később nem módosítható.",
    birthDate: "Születési dátum",
    acceptBefore: "Elfogadom az",
    terms: "Általános Szerződési Feltételeket",
    and: "és az",
    privacy: "Adatvédelmi tájékoztatót",
    create: "Profil létrehozása",
    signOut: "Kijelentkezés",
  },

  authErrors: {
    invalidCredentials: "Hibás e-mail-cím vagy jelszó.",
    noAccount: "Nincs fiók ezzel az e-mail-címmel.",
    invalidEmail: "Ez nem érvényes e-mail-cím.",
    disabled: "Ez a fiók fel van függesztve. Írj nekünk, ha szerinted tévedés.",
    tooMany: "Túl sok próbálkozás. Várj pár percet, vagy állítsd vissza a jelszavad.",
    emailInUse: "Ezzel az e-mail-címmel már van fiók. Lépj be, vagy állítsd vissza a jelszavad.",
    weakPassword: "A jelszó túl gyenge. Legalább 8 karakter kell.",
    unavailable: "Ez a bejelentkezési mód jelenleg nem elérhető.",
    linkAccount: "Ezzel az e-mail-címmel már van fiókod, jelszóval. Lépj be jelszóval, és a Beállításokban összekapcsolhatod a Google- vagy Apple-fiókoddal.",
    credentialInUse: "Ez a fiók már egy másik GRUNDO-fiókhoz tartozik.",
    alreadyLinked: "Ez a bejelentkezési mód már össze van kapcsolva.",
    recentLogin: "Ehhez frissen kell belépned. Lépj ki, és jelentkezz be újra.",
    notLinked: "Ez a belépési mód nincs összekapcsolva a fiókoddal.",
    cancelled: "A bejelentkezést megszakítottad.",
    popupBlocked: "A böngésző blokkolta a bejelentkezési ablakot. Engedélyezd, és próbáld újra.",
    network: "Nincs kapcsolat. Ellenőrizd az internetet.",
    unexpected: "Váratlan hiba történt. Próbáld újra.",
  },

  validation: {
    usernameRequired: "Adj meg egy felhasználónevet.",
    usernameMin: "Legalább 3 karakter kell.",
    usernameMax: "Legfeljebb 20 karakter lehet.",
    usernamePattern: "Csak kisbetű, szám, pont és alulvonás használható.",
    usernameReserved: "Ez a név foglalt.",
    emailRequired: "Adj meg egy e-mail-címet.",
    invalidEmail: "Ez nem érvényes e-mail-cím.",
    passwordRequired: "Adj meg egy jelszót.",
    passwordMin: "Legalább 8 karakter kell.",
    weak: "Gyenge",
    fair: "Megfelelő",
    strong: "Erős",
  },

  firebaseNotice: {
    title: "A Firebase nincs beállítva",
    note: "— a bejelentkezés nem működik.",
    missing: "Hiányzó változó:",
    copy: "Másold a",
    file: "fájlt",
    configure: "néven, és töltsd ki.",
  },

  firstFollows: {
    failed: "Nem sikerült betölteni az ajánlásokat.",
    title: "Kövess valakit",
    intro: "Ezekkel az aktív felhasználókkal indulhatsz — bármikor bekövetheted őket később is, ez a lépés teljesen opcionális.",
    loading: "Betöltés…",
    empty: "Egyelőre nincs ajánlásunk — nézz vissza később a Keresésben.",
    done: "Kész",
    followed: "Követve",
    follow: "Követés",
  },

  cellColorOnboarding: {
    title: 'Válaszd ki a grundod színét',
    start: 'Indulhat',
  },
  privacyOnboarding: {
    failed: "Nem sikerült menteni a beállítást. Próbáld újra.",
    basics: "Alapadatok",
    intro: "Ezekkel pontosabb lesz a becsült teljesítmény és kalória az aktivitásaidban. Bármikor kihagyhatod, és a Beállításokban később is megadhatod.",
    weight: "Testsúly",
    enterWeight: "Testsúly megadása",
    height: "Magasság",
    enterHeight: "Magasság megadása",
    skip: "Kihagyás",
    next: "Tovább",
    title: "Védd a kiindulópontod",
    privacyHint: "Az elejét és a végét elrejtjük mások elől — a táv, idő és tempó nem változik.",
    noHiding: "Nincs elrejtés",
    start: "Kiindulási pont",
    end: "Befejezési pont",
    ok: "Rendben",
  },

  authProvider: {
    timeout: (operation: string) => `${operation} nem válaszol. Ellenőrizd az internetkapcsolatot, majd próbáld újra.`,
    googleAccount: "Ezt a fiókot Google-fiókkal hoztad létre. Lépj be a Google-gombbal.",
    appleAccount: "Ezt a fiókot Apple-fiókkal hoztad létre. Lépj be az Apple-gombbal.",
    googleToken: "A Google-belépés nem adott azonosító tokent. Próbáld újra.",
    appleToken: "Az Apple-belépés nem adott azonosító tokent. Próbáld újra.",
    cancelled: "A bejelentkezést megszakítottad.",
    firebaseOperation: "A Firebase-belépés",
    providerOperation: "A belépési mód ellenőrzése",
    serverOperation: "A belépési szerver",
    usernameBackend: "Felhasználónévvel csak a háttérszolgáltatással lehet belépni. Használd az e-mail-címed.",
    noUser: "Nincs bejelentkezett felhasználó.",
    noEmail: "Nincs e-mail-cím a fiókodhoz kapcsolva.",
    noPasswordAccount: "Nincs jelszavas belépéssel rendelkező fiók.",
  },

  socialAdmin: {
    title: "GRUNDO közösségi stúdió",
    subtitle: "Tartalomnaptár és közösségi csatornák",
    signInTitle: "Privát adminfelület",
    signInHint: "A belépéshez a GRUNDO számára engedélyezett Google-fiókodat használd.",
    signInGoogle: "Belépés Google-fiókkal",
    checking: "Hozzáférés ellenőrzése…",
    deniedTitle: "Ehhez a felülethez nincs hozzáférésed",
    deniedHint: "Csak a jóváhagyott, ellenőrzött Google-fiókok használhatják ezt a felületet.",
    signOut: "Kijelentkezés / fiókváltás",
    errorTitle: "Nem sikerült ellenőrizni a hozzáférést",
    retry: "Újrapróbálom",
    welcome: "Sikeres belépés",
    workspaceHint: "A social tartalomnaptár és a fiókkapcsolatok ezen a felületen kezelhetők.",
    workspaceNavigation: "Social Studio munkaterületek",
    calendarWorkspace: "Tartalomnaptár",
    contentEngineWorkspace: "Content Engine",
    contentFlow: contentFlowHu,
    contentWizard: contentWizardHu,
    contentEngine: {
      workspaceNavigation: "Content Engine munkafolyamat",
      refresh: "Frissítés",
      views: { campaigns: { title: "Kampányok és naptár", hint: "Tervezés és kreatív átadás", secondary: "Narratívák és kampányok kezelése", secondaryHint: "Hosszú távú történetívek, kampányfejezetek és jóváhagyások" }, knowledge: { title: "Tudástár", hint: "Üzenetek és ellenőrzések", lead: "A jóváhagyott üzenetek és a tudáskapcsolatok itt tarthatók karban. A részletes ellenőrzés csak igény esetén nyílik meg.", secondary: "Funkciók, állítások és tudáskapcsolatok", secondaryHint: "Részletes ellenőrzések és üzenetszintű kapcsolatok" }, data: { title: "Adatforrás", hint: "Import és verziók", lead: "Az aktív, jóváhagyott tartalomforrás és az importfolyamat egy helyen." } },
      nextEyebrow: "Következő lépések", nextTitle: "Kampány a naptárig", nextLead: "A munka sorrendje rögzített, a kreatívgyártás és a publikálás csak külön döntéssel indul.", nextSteps: { strategy: "Történetív és kampány jóváhagyása", plan: "Shadow naptárterv készítése", create: "Kiválasztott javaslat átadása kreatív briefként", approve: "Naptár-draft, kreatív ellenőrzés, majd külön időzítési jóváhagyás" },
      planning: {
        title: "Shadow tartalomtervező", lead: "A kiválasztott, jóváhagyott stratégiai forrásból készít visszanézhető tervezetet. Nem ír a tartalomnaptárba és nem publikál.",
        refresh: "Frissítés", loading: "Tervező betöltése…", loadError: "A tervező adatai nem tölthetők be.", noArc: "Előbb hozz létre és hagyj jóvá egy magyar történetívet.",
        savedRuns: "Korábbi tervezetek", noRuns: "Nincs mentett tervezet", settings: "Tervezési beállítások", arc: "Történetív", chooseArc: "Válassz történetívet", startDate: "Kezdőnap", horizon: "Időtáv", days7: "7 nap", days14: "14 nap", platform: "Ellenőrzött felület",
        advancedSettings: "Tartalmi kiválasztás és ritmus beállítása", cadence: "Csatornák és ritmus", cadenceHint: "Csatornánként jelöld ki a napokat, időpontot és kreatívformátumot. A hiányzó vagy téli/nyári időváltáskor nem egyértelmű időpontot a terv üresen hagyja.",
        channels: { facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok" }, weekdays: ["H", "K", "Sze", "Cs", "P", "Szo", "V"], time: "Időpont", format: "Formátum", formats: { feed_image: "Kép a hírfolyamban", carousel: "Lapozó", reel: "Reel", short_video: "Rövid videó" },
        ratiosAndWeights: "Arányok és pontozás", ratioHint: "A kategória- és mozgásarányok, valamint a pontsúlyok külön-külön pontosan 100%-ot adjanak ki.", categoriesTitle: "Témakategóriák", categories: { branding: "Márka", feature: "Funkció", fomo: "FOMO", curiosity: "Érdekesség" }, movementsTitle: "Mozgástípusok", movements: { walk: "Séta", run: "Futás", bike: "Kerékpár", mixed: "Vegyes" }, weightsTitle: "Pontsúlyok", weights: { strategy: "Stratégia", coverage: "Lefedettség", freshness: "Frissesség", audience: "Célcsoport", performance: "Teljesítmény" }, performanceHint: "Valós teljesítménymérés még nincs a forrásban, ezért a teljesítmény súlya kimarad, a többi pont arányosan újraszámolódik.",
        evergreenPercent: "Örökzöld minimum (%)", repeatDays: "Üzenet ismétlése után (nap)", topicRepeatDays: "Témaismétlés után (nap)", messages: "Felhasználható üzenetek", messageHint: "Csak itt, kifejezetten kijelölt üzenet kerülhet a tervbe. A kreatív ellenőrzése és a tudáskapcsolatok külön feltétel.", search: "Keresés", selectedCount: (count: number) => `${count} üzenet kijelölve`, reviewed: "tudáskapcsolat ellenőrizve", unreviewed: "tudáskapcsolat nincs ellenőrizve", useMessage: "Felhasználom", movement: "Mozgástípus", evergreen: "Örökzöldként is használható", campaigns: "Kapcsolt kampányok", noCampaigns: "Ehhez a történetívhez nincs jóváhagyott kampány.", unavailable: "Már nem elérhető", formatsTitle: "Engedélyezett formátumok", creativeReviewed: "Kreatív szerkesztőileg ellenőrizve", reason: "Szerkesztői indoklás", confirm: "Tudomásul veszem: ez csak tervezet, nem készít posztot és nem módosít időzítést.", generate: "Tervezet készítése", stale: "A stratégiai forrás vagy a naptár közben megváltozott. Frissíts és ellenőrizd újra.", invalid: "Ellenőrizd a dátumot, az arányokat, a kijelölt üzeneteket és a kampányokat.", actionError: "A tervezet nem készült el. Próbáld újra.",
        result: "Tervezeti eredmény", daysLabel: "nap", draftHint: "A sorok csak javaslatok. A meglévő naptár-bejegyzések foglaltnak látszanak, de semmi nem módosul.", stats: { slots: "idősáv", suggested: "javasolt", occupied: "foglalt", unfilled: "kitöltetlen" }, unmapped: (count: number) => `${count} meglévő poszt nem köthető bizonyítottan Content Engine-üzenethez; a naptárhelyét ettől még foglalja.`, coverage: "Célmegoszlás", coverageHint: "A cél az elérhető, nem foglalt idősávokra számolt kvóta. A tényleges érték a kiválasztott javaslatok aránya.", dimension: "Dimenzió", target: "Cél", actual: "Tényleges", channel: "Csatorna", category: "Kategória", status: "Állapot", all: "Mind", slots: "Idősávok", noResults: "Nincs a szűrésnek megfelelő idősáv.", rowReasons: { occupied: "Az idősávot meglévő naptárbejegyzés foglalja.", past_time: "Az idősáv már elmúlt.", invalid_time: "Ez az időpont az időzónaváltás miatt nem egyértelmű vagy nem létezik.", no_candidate: "Nincs olyan ellenőrzött üzenet, amely minden korlátnak megfelel." }, occupiedCount: (count: number) => `${count} meglévő poszt foglalja ezt az idősávot.`, campaign: "Kampány", imageCapability: "Képes végrehajtás", adapterMissing: "Nincs publikálási adapter ehhez a formátumhoz vagy csatornához.", score: "Pontszám", scoreHint: "Stratégia 30%, lefedettség 20%, frissesség 20%, célcsoport 15%; a teljesítményadat hiánya miatt ezek a súlyok vannak normalizálva.", component: "Összetevő", effectiveWeight: "Hatékony súly", noData: "nincs adat", exclusions: (count: number) => `${count} kizárt üzenet`, exclusionReasons: { missing_message: "Az üzenet már nem érhető el.", unknown_category: "Ismeretlen üzenetkategória.", creative_unreviewed: "A kreatív nincs szerkesztőileg ellenőrizve.", wrong_format: "Ez a formátum nincs engedélyezve.", category_quota: "A kategória kvótája betelt.", movement_quota: "A mozgástípus kvótája betelt.", message_repeat: "Az üzenet túl hamar ismétlődne.", topic_repeat: "A téma túl hamar ismétlődne.", evergreen_required: "Most örökzöld üzenet szükséges.", outside_campaign: "Nincs érvényes kampány vagy örökzöld történetív.", knowledge_blocked: "A tudáskapcsolat blokkolja az üzenetet.", no_channel_variant: "Nincs használható kreatívváltozat ezen a csatornán." }, provenance: "Források", utc: "UTC", sourceMessage: "Üzenet", sourceVariant: "Kreatívváltozat", sourceArc: "Történetív-jóváhagyás", sourceCampaign: "Kampányjóváhagyás", replay: "Rögzített bemenet", runId: "Tervezet azonosító", inputHash: "Bemeneti lenyomat", policyId: "Szabályzat lenyomata",
        handoff: { title: "Kreatív átadása", lead: "Előbb egy visszanézhető brief készül. A naptár-draft külön lépés; kép, időzítés és publikálás továbbra is külön döntés.", unsupported: "Ehhez az idősávhoz még nincs biztonságos kreatívátadási adapter.", reason: "Szerkesztői indoklás", confirmBrief: "Tudomásul veszem: a brief még nem hoz létre naptárbejegyzést.", createBrief: "Brief rögzítése", briefCreated: "A brief elkészült. Ellenőrizd, majd hozz létre szerkeszthető naptár-draftot.", briefDetails: "Brief részletei", confirmDraft: "Tudomásul veszem: a naptár-draft még nem készít képet és nem publikál.", createDraft: "Naptár-draft létrehozása", draftCreated: "A naptár-draft elkészült.", openCalendar: "Megnyitás a tartalomnaptárban", stale: "A terv vagy a naptárhely közben megváltozott. Frissíts és ellenőrizd újra.", invalid: "A kiválasztott kreatív nem adható át ebben az állapotban.", actionError: "Az átadás nem sikerült. Próbáld újra." },
      },
      narrative: {
        title: "Narratívák és kampányok",
        lead: "Hosszú távú történetív és kampányfejezetek. Előbb mentsd a változatot, majd külön hagyd jóvá.",
        loading: "Kampányok betöltése…", loadError: "A kampányok betöltése nem sikerült.",
        newArc: "Új történetív", newCampaign: "Új kampány", search: "Keresés", arcFilter: "Történetív",
        allArcs: "Minden történetív", showArchived: "Archiváltak is", timeline: "12 hónapos áttekintő",
        fromMonth: "Kezdő hónap", timelineEmpty: "Ebben az időszakban még nincs történetív vagy kampány.",
        list: "Történetívek és kampányok listája", empty: "Még nincs ilyen kampány. Hozd létre az első történetívet.",
        selectHint: "Válassz egy történetívet vagy kampányt, vagy hozz létre újat.",
        kinds: { arc: "Történetív", campaign: "Kampány" },
        active: "Aktív", archived: "Archivált", approved: "Jóváhagyva", changed: "Új változat / megváltozott forrás", draft: "Vázlat",
        fields: { id: "Azonosító", title: "Cím", objective: "Cél", startDate: "Kezdőnap", endDate: "Zárónap",
          status: "Állapot", locale: "Nyelv", region: "Országkód", evergreenPolicy: "Folyamatosan jelen lévő témák és célok",
          arcId: "Történetív", arcVersionId: "Kapcsolt történetív-változat", theme: "Fejezet témája",
          priority: "Prioritás (0–100)", audienceIds: "Célcsoportok", location: "Kampány helyszíne (opcionális)" },
        idHint: "Stabil azonosító: kisbetűk, számok, kötőjelek; legalább 2 karakter.",
        evergreenHint: "Az alapjáték és az új érdeklődőknek szóló bemutatás minden kampány mellett jelen marad.",
        parentHint: "A kampány a kiválasztott változatra hivatkozik. Jóváhagyásához jóváhagyott történetív szükséges.",
        chooseArc: "Válassz történetívet", previousArcVersion: "Korábban kapcsolt változat",
        noAudiences: "Még nincs aktív célcsoport a tudástárban. Általános kampány célcsoport nélkül is menthető.",
        unavailable: "Már nem elérhető; töröld a kijelölést", hu: "Magyar", en: "Angol",
        saved: "A változat mentve.", approvedNotice: "A mentett változat jóváhagyva.",
        stale: "Az adatok közben megváltoztak. Őrizd meg a szerkesztésed, majd elvetés és frissítés után ellenőrizd újra.",
        invalid: "Ellenőrizd a kötelező mezőket, az időszakot, a történetívet és a célcsoportokat.",
        actionError: "A művelet nem sikerült. Próbáld újra.",
        approvalTitle: "Mentett változat jóváhagyása",
        approvalHint: "A döntés a mentett tartalmat és annak forrásait rögzíti. A későbbi szerkesztés új változatot készít; a korábbi döntés visszanézhető.",
        reason: "Szerkesztői indoklás", approve: "Változat jóváhagyása", history: "Változatok és jóváhagyások",
        lastApproved: (revision: number) => `Utoljára jóváhagyott: ${revision}. változat`,
        sourceSnapshot: "Stratégiai forrás", sourceCounts: (brand: number, audiences: number) => `${brand} márkaszabály és ${audiences} célcsoport rögzített változata`,
        blocks: { no_source: "Nincs aktív stratégiai forrás.", brand_unapproved: "Előbb hagyd jóvá a jelenlegi márkaszabályokat a stratégiai könyvtárban.",
          archived: "Archivált rekord nem hagyható jóvá.", parent_unapproved: "Előbb hagyd jóvá az aktív történetívet.",
          parent_changed: "A kapcsolt történetív vagy annak forrása megváltozott. Ellenőrizd és válaszd ki a jóváhagyott változatot.",
          outside_arc: "A kampány időszaka túlnyúlik a jóváhagyott történetíven.", audience_unavailable: "Egy kijelölt célcsoport már nem aktív." },
      },
      eyebrow: "Stratégiai tartalomtervezés",
      title: "Content Engine",
      lead: "Az üzenet- és kreatívadatbázis itt különül el a posztgenerálástól és a publikálási folyamattól.",
      boundaryTitle: "Munkafolyamat-határok", boundaryLead: "Mi történik a stratégia, kreatív és publikálási lépésekben?",
      strategyTitle: "Stratégiai tervezés",
      strategyHint: "Üzenetek, tudáskapcsolatok, narratívák és verziózott kampányok. A mentett változatok külön szerkesztői jóváhagyást kapnak.",
      creativeTitle: "Kreatív Studio",
      creativeHint: "A meglévő naptárból indított szöveg- és médiagyártás külön szerkesztői döntés marad.",
      publishingTitle: "Időzítés és publikálás",
      publishingHint: "Csak külön jóváhagyás után, a meglévő Social Studio-folyamat küldhet posztot a csatornáknak.",
      stagingGateTitle: "Importdöntés",
      stagingGateLead: "Az érvényes fájl külön művelettel inaktív stagingbe menthető. Ezután diff és szerkesztői jóváhagyás következik.",
      integrityCheck: "Szerkezeti ellenőrzés",
      integrityReady: "A fájl előkészíthető staging ellenőrzésre.",
      integrityBlocked: (count: number) => `${count} blokkoló hiba javítandó.`,
      reviewCheck: "Szerkesztői felülvizsgálat",
      reviewRequired: (count: number) => `${count} figyelmeztetés szerkesztői döntést igényel.`,
      reviewClear: "Nincs nyitott figyelmeztetés.",
      activationCheck: "Aktiválás",
      activationLocked: "Zárolva: staging és kifejezett jóváhagyás nélkül nem aktiválható.",
      reviewNoWrite: "Az ellenőrzés önmagában nem ír Firestore-ba, nem készít kreatívot és nem változtat időzített posztot.",
      stage: "Staging előkészítése",
      staging: "Staging előkészítése…",
      stageHint: "A művelet megőrzi az eredeti XLSX-et és a 447 forrássort, de nem aktivál adatot és nem hoz létre posztot.",
      stagingError: "A staging előkészítése nem sikerült.",
      stagingReadyTitle: "Staging-verzió elkészült",
      stagingReadyLead: (count: number) => `${count} forrássor került az inaktív staging-verzióba.`,
      stagingId: "Staging azonosító",
      stagingStillInactive: "A verzió inaktív. Aktiválás előtt még diff és külön szerkesztői jóváhagyás szükséges.",
      importTitle: "Tartalomadatbázis ellenőrzése",
      importHint: "Az XLSX csak elemzésre töltődik fel: ez a lépés nem ír adatbázist, nem készít kreatívot és nem időzít posztot.",
      chooseFile: "XLSX kiválasztása",
      previewing: "Adatbázis ellenőrzése…",
      previewError: "Az adatbázis ellenőrzése nem sikerült.",
      fileError: "Legfeljebb 1 MB-os XLSX fájlt válassz.",
      validTitle: "Az adatbázis importálható staging ellenőrzésre",
      invalidTitle: "Az adatbázis hibákat tartalmaz",
      valid: "Érvényes",
      invalid: "Hibás",
      processedRows: (count: number) => `${count} feldolgozott sor`,
      messages: "Üzenetek",
      variants: "Kreatívvariánsok",
      rotations: "Rotációs napok",
      longSubtitles: "Hosszú feliratok",
      sheet: "Munkalap",
      rows: "Sorok",
      referenceRows: "Kiinduló sorok",
      errors: "Hibák",
      warnings: "Figyelmeztetések",
      noErrors: "Nincs blokkoló hiba.",
      noWarnings: "Nincs figyelmeztetés.",
      issueLocation: (sheet: string, row?: number) => ` (${sheet}${row ? ` · ${row}. sor` : ''})`,
      hash: "Fájl SHA-256",
      diffTitle: "Háromutas összevetés",
      diffLead: "Az előző import, a jelenlegi stratégiai adat és az új XLSX mezőnkénti összevetése. A kézi szerkesztés megőrződik, az eltérő változtatásokról te döntesz.",
      diffRefresh: "Diff készítése / frissítése",
      diffLoading: "Összevetés folyamatban…",
      diffError: "Az összevetés nem sikerült. Ellenőrizd a kulcsösszerendelést, majd készíts új diffet.",
      diffStale: "A stratégiai adatok közben megváltoztak. Frissítsd a diffet és ellenőrizd újra a döntéseket.",
      approvalError: "A jóváhagyás nem sikerült. Ellenőrizd a meződöntéseket és a hivatkozásokat.",
      diffBasis: (revision: number, epoch: number) => `Aktív stratégia: ${revision}. revízió · szerkesztési számláló: ${epoch}`,
      diffOutcomes: { unchanged: "Változatlan / kézi érték megőrzése", created: "Új sor", revised: "Változó sor", conflict: "Konfliktus", deferred: "Megtartott eltűnt sor" },
      diffChanges: { unchanged: "Azonos érték", incoming: "Új forrásérték", current: "Jelenlegi érték megőrzése" },
      diffMissingCount: (count: number) => `${count} korábbi sor hiányzik az új forrásból. Ezek megmaradnak.`,
      diffMissingHint: "Az új forrásból eltűnt sor megmarad; az archiválás külön döntés.",
      diffAliasLabel: "Ha ez átnevezés, válaszd ki a korábbi kulcsot:",
      diffAliasNew: "Önálló új sor",
      diffAliasApplied: "Kulcsösszerendelés alkalmazva (kikapcsolható)",
      diffAliasesChanged: "A kulcsösszerendelés módosult. Jóváhagyás előtt frissítsd a diffet.",
      diffField: "Mező", diffBaseline: "Előző import", diffCurrent: "Jelenlegi adat", diffIncoming: "Új XLSX", diffChoice: "Döntés",
      diffAbsent: "Nincs mező", diffChoose: "Válassz…", diffKeepCurrent: "Jelenlegi érték megtartása", diffUseIncoming: "Új XLSX-érték elfogadása",
      diffUnresolved: (count: number) => `${count} feloldatlan mezőkonfliktus.`,
      diffAcknowledgeWarnings: (count: number) => `Átnéztem a forrás ${count} figyelmeztetését.`,
      diffAcknowledgeMissing: "Elfogadom az eltűnt sorok megtartását és az új kulcsokat külön sorokként, ahol nincs összerendelés.",
      diffNote: "Szerkesztői indoklás (opcionális)",
      diffPreviousPage: "Előző oldal", diffNextPage: "Következő oldal",
      diffPage: (page: number, total: number) => `${page}/${total}. oldal`,
      diffApprovalHint: "A jóváhagyás csak inaktív importtervet és döntésnaplót ment. Nem aktivál stratégiát, nem hagy jóvá termékállítást, nem készít kreatívot és nem módosít posztot.",
      diffApprove: "Inaktív importterv jóváhagyása", diffApproving: "Jóváhagyás mentése…",
      diffApproved: "Jóváhagyott inaktív importterv elkészült. Az aktiválás külön lépés. Döntésazonosító:",
      diffHash: "Diff SHA-256",
      savedTitle: "Mentett stratégiai adatbázis",
      savedLoading: "Mentett importok betöltése…",
      savedError: "A mentett importok állapota nem tölthető be.",
      savedActive: (rows: number, revision: number) => `Aktív: ${rows} stratégiai forrássor · ${revision}. revízió`,
      savedNoUpload: "Az adatbázis megvan. XLSX-et csak új vagy módosított forrás importálásához kell kiválasztanod.",
      savedOpen: "Mentett import összevetése",
      savedEmpty: "Még nincs aktív stratégiai adatbázis.",
      savedRecent: "Legutóbbi mentett importok",
      savedActiveLabel: "Aktív",
      savedStates: { staging: "Előkészítés alatt", staged: "Mentett staging", failed: "Sikertelen előkészítés" },
      savedSameFile: "Ez a fájl már az aktív import forrása. Nem szükséges újra stagingbe menteni.",
      libraryTitle: "Stratégiai tudástár",
      knowledgeTitle: "Funkciók, állítások és tudáskapcsolatok",
      knowledgeLead: "Nyilvántartott termékfunkciók, bizonyítandó állítások és célcsoportok. Az üzenetekhez explicit kapcsolatokat rendelsz; a forrás alapján nem keletkezik automatikus igazolás.",
      knowledgeLoadError: "A tudásregiszter nem tölthető be.",
      knowledgeTabs: { feature: "Funkciók", claim: "Állítások", audience: "Célcsoportok", relation: "Kapcsolatok", messages: "Üzenetellenőrzés" },
      knowledgeNavigation: "Tudásregiszter nézetei", knowledgeCreate: "Új rekord", knowledgeEdit: "Rekord szerkesztése",
      knowledgePlatform: "Termékplatform", knowledgeRegion: "Régió", knowledgeApply: "Hatókör alkalmazása",
      knowledgeContextHint: "Az ellenőrzés az itt kiválasztott termékplatformra, régióra és a jelenlegi időpontra érvényes. Egy későbbi tervnél újra ellenőrizni kell.",
      knowledgePlatforms: { web: "Web", ios: "iOS", android: "Android" },
      knowledgeStatuses: { active: "Aktív", archived: "Archivált" }, knowledgeLocales: { hu: "Magyar", en: "Angol" },
      knowledgeLifecycles: { planned: "Tervezett", beta: "Béta", live: "Éles", retired: "Kivezetett" },
      knowledgeCommunications: { live_only: "Csak éles funkcióként", beta_with_label: "Bétaként, jelöléssel", planned_with_label: "Tervezettként, jelöléssel", blocked: "Nem kommunikálható" },
      knowledgeClaimTypes: { product: "Termékállítás", promotion: "Promóció", userMetric: "Valós játékos-/aktivitásadat", health: "Egészségállítás", other: "Egyéb állítás" },
      knowledgeSources: { documentation: "Dokumentált forrás", product_check: "Termékellenőrzés", analytics: "Valós analitika", simulation: "Alfa / szimuláció" },
      knowledgeRelations: { requires: "Megértést segítő előfeltétel", related_to: "Kapcsolódó téma", complements: "Kiegészíti", contrasts_with: "Kontrasztba állítható", targets: "Célcsoportnak szól", supports_message: "Üzenetet alátámasztó funkció / állítás" },
      knowledgeFields: { id: "Stabil azonosító", name: "Név / állítás szövege", description: "Leírás és kontextus", aliases: "Más elnevezések (vesszővel)", lifecycle: "Termékéletciklus", communication: "Kommunikációs szabály", claimType: "Állítás típusa", platforms: "Termékplatformok", regions: "Régiók (vesszővel; * = minden régió)", validFrom: "Érvényesség kezdete", expiresAt: "Érvényesség vége", reference: "Bizonyíték hivatkozása", source: "Bizonyíték eredete", observedAt: "Bizonyíték ellenőrzésének ideje", proofExpiry: "Bizonyíték lejárata", motivation: "Célcsoport motivációja", locale: "Nyelv", relation: "Kapcsolat típusa", sourceEntity: "Kiinduló rekord", targetEntity: "Célrekord", note: "Szerkesztői megjegyzés", status: "Nyilvántartási állapot", message: "Üzenet" },
      knowledgeEvidenceTitle: "Bizonyíték és szerkesztői igazolás",
      knowledgeEvidenceHint: "A hivatkozás mentése még nem igazolás. Az igazoló művelettel a mentett forrás ellenőrzését erősíted meg. Módosításkor új igazolás kell; valós játékosadathoz szimuláció nem fogadható el, és lejárati idő szükséges.",
      knowledgeVerify: "Mentett bizonyíték igazolása", knowledgeVerified: "Bizonyíték igazolva", knowledgeDraft: "Nincs igazolt bizonyíték", knowledgeArchived: "Archivált", knowledgeSaved: "Mentett",
      knowledgeSaveError: "A művelet nem sikerült. Ellenőrizd az azonosítót, a kötelező mezőket és a kapcsolat végpontjait.",
      knowledgeEvidenceError: "A bizonyíték hiányzik, lejárt, jövőbeli, vagy nem igazolhatja a valós állítást.",
      knowledgeStale: "Az adatok közben megváltoztak. Őrizd meg a módosításodat, majd frissíts és ellenőrizd újra.",
      knowledgeRelationsHint: "A kapcsolatok nem készülnek automatikusan a témaszövegből. Az előfeltétel ajánlás; egy önmagában érthető üzenetet nem tilt le.",
      knowledgeMessageHint: "Az alapüzenethez tartozó funkció-/állításkapcsolatokat ellenőrzöd. A kiválasztott kreatívvariáns, helyváltozó, média és csatorna később külön ellenőrzést kap; ez nem publikálási engedély.",
      knowledgeEligible: "Az alapüzenet stratégiai ellenőrzésen átmegy", knowledgeBlocked: "Nyitott stratégiai ellenőrzési pontok",
      knowledgeSupportCount: (count: number) => `${count} alátámasztó funkció-/állításkapcsolat.`,
      knowledgeClaimFree: "Átnéztem: ez az üzenet nem tartalmaz külön bizonyítandó termék-/tényállítást.",
      knowledgeReviewMessage: "Mentett üzenetkapcsolatok ellenőrzése", knowledgeMessageReviewed: "A mentett kapcsolatok felülvizsgálata rögzítve.",
      knowledgeReasons: { unreviewed_message: "Az üzenet kapcsolatai még nincsenek felülvizsgálva.", missing_evidence: "Hiányzó bizonyíték.", missing_expiry: "Valós játékosadatnak nincs lejárati ideje.", future_evidence: "A bizonyíték ellenőrzési időpontja jövőbeli.", expired_evidence: "Lejárt bizonyíték.", simulated_user_evidence: "Szimuláció nem igazolhat valós játékos-/aktivitásadatot.", unverified: "A mentett bizonyíték nincs igazolva.", archived: "Archivált forrásrekord.", outside_validity: "Az állítás/funkció az érvényességi időablakon kívül van vagy a promóció időablaka hiányzik.", wrong_platform: "Eltérő termékplatform.", wrong_region: "Eltérő régió.", feature_unavailable: "A funkció nem kommunikálható élesként.", missing_beta_label: "Hiányzik a megengedett bétajelölés.", missing_planned_label: "Hiányzik a megengedett tervezett/hamarosan jelölés.", blocked_feature: "A funkció kommunikációja tiltva van.", missing_relation_target: "Hiányzó kapcsolati végpont.", unresolved_location: "Feloldatlan hely- vagy tényváltozó.", promotion_proof_required: "Promócióhoz kapcsolt, érvényes promóciós állítás szükséges.", local_user_proof_required: "Helyi közösségi állításhoz friss, valós játékosadatot igazoló kapcsolat szükséges.", requires_advisory: "Az előfeltétel kommunikációs ajánlás, nem tiltás.", requires_cycle: "Körkörös előfeltétel; szerkesztői figyelmeztetés." },
      libraryLead: "Az aktív adatbázis márkaszabályai, hitelességi szabályai, tervezési sablonjai és üzenetcsomagjai. A forrásszöveg szerkeszthető, az eredeti Excel-érték megmarad.",
      libraryLoading: "Tudástár betöltése…", libraryLoadError: "A tudástár nem tölthető be.",
      libraryRefresh: "Tudástár frissítése", libraryNavigation: "Tudástár nézetei",
      libraryTabs: { brand: "Brand Brain", policy: "Hitelességi szabályok", templates: "Tervezési sablonok", messages: "Üzenetek és variánsok" },
      libraryApproved: "Mentett tartalom jóváhagyva", libraryDraft: "Szerkesztői jóváhagyásra vár",
      libraryApprove: "Mentett forráscsoport jóváhagyása",
      libraryApprovalHint: "Ez a mentett márka-, szabály- vagy sablonszöveg jóváhagyása. Nem igazol termékállítást, nem készít kreatívot és nem időzít posztot.",
      libraryApprovedNotice: "A mentett forráscsoport jóváhagyása rögzítve.",
      libraryUnverified: "Az importált termékstátusz és feltétel forrásszöveg. A funkciók és állítások bizonyítékainak ellenőrzése még külön lépés.",
      librarySearch: "Keresés a kiválasztott csoportban", libraryNoResults: "Nincs találat.",
      libraryDirty: "Nem mentett módosítás van. Mentés vagy elvetés után válthatsz nézetet és hagyhatsz jóvá.",
      libraryVariants: (count: number) => `${count} összetartozó kreatívvariáns`,
      libraryRevision: (revision: number) => `${revision}. rekordrevízió`,
      librarySave: "Módosítás mentése", libraryCancel: "Módosítás elvetése",
      librarySaveError: "A módosítás mentése nem sikerült.", libraryApprovalError: "A jóváhagyás nem sikerült.",
      libraryStale: "A tudástár közben megváltozott. Őrizd meg a szerkesztésedet, majd frissíts és ellenőrizd újra.",
      libraryFields: { sourceId: "Forrásazonosító", sourceLabel: "Terület / kockázat", statement: "Irányelv", interpretation: "Gyakorlati jelentés", ruleText: "Szabály", example: "Példa / javaslat", category: "Kategória", topicRaw: "Funkció / téma", title: "Cím", subtitle: "Teljes alcím", objectiveRaw: "Cél", audienceRaw: "Célcsoport / nézőpont", visualIdea: "Képi alapötlet", platformPreferences: "Platformfókusz", sourceStatus: "Forrás szerinti állapot", editorialRequirements: "Szerkesztői feltételek", expectedVariantCount: "Várt variánsszám", compatibleCategories: "Kombinálható kategóriák", messageId: "Üzenetazonosító", variantNo: "Variánsszám", caption: "Posztszöveg", creativeDirectionRaw: "Kreatív irány", visualPrompt: "Kép-/videóbrief", cta: "Felhívás", hashtagsRaw: "Hashtagek", messageGroup: "Üzenetcsoport", objective: "Kampánycél", creativeFormat: "Kreatív forma", mediaType: "Médiatípus", ctaType: "Felhívás típusa", secondaryTopic: "Kiegészítő téma", metric: "Mérőszám", requirements: "Előfeltétel", relativeDay: "Mintanap", titleSnapshot: "Mintacím" },
      activate: "Jóváhagyott stratégiai import aktiválása",
      activating: "Stratégiai import aktiválása…",
      activationHint: "Ez a külön művelet a jóváhagyott importot az aktív stratégiai forrásadatok közé emeli. Nem indít gyártást, nem hagy jóvá termékállítást és nem módosít posztot.",
      activationError: "Az aktiválás nem sikerült. A részleges snapshot inaktív marad; a művelet újrapróbálható.",
      activated: (count: number, revision: number) => `${count} stratégiai forrássor aktív a ${revision}. dataset-revízióban.`,
    },
    operations: "GRUNDO üzemeltetési admin",
    connectionsTitle: "Közösségi fiókok",
    connectionsHint: "A kapcsolt fiókokat az OAuth-belépés engedélyeivel kezeljük. A hozzáférési tokenek titkosítva, csak a szerveren tárolódnak.",
    metaConnectionHint: "Facebook-oldal és a hozzá kapcsolt Instagram Business/Creator-fiók.",
    tiktokConnectionHint: "A GRUNDO TikTok-fiók kapcsolása a hivatalos TikTok Login Kiten keresztül.",
    connectMeta: "Facebook és Instagram összekapcsolása",
    connectTikTok: "TikTok összekapcsolása",
    disconnect: "Leválasztás",
    oauthNotConfigured: "Előbb be kell állítani a fejlesztői appot és a szerver titkait.",
    oauthDeveloperPortal: "Fejlesztői portál megnyitása",
    oauthConnected: "A közösségi fiók összekapcsolása sikerült.",
    oauthDisconnected: "A közösségi fiók leválasztva.",
    oauthFailed: "A fiók összekapcsolása nem sikerült. Ellenőrizd a fejlesztői app beállításait és a jóváhagyott jogosultságokat.",
    oauthActionError: "A fiókkapcsolat kezelése nem sikerült.",
    calendarTitle: "Tartalomnaptár",
    calendarHint: "Az időpontok budapesti idő szerintiek; a délelőtti sávokat teszteljük, a délutániakat friss iparági adatok alapján választottuk.",
    weekSummary: "Heti összesítő",
    totalPosts: (count: number) => `Összesen ${count} poszt`,
    creativeTitleLabel: "Kreatív címsor / szlogen",
    sloganPickerPlaceholder: "Válassz címsort vagy írd át alatta",
    sloganOptions: [
      "Ne csak ingázz, bringázz!",
      "A város a játéktered",
      "Egy város, 3 mozgásforma",
      "Kerüld a monotonitást",
      "Ne csak haladj, szaladj!",
      "A séta is lehet kaland",
      "Gyalog is igazi király lehetsz",
      "Hódítsd meg a Grundot!",
      "Szerezd meg a Parlament!",
      "Szerezd meg a Városliget!",
    ],
    previousWeek: "Előző hét",
    nextWeek: "Következő hét",
    generateCalendar: "Következő 7 nap megtervezése",
    generatingCalendar: "Naptár készítése…",
    calendarGenerated: (created: number, existing: number, rescheduled: number) => `${created} új terv készült; ${existing} meglévő terv megmaradt${rescheduled ? `, ${rescheduled} alapértelmezett időpontot frissítettünk` : ''}.`,
    calendarLoadError: "A tartalomnaptár nem tölthető be.",
    calendarGenerateError: "Nem sikerült létrehozni a naptártervet.",
    noPosts: "Erre a napra még nincs posztterv.",
    noPostsInRange: "Ebben az időszakban még nincs posztterv. Készíts heti tervet a gombbal.",
    loadingCalendar: "Naptár betöltése…",
    channel: "Csatorna",
    pillar: "Tartalmi pillér",
    format: "Formátum",
    status: "Állapot",
    scheduledAt: "Tervezett időpont",
    titlePlaceholder: "Rövid cím",
    captionPlaceholder: "Képaláírás és CTA",
    overlayPlaceholder: "Képre kerülő rövid szöveg",
    visualPromptPlaceholder: "Írd le a képen vagy videóban látható jelenetet",
    visualPromptLabel: "Generálási prompt — szerkeszthető",
    visualThemeLabel: "Kreatív háttérszíne",
    visualThemes: { white: "Fehér · sötét logó", charcoal: "Antracit · világos logó", purple: "Lila · világos logó" },
    weekDaysLabel: "A hét napjai, vízszintesen görgethető tartalomnaptár",
    collapseDay: "Nap összecsukása",
    expandDay: "Nap kinyitása",
    imagePreviewAlt: "A poszt generált kreatívja",
    noImageGenerated: "Itt jelenik meg a frissen generált kreatív",
    savePost: "Szöveg mentése",
    savingPost: "Mentés…",
    generateCopy: "Szövegjavaslat",
    generatingCopy: "Szöveg készítése…",
    generateImage: "Gemini-kép generálása",
    generatingImage: "Gemini-kép készítése…",
    generateAdobeImage: "Adobe-kép generálása",
    generatingAdobeImage: "Adobe-kép készítése…",
    generatingVideo: "Gemini-videó készítése…",
    generateVideo: "Gemini-videó generálása",
    imageGenerated: "Új kép készült a Gemini segítségével.",
    adobeImageGenerated: "Új kép készült az Adobe Firefly segítségével.",
    videoGenerated: "Új TikTok-videó készült a Gemini segítségével.",
    adobeApiSetupHint: "Az Adobe-gombhoz Firefly Services API-hozzáférés és szerveroldali hitelesítő kulcsok szükségesek.",
    imageGenerationError: "Nem sikerült a kreatív generálása. Próbáld újra később.",
    approvePost: "Jóváhagyás és időzítés",
    postSaved: "Posztterv elmentve.",
    copyGenerated: "Új szövegjavaslat készült. Szerkeszd át, mielőtt jóváhagyod.",
    postApproved: "Jóváhagyva. A poszt a beállított időpontban megjelenik.",
    postActionError: "A poszt mentése nem sikerült.",
    approvedNeedsCopy: "Jóváhagyás előtt cím és képaláírás szükséges.",
    channels: { facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok" },
    pillars: { brand: "Márka", feature: "Funkció", community: "Közösség" },
    formats: { feed_image: "Feedkép", carousel: "Lapozható poszt", reel: "Reel", short_video: "Rövid videó" },
    statuses: { planned: "Tervezett", draft: "Szerkesztés alatt", approved: "Jóváhagyva", scheduled: "Ütemezett", publishing: "Közzététel alatt", published: "Publikálva", failed: "Sikertelen", discarded: "Elvetett" },
  },

  birthDate: {
    invalid: "Adj meg egy érvényes születési dátumot (ÉÉÉÉ-HH-NN).",
    future: "A születési dátum nem lehet a jövőben.",
  },

  googleAuthErrors: {
    configuration: "A Google-belépés ebben a telepített változatban nincs helyesen beállítva. Lépj be e-mail-címmel és jelszóval, és jelezd a hibát.",
    noAccount: "Nem találtunk Google-fiókot a készüléken. Add hozzá a fiókodat a rendszer beállításaiban, vagy lépj be e-mail-címmel és jelszóval.",
    network: "A Google-belépés nem érte el a hálózatot. Ellenőrizd a kapcsolatot, majd próbáld újra.",
  },

  appleAuthErrors: {
    configuration: "Az Apple-belépés ebben a telepített változatban nincs helyesen beállítva. Lépj be e-mail-címmel és jelszóval, vagy próbáld a Google-belépést, és jelezd a hibát.",
    unexpected: "Az Apple-belépés váratlan hibába ütközött. Próbáld újra.",
    network: "Az Apple-belépés nem érte el a hálózatot. Ellenőrizd a kapcsolatot, majd próbáld újra.",
  },

  discover: {
    failed: "A keresés most nem működik.",
    searchType: "Keresés típusa",
    people: "Emberek",
    crews: "Bandák",
    username: "Felhasználónév",
    searchUsername: "Felhasználónév keresése",
    clear: "Keresés törlése",
    hint: "Kezdj el gépelni egy felhasználónevet, vagy nézd meg lent, ki mozgott mostanában.",
    searching: "Keresés…",
    empty: "Nincs ilyen nevű felhasználó.",
    following: "Követve",
    requestSent: "Kérés elküldve",
    request: "Kérés küldése",
    follow: "Követés",
    feedTitle: "Ki mozog most?",
    feedView: "Feed nézete",
    latest: "Legújabb",
    local: "Helyi",
    noPosition: "Nincs helyadat",
    noPositionHint: "A helyi nézethez engedélyezd a helymeghatározást a böngészőben, vagy válaszd a Legújabb nézetet.",
    retry: "Újrapróbálom",
    loading: "Betöltés…",
    newFaces: "Új arcok — akiket még nem követsz",
    noNewFaces: "Nincs új arc a közelben — itt mindenki aktivitása látszik",
    noActivities: "Ebben a nézetben most senki nem mozgott. Próbáld a másik nézetet.",
  },

  search: {
    failed: "A keresés most nem működik.",
    title: "Keresés",
    username: "Felhasználónév",
    searchUsername: "Felhasználónév keresése",
    clear: "Keresés törlése",
    hint: "Kezdj el gépelni egy felhasználónevet.",
    crewSuggestions: "Banda-ajánlások",
    searching: "Keresés…",
    empty: "Nincs ilyen nevű felhasználó.",
  },

  passport: {
    title: 'Útlevél',
    coverTitle: 'ÚTLEVÉL',
    coverSubtitle: 'PASSPORT',
    count: (count: number, total: number) => `${count} / ${total} ország`,
    totalCount: (count: number, total: number) => `Összesen ${count} / ${total} ország`,
    nextMilestone: (target: number) => `Következő Felfedező-jelvény: ${target} ország`,
    emptyTitle: 'Még üres az útleveled',
    emptyText: 'Ments egy aktivitást, és megkapod az első pecsétet.',
    loading: 'Útlevél betöltése…',
    loadFailed: 'Nem sikerült betölteni az útlevelet.',
    retry: 'Újrapróbálom',
    approved: 'APPROVED',
    pageTitle: 'Vízum',
    stampNewTitle: 'Új ország az útleveledben!',
    stampContinue: 'Szuper!',
    stampOpen: 'Útlevél megnyitása',
    firstVisit: 'Első látogatás',
    activities: 'Aktivitások',
    areaGained: 'Ott szerzett terület',
    close: 'Bezárás',
    flagLabel: (name: string) => `${name} — részletek`,
    stampLabel: (name: string) => `${name}: pecsét az útlevélben`,
  },
  plannedCommunity: {
    challenges: "A kihívások hamarosan érkeznek",
    challengesNote: "Időszakos, admin által kiírt feladatok — távolság, terület, lopás, sorozat, felfedezés — kártyás listája és a haladásod itt jelenik majd meg.",
  },

  profileCrews: {
    failed: "A bandáid most nem tölthetők be.",
    loading: "Betöltés…",
    empty: "Még nem vagy egy bandában sem",
    hint: "A Közösség → Bandák oldalon csatlakozhatsz egy publikus bandához, léphetsz be meghívókóddal, vagy alapíthatsz sajátot.",
    browse: "Bandák böngészése",
    private: "privát",
  },

  profileStats: {
    run: "Futás",
    walk: "Séta",
    ride: "Bringa",
    type: "Aktivitástípus",
    failed: "Nem sikerült betölteni",
    failedNote: "A statisztikák most nem érhetők el. Próbáld újra később.",
    loading: "Betöltés…",
    empty: "Még nincs adat",
    emptyNote: "Az első aktivitásod után itt jelennek meg a rekordjaid és összesítéseid.",
    allTime: "Mindenkori",
    distance: "Távolság",
    activities: "Aktivitások",
    movingTime: "Mozgásidő",
    longest: "Leghosszabb",
    speed: "Átlagsebesség",
    pace: "Átlagtempó",
    best: "Legjobb eredmények",
    area: "Terület aktivitással",
    stolen: "Elvett mező",
    breakthrough: "Áttört mező",
    gp: "GP az aktivitásokból",
    base: "  alap",
    claim: "  területfoglalás",
    takeOver: "  elvétel",
    breakthroughGp: "  áttörés",
    other: "  sorozat-szorzó és egyéb",
    truncated: "Csak a legutóbbi 5000 aktivitás szerepel az összesítésben.",
  },

  rivalsSheet: {
    truncated: (count: number) => `A legtöbbet cserélt ${count} rivális látszik. Akikkel ennél kevesebb mező cserélt gazdát, azok most kimaradnak.`,
    failed: "A rivális-lista most nem tölthető be. Próbáld később.",
    title: "Riválisok",
    close: "Bezárás",
    search: "Keresés név szerint",
    searchLabel: "Keresés a riválisok között",
    loading: "Betöltés…",
    empty: "Még nincs riválisod. Akkor lesz, ha területet veszel el valakitől — vagy ő tőled.",
    noMatch: "Nincs ilyen nevű riválisod.",
  },

  rivalExplain: {
    youTook: (rival: string) => `Ennyi mezőt vettél el tőle: ${rival}.`,
    theyTook: (name: string, rival: string) => `Ennyi mezőt vett el ${name} tőle: ${rival}.`,
    close: "Bezárás",
    title: "Riválisok",
    and: "és",
    activity: "Ebben az aktivitásban",
    gained: "Összesen elvett",
    gainedHint: "Minden megszerzett mező a riválistól.",
    lost: "Összesen elvesztett",
    lostHint: "Rivális által elvett mezők száma.",
    multiplier: "Rivális szorzó",
    multiplierHint: "Hány alkalommal vettek már el egymástól mezőket.",
    others: "További megszerzett mezők",
  },

  rivalsCard: {
    title: "Riválisok",
    you: "Te",
    all: "Összes rivális",
  },

  liveBattle: {
    locate: (name: string) => `Ugrás ${name} pozíciójára`,
    loading: "Betöltés…",
    waiting: "Várakozás a játékosokra…",
    waitingNote: "Amint egy játékos belép, megjelenik a térkép nézet.",
    view: "Nézet",
    shared: "Közös",
    split: "Osztott",
    noMap: "A térkép nem érhető el.",
    cells: "Mezők",
    new: "Új",
    stolen: "Lopott",
    lost: "Vesztett",
    defended: "Védett",
    estimateBefore: "A mezők",
    estimate: "élő becslés",
    estimateAfter: ": a végleges eredmény a mentéskor dől el.",
  },

  finishButtons: {
    finish: "Befejezés",
    hold: "Befejezés — tartsd nyomva egy másodpercig",
    swipe: "Befejezés — húzd jobbra a sáv végéig",
  },

  territoryToast: {
    reinforcedSummary: (count: string) => `${count} cella megerősítve`,
    claimedSummary: (area: string, count: string) => `${area} · ${count} cella`,
    claimed: "Grund megszerezve!",
    stolenTitle: "Grund elfoglalva!",
    reinforcedTitle: "Grund megerősítve!",
    close: "Üzenet bezárása",
    stolen: "elvéve",
    new: "új",
    reinforced: "megerősítve",
    max: "maxon",
    breakthrough: "áttörve",
  },

  weatherWidget: {
    detailsMetric: (description: string, celsius: number) => `${description}, ${celsius} fok. Részletek megnyitása.`,
    details: (description: string, temperature: string) => `${description}, ${temperature}. Részletek megnyitása.`,
    location: (place: string) => `Lokáció: ${place}. Lokáció módosítása.`,
    ask: "Időjárás megjelenítése a helyzeted alapján",
    show: "Időjárás megjelenítése",
    title: "Időjárás:",
    precipitation: "csapadék esélye",
    humidity: "páratartalom",
    wind: "szél",
    city: "Város választása",
    here: "Saját helyzet",
  },

  refhex: {
    reward: (gp: number, fast: boolean) => `+${gp} GP · ${fast ? 'Gyors' : 'Lassú'} mód napi jutalma`,
    hitCount: (count: number) => `${count} találat`,
    errorCount: (count: number) => `${count} hiba · büntetés: ${count} mp`,
    hiddenGame: 'REJTETT GRUNDO MINIGAME', sound: 'Hang:',
    rewardUsed: "Ennek a módnak a napi 50 GP jutalmát már átvetted.",
    uploadFailed: "Az eredményt most nem sikerült feltölteni.",
    rankingFailed: "A globális toplista most nem érhető el.",
    title: "REFHEX reakciójáték",
    close: "REFHEX bezárása",
    timeLeft: "Hátralévő idő",
    second: "mp",
    fastLower: "gyors",
    slowLower: "lassú",
    mode: "mód",
    reaction: "Reakcióidő",
    best: "Legjobb:",
    hits: "Találat",
    penaltyPrefix: "Büntetés: −",
    target: "Lila célpont",
    fast: "GYORS",
    slow: "LASSÚ",
    global: "Globális ranglista · játékosonként a legjobb eredmény",
    rankingLoading: "Toplista betöltése…",
    rankPlayer: "Hely / játékos",
    average: "Átlag",
    empty: "Még senki nem fejezett be játékot ebben a módban.",
    ready: "Készülj!",
    aborted: "Játék megszakítva",
    question: "Készen állsz?",
    background: "Az app háttérbe került. Indíts új játékot!",
    fastHint: "Koppints a lila hexagonra! Hibánként −1 mp. A következő célpont azonnal jön.",
    slowHint: "Koppints a lila hexagonra! A célpontok között 1–3 mp szünet van, ilyenkor az óra áll.",
    modeLabel: "Játékmód",
    slowPause: "1–3 mp szünet",
    instant: "azonnali célpont",
    restart: "ÚJRAKEZDÉS",
    start: "START",
    vibration: "Rezgés:",
    on: "be",
    off: "ki",
  },

  modeChooser: {
    keep: (mode: string) => `Maradjon a legutóbbi (${mode})`,
    choose: "Üzemmód választása",
    title: "Milyen módban indulj?",
    lead: "Ezt a kérdést csak a tesztelői kör kapja. A választás bármikor módosítható a Beállításokban.",
    normal: "Normál",
    normalHint: "Pontosan az, amit a felhasználó lát. Semmilyen fejlesztői eszköz nem fut.",
    debugHint: "Lebegő hibabejelentő gomb és napló. A napló a hibakereséshez gyűjti, mi történt — helyadatot nem tartalmaz.",
    remember: "Ne kérdezd többet, indulj a legutóbbi módban",
  },

  bugReport: {
    low: "Apróság",
    normal: "Zavaró",
    high: "Használhatatlan",
    failed: "A bejelentést nem sikerült elküldeni.",
    close: "Bezárás",
    sent: "Elküldve. Azonosító:",
    done: "Kész",
    preview: "Képernyőkép előnézet",
    question: "Mi történt?",
    placeholder: "Például: a Play gombra koppintva nem indult el a visszaszámlálás.",
    cancel: "Mégse",
    send: "Küldés",
  },

  recorderErrors: {
    start: "A helymeghatározás nem indult el.",
    restore: "A folyamatban lévő mérés helyreállítása nem sikerült.",
    unconfigured: "A háttérszolgáltatás nincs beállítva, a mentés nem megy.",
    save: "A mentés nem sikerült.",
  },

  nativeLocationErrors: {
    permission: "Nincs helyhozzáférés. Engedélyezd a GRUNDO számára a készülék beállításaiban.",
    disabled: "A helymeghatározás ki van kapcsolva a készüléken.",
    failed: "A helymeghatározás nem sikerült.",
  },

  browserLocationErrors: {
    unsupported: "Ez a böngésző nem tud helymeghatározást.",
    secure: "A helymeghatározáshoz biztonságos kapcsolat (https) kell.",
    permission: "Nincs helyhozzáférés. Engedélyezd a böngésző beállításaiban.",
    signal: "Most nincs GPS-jel. Menj szabad ég alá, és próbáld újra.",
    waiting: "Nem érkezett helyadat. Keressük a jelet…",
    failed: "A helymeghatározás nem sikerült.",
  },

  positionErrors: {
    empty: "Nem érkezett helyadat. Ellenőrizd, hogy a helymeghatározás be van-e kapcsolva.",
    failed: "Nem sikerült helyzetet mérni.",
    unsupported: "Ez a böngésző nem tud helyet meghatározni.",
  },

  screenshotErrors: {
    native: 'A képernyőkép csak a natív alkalmazásban érhető el.',
    failed: 'A képernyőkép nem készült el.',
  },
  videoErrors: {
    tooLarge: (mb: string) => `A videó ${mb} MB lett, a feltöltés korlátja 50 MB — ezért nem küldhető el.`,
    readFailed: (reason: string) => `A videó beolvasása nem sikerült: ${reason}`,
    native: "A videó csak a natív alkalmazásban érhető el.",
    empty: "A videó üres lett.",
    failed: "A videó nem készült el.",
  },

  assistantChat: {
    suggestionRunImperial: '5 mérföldes futókör a közelben, sík terepen',
    guessedPlace: (name: string) => `erre gondoltál („${name}”)?`,
    whichPlace: (name: string) => `melyik „${name}”?`,
    more: (count: string) => `Több találat (${count})`,
    searchRole: (role: string) => `${role} keresése`,
    stop: (n: number) => `Megálló ${n}`,
    messageCount: (count: string) => `${count} üzenet`,
    deleteTitle: (title: string) => `Törlés: ${title}`,
    forget: (text: string) => `Elfelejtés: ${text}`,
    suggestionRun: "8 km-es futókör a közelben, sík terepen",
    suggestionWalk: "Séta a Margitszigeten",
    suggestionRide: "Bringával a Normafáig, csendes utakon",
    history: "Előzmények",
    memory: "Amit megjegyeztem",
    back: "Vissza a beszélgetéshez",
    subtitle: "AI-asszisztens · útvonaltervezés",
    menu: "Menü",
    close: "Bezárás",
    new: "Új beszélgetés",
    withdrawHint: "Minden beszélgetés és emlék törlődik, és a következő kérésnél újra megkérdezünk.",
    wait: "Pillanat…",
    withdrawDelete: "Visszavonom és törlöm",
    withdraw: "Hozzájárulás visszavonása",
    greeting: "Szia, Grout vagyok! Mondd el, hová mennél vagy mihez van kedved — segítek összerakni az útvonalat, és emlékszem arra, amit legutóbb szerettél.",
    thinking: "Grout gondolkodik…",
    any: "Mindegy",
    quickReplies: "Gyorsválaszok",
    remembered: "Megjegyeztem:",
    activity: "Mozgásforma",
    activityQuestion: "Gyalog, futva vagy bringával?",
    run: "Futás",
    walk: "Séta",
    ride: "Bringa",
    applied: "Beállítottam",
    notThis: "Nem ez — keresek",
    none: "Egyik sem — keresek",
    searchPrecise: ": keresd meg pontosabban",
    searchPlaceholder: "Pl. Szentendre, Fő tér",
    searching: "Keresés…",
    noResults: "Nincs találat — próbáld másképp, pl. településsel együtt.",
    notHere: "Kihagyom",
    backResults: "Vissza a találatokhoz",
    start: "Rajt",
    end: "Cél",
    planner: "Megnyitom a tervezőben",
    consentBefore: "A beszélgetést a",
    consentProcessing: "dolgozza fel, EU-s adatközpontban. A beszélgetéseidet",
    consentSaved: "elmentjük",
    consentAfter: ", és Grout megjegyzi belőlük, ami a későbbi tervezéshez hasznos (pl. választott helyek, mozgásformák, amit magadról mondasz).",
    consentVoice: "Ha a billentyűzet diktálását vagy a mikrofont használod, a telefonod beszédfelismerője (Google vagy Apple) is feldolgozhatja.",
    consentControl: "Az emlékeket és a beszélgetéseket itt bármikor megnézheted és törölheted; a hozzájárulás visszavonásakor minden törlődik. Nélküle is tervezhetsz, a beállításokkal.",
    accept: "Elfogadom",
    notNow: "Most nem",
    placeholder: "Írj Groutnak…",
    message: "Üzenet Groutnak",
    send: "Küldés",
    consentFirst: "Előbb fogadd el a hozzájárulást.",
    loading: "Betöltés…",
    noHistory: "Még nincs elmentett beszélgetésed.",
    conversation: "Beszélgetés",
    deleteWarning: "Minden beszélgetés és minden emlék törlődik. Ez nem vonható vissza.",
    deleteAll: "Mindent törlök",
    clearHistory: "Minden előzmény és emlék törlése",
    runLower: "futás",
    walkLower: "séta",
    rideLower: "bringa",
    loopLower: "oda-vissza",
    directLower: "csak oda",
    detourSmall: "kis kerülő",
    detourMedium: "közepes kerülő",
    detourLarge: "nagy kerülő",
    quiet: "csendes utak",
    protected: "védett utak",
    fast: "gyors út",
    flat: "sík",
    hilly: "dombos",
    balanced: "vegyes terep",
    cycleways: "kerékpárutak",
    memoryHint: "Ezekből dolgozom, amikor útvonalat tervezünk. Bármelyiket törölheted; a beszélgetéseket az Előzményeknél.",
    emptyMemory: "Még nem jegyeztem meg semmit.",
    facts: "Amit magadról mondtál",
    places: "Helyek, amiket választottál",
    habits: "Szokások",
    recent: "Legutóbbi kérések",
  },

  assistantPrompt: {
    conversation: "Beszélgetés",
    send: "Küldés Groutnak",
  },

  routeAssistant: {
    currentPosition: 'Jelenlegi pozícióm',
    notFound: (name: string) => `Nem találtam: „${name}” — keresd meg lent, vagy jelöld ki a térképen.`,
    terrainUnavailable: (terrain: string) => `A terep („${terrain}”) még nem állítható.`,
    destination: (label: string) => `Cél: ${label}`,
    start: (label: string) => `Rajt: ${label}`,
    stop: (label: string) => `Megálló: ${label}`,
    distance: (distance: string) => `Kért táv: ${distance} — a kerülő méretét lent te választod.`,
    unsupported: (items: string) => `Ezt még nem tudom figyelembe venni: ${items}.`,
    quota: (count: number) => `Ma még ${count} kérés.`,
    run: "Futás",
    walk: "Séta",
    ride: "Bringa",
    loop: "Oda-vissza",
    direct: "Csak oda",
    small: "Kis kerülő",
    medium: "Közepes kerülő",
    large: "Nagy kerülő",
    protected: "Védett út",
    fast: "Gyors",
    quiet: "Nyugalom",
    flat: "Sík",
    balanced: "Vegyes terep",
    hilly: "Dombos",
    cycleways: "Kerékpárutak előnyben",
    noCycleways: "Kerékpárút: mindegy",
    unnamed: "a térképen kijelölt pont",
    modeQuestion: "Oda-vissza kört mennél, vagy csak odáig?",
    detourQuestion: "Mekkora kerülővel? Nagyobb kerülővel több területet foglalhatsz.",
    roadQuestion: "Milyen utakon mennél?",
    terrainQuestion: "Milyen terepen?",
    cyclewayQuestion: "A kerékpárutakat vegyem előre?",
    greenQuestion: "Parkon, zöldön át mennél, vagy a legrövidebb úton?",
    greenPark: "Parkon át",
    greenShortest: "Legrövidebb úton",
    greenStop: (name: string) => `Útba ejtjük: ${name}`,
    greenNone: "Az utadba eső parkot nem találtam — csendes utakra állítottam.",
    yes: "Igen",
    no: "Nem kell",
    failed: "Nem sikerült elérni az asszisztenst. Ellenőrizd a kapcsolatot, és próbáld újra.",
  },

  assistantStore: {
    stop: (n: number) => `Megálló ${n}`,
    reject: (role: string) => `Oké, nem az. Írd le pontosabban, hol van (pl. utca, kerület, település), vagy keresd meg a tervezőben a ${role.toLowerCase()} mezőben.`,
    none: (role: string, name: string) => `${role}: egyik sem („${name}”)`,
    run: "Futás",
    walk: "Séta",
    ride: "Bringa",
    closing: "Minden megvan. Kérsz még valamit, mielőtt tervezünk? Például egy megállót útközben, más kerülőt, vagy hogy kerülje a főutakat. Ha nem, nyisd meg a tervezőben.",
    start: "Rajt",
    end: "Cél",
  },

  notificationText: {
    newBoost: 'Új akció',
    modifier: "Aktív akció indult",
    badge: "Új jelvény!",
    passport: "Új ország az útleveledben!",
    passportBody: (country: string) => `${country} — új pecsét az útleveledben.`,
    stolen: "Elvették a grundod",
    rivalAttack: "Egy riválisod megtámadta a grundod!",
    summary: "Aktivitás összegzés",
    saved: "Kész a mentés",
    reviewApproved: "Az aktivitásod érvényes",
    reviewRejected: "Az aktivitásod nem érvényes",
    reviewCredit: "Az ellenőrzés lezárult, jóváírtuk.",
    reviewNoCredit: "Az ellenőrzés lezárult: ez az aktivitás nem kap területet és pontot.",
    daily: "Napi pont-összegzés",
    follower: "Új követő",
    followRequest: "Követési kérés",
    report: "Bejelentésed elbírálva",
    reportUpheld: "Megvizsgáltuk a bejelentésedet, és intézkedtünk.",
    reportDismissed: "Megvizsgáltuk a bejelentésedet — szabályszegést nem találtunk.",
    invite: "Banda-meghívó",
    removed: "Kirúgtak egy bandából",
    streak: "A sorozatod él — ma még nem mozogtál",
    streakOne: "Tegyél meg ma egy sétát, hogy a sorozatod folytatódjon.",
    defended: "Sikertelen támadás ellened",
    messageRequest: "Új üzenetkérelem",
    image: "Képet küldött.",
    grund: "grund",
    runRecord: "futó",
    walkRecord: "sétás",
    rideRecord: "bringás",
    stolenBody: (actor: string, area: string, count: string) => `${actor} elvett tőled ${area} területet (${count} cellát).`,
    commented: (actor: string) => `${actor} hozzászólt az aktivitásodhoz`,
    replied: (actor: string) => `${actor} válaszolt a hozzászólásodra`,
    liked: (actor: string) => `${actor} kedvelte az aktivitásodat`,
    likedMany: (actor: string, _count: number, others: string) => `${actor} és ${others} másik kedvelte az aktivitásodat`,
    followedActivity: (actor: string) => `${actor} új aktivitást rögzített`,
    newFollowerBody: (actor: string) => `${actor} követni kezdett téged.`,
    followRequestBody: (actor: string) => `${actor} követni szeretne téged.`,
    dailyBody: (gp: string) => `+${gp} GP a tartott grundodért.`,
    inviteBody: (actor: string, crew: string) => `${actor} meghívott a(z) „${crew}” bandába.`,
    joined: (actor: string) => `${actor} belépett a bandába.`,
    left: (actor: string) => `${actor} kilépett a bandából.`,
    removedBody: (crew: string) => `Eltávolítottak a(z) „${crew}” bandából.`,
    post: (actor: string) => `${actor} új posztot írt a hírfolyamba.`,
    streakBody: (days: string) => `${days} napja tart a sorozatod. Egy rövid séta ma is elég a folytatásához.`,
    record: (distance: string) => `Új egyéni rekord: ${distance}`,
    recordBody: (time: string, improvement: string, sport: string, previous: string) => `${time} — ${improvement} másodperccel jobb a korábbi ${sport} legjobbnál (${previous}).`,
    mentioned: (actor: string) => `${actor} megemlített egy hozzászólásban`,
    chatReply: (actor: string, crew: string) => `${actor} válaszolt neked a(z) ${crew} chatjében`,
    chatMention: (actor: string, crew: string) => `${actor} megemlített a(z) ${crew} chatjében`,
    crewDaily: (crew: string) => `${crew} — mai mérleg`,
    crewDailyBody: (area: string, gp: string) => `${area} terület és ${gp} GP gyűlt össze ma.`,
    defendedBody: (count: string) => `${count} cellád védelme 1-gyel csökkent, de megtartottad a grundod.`,
    requestBody: (actor: string) => `${actor} üzenetet küldött neked.`,
  },
  notificationPanel: {
    title: "Értesítések",
    clearLabel: "Összes értesítés törlése",
    clear: "Összes törlése",
    readAll: "Összes megjelölése olvasottként",
    close: "Bezárás",
    clearWarning: "Törlöd az összes értesítést? Ez nem vonható vissza.",
    cancel: "Mégse",
    delete: "Törlöm",
    loading: "Betöltés…",
    empty: "Még nincs értesítésed.",
    more: "További értesítések betöltése",
  },

  remainingUI: {
    membersOnly: 'Csak tagoknak',
    quietFeed: 'Csend van',
    readAllTitle: 'Mind olvasott',
    slide: (n: number) => `${n}. lap`,
    exitHint: "Még egyszer a kilépéshez",
    carousel: "lapozó",
    community: "Közösség",
    feed: "hírfolyam",
    emptyMap: "Nincs mit megjeleníteni",
    closedCells: "A bezárt terület hexagonjai",
    specification: "Specifikáció",
    refreshing: "Frissítés folyamatban.",
    topRival: "Fő rivális ·",
    details: "Részletek",
    customRate: "Egyéni lejátszási sebesség",
    rateExample: "pl. 2.5",
    feedFailed: "Nem sikerült betölteni az aktivitásokat. Próbáld újra.",
    uploadMissing: "A feltöltés nincs beállítva.",
    firebaseMissing: "A Firebase nincs konfigurálva. Lásd .env.example.",
    backendMissing: "A háttérszolgáltatás még nincs beállítva (VITE_API_BASE_URL).",
    actionFailed: "A művelet nem sikerült. Próbáld újra.",
    newTerritory: "Új terület!",
    newCapture: "Új zsákmány!",
    arrived: "Megérkeztél!",
    storageOpen: "IndexedDB megnyitása sikertelen",
    storageRequest: "IndexedDB művelet sikertelen",
    customMultiplier: (label: string) => `${label} egyéni szorzó`,
    rivalSummary: (encounters: string, area: string, gained: string, lost: string) => `${encounters} összecsapás, ${area} gazdát cserélt terület: ${gained} szerzett, ${lost} vesztett mező`,
  },

  saveActivity: {
    photoLimit: (limit: number) => `Legfeljebb ${limit} kép tartozhat egy aktivitáshoz.`,
    photosSkipped: (limit: number) => `Csak ${limit} kép fér el, a többit kihagytam.`,
    apiUnavailable: 'A háttérszolgáltatás nincs beállítva, a mentés nem megy.',
    saving: 'Mentés…',
    save: 'Mentés',
    saveFailed: 'A mentés nem sikerült. Ellenőrizd az internetkapcsolatot, és próbáld újra.',
    saved: 'Elmentve.',
    view: 'Megnézem',
    name: 'Név',
    namePlaceholder: 'Pl. Reggeli kör a Duna-parton',
    nameHint: 'Ha üresen hagyod, a napszak és a mozgásforma adja a nevet.',
    description: 'Leírás',
    descriptionPlaceholder: 'Milyen volt? Mi történt útközben?',
    photos: 'Képek',
    removePhoto: 'Kép eltávolítása',
    photo: 'Kép',
    discardLabel: 'Aktivitás elvetése',
    discardTitle: 'Biztosan elveted ezt az aktivitást?',
    discardBody: 'A rögzítés már feltöltődött, ezért az elvetés törli az aktivitást. Az adatait 30 napig visszaállíthatóan megőrizzük. A már kiosztott GP és a területek történeti állapota nem változik.',
    cancel: 'Mégse',
    discardFailed: 'Az elvetés nem sikerült. Próbáld újra.',
    confirmDiscard: 'Elvetem',
    discard: 'Elvetés',
    preparingPhoto: (which: string) => `Kép előkészítése…${which}`,
    uploadingPhoto: (which: string) => `Kép feltöltése…${which}`,
  },

  photoErrors: {
    processingFailed: 'A böngésző nem tudta feldolgozni a képet.',
    compressionFailed: 'Ezt a képet nem sikerült elég kicsire tömöríteni. Válassz másikat.',
    preparationFailed: 'A képet nem sikerült előkészíteni.',
    videoUnsupported: 'Videót nem lehet feltölteni, csak képet.',
    imagesOnly: 'Csak képet lehet feltölteni.',
    sourceTooLarge: (mb: number) => `A kiválasztott kép túl nagy (legfeljebb ${mb} MB lehet).`,
    emptyFile: 'A kiválasztott fájl üres.',
    heicUnsupported: 'Ezt a HEIC-képet a készülék böngészője nem tudja megnyitni. A telefon Beállítások → Kamera → Formátum menüjében válaszd a „Legjobb kompatibilitás" lehetőséget, vagy tölts fel JPEG-et.',
    imageUnsupported: 'Ezt a képet nem sikerült megnyitni. Lehet, hogy sérült vagy nem támogatott formátum.',
    uploadUnavailable: 'A képfeltöltés nincs beállítva.',
    uploadLimit: (limit: number) => `Legfeljebb ${limit} képet lehet feltölteni.`,
    unauthorized: 'A kép feltöltését a szerver elutasította. Lehet, hogy lejárt a bejelentkezésed — lépj ki és vissza.',
    unauthenticated: 'A feltöltéshez be kell jelentkezni. Lépj ki és vissza.',
    retryLimit: 'A feltöltés megszakadt a hálózat miatt. Próbáld újra, lehetőleg jobb térerőnél.',
    quotaExceeded: 'A tárhely megtelt. Szólj a fejlesztőknek.',
    canceled: 'A feltöltés megszakadt.',
    uploadFailed: 'A kép feltöltése nem sikerült. Ellenőrizd az internetkapcsolatot, és próbáld újra.',
    compressedLimit: 'A tömörített kép legfeljebb 5 MB lehet.',
    pickedLimit: 'A kiválasztott kép legfeljebb 5 MB lehet.',
    compressedTooLarge: 'A tömörített kép is nagyobb 5 MB-nál. Válassz kisebb képet.',
  },

  trackingOverlay: {
    planningMap: 'Térkép leporolása…',
    planningHexes: 'Ötszögek hatszögesítése…',
    planningBush: 'Próbálunk nem beküldeni egy bokorba…',
    planningAsphalt: 'Aszfalt-molekulák elemzése…',
    planningHint: 'Az útvonaltervezés a valós úthálózatot követi.',
    savingMovement: 'Mentjük a mozgásodat…',
    savingTerritory: 'Elszámoljuk a megszerzett területet…',
    savingLargeLoop: 'Nagy kör — ez eltarthat egy kicsit.',
    savingStillWorking: 'Még dolgozunk rajta — a nagyon nagy kör egy percig is eltarthat.',
    elapsed: (time: string) => `Eltelt: ${time}`,
  },

  news: {
    tab: 'Hírek',
    search: 'Keresés',
    clearSearch: 'Keresés törlése',
    all: 'Összes',
    saved: 'Mentett',
    filters: 'Szűrők',
    empty: 'Még nincs hír.',
    emptySaved: 'Nincs mentett hír.',
    emptySearch: 'Nincs találat.',
    loadMore: 'Továbbiak',
    loadFailed: 'A hírek most nem tölthetők be.',
    author: 'GRUNDO',
    more: 'Tovább',
    less: 'Kevesebb',
    heart: 'Szív',
    comments: 'Hozzászólások',
    report: 'Jelentés',
    reportTitle: 'Hír jelentése',
    share: 'Megosztás',
    linkCopied: 'Link másolva',
    bookmark: 'Mentés',
    unbookmark: 'Mentés visszavonása',
    playVideo: 'Videó lejátszása',
    imageAlt: 'Kép a hírhez',
    notFound: 'Ez a hír már nem elérhető.',
    back: 'Vissza a hírekhez',
    actionFailed: 'Nem sikerült. Próbáld újra.',
    sheet: {
      title: 'Hozzászólások',
      newest: 'Legújabb',
      oldest: 'Legrégebbi',
      sort: 'Rendezés',
      empty: 'Még nincs hozzászólás.',
      loading: 'Betöltés…',
      placeholder: 'Írj hozzászólást…',
      send: 'Küldés',
      reply: 'Válasz',
      replyingTo: 'Válasz neki: ',
      cancel: 'Mégse',
      edit: 'Szerkesztés',
      editing: 'Szerkesztés',
      save: 'Mentés',
      delete: 'Törlés',
      deleteConfirm: 'Törlöd a hozzászólást?',
      deleted: 'Törölt hozzászólás',
      hidden: 'Elrejtett hozzászólás',
      edited: 'módosítva',
      heart: 'Szív',
      like: 'Tetszik',
      report: 'Jelentés',
      reportTitle: (name: string) => `${name} hozzászólásának jelentése`,
      hide: 'Elrejtés',
      restore: 'Visszaállítás',
      remove: 'Végleges törlés',
      removeConfirm: 'Véglegesen törlöd? Nem vonható vissza.',
      moderation: 'Moderálás',
      loadFailed: 'A hozzászólások most nem tölthetők be.',
      count: (n: number) => `${n} hozzászólás`,
      sendFailed: 'Nem sikerült elküldeni.',
    },
  },

  communityTabs: {
    news: 'Hírek',
    discover: 'Felfedezés',
    crews: 'Bandák',
    challenges: 'Kihívások',
    leaderboard: 'Toplista',
    ariaLabel: 'Közösség fülek',
  },

  profileTabs: {
    profile: 'Profil',
    badges: 'Jelvények',
    passport: 'Útlevél',
    rivals: 'Riválisok',
    crews: 'Bandák',
    missions: 'Küldetések',
    stats: 'Statisztika',
    ariaLabel: 'Profil fülek',
  },

  profile: {
    title: 'Profilom',
    settings: 'Beállítások',
    fallbackName: 'Profil',
    avatar: {
      edit: 'Profilkép feltöltése vagy módosítása',
      uploadFailed: 'Nem sikerült feltölteni a képet.',
      uploading: 'Profilkép feltöltése…',
      editHint: 'Koppints a képre a módosításhoz',
    },
    stats: {
      level: 'szint',
      myGrund: 'grundom',
      activity: 'aktivitás',
      followers: 'követő',
      following: 'követett',
      openList: (value: string, label: string) => `${value} ${label} — lista megnyitása`,
    },
    level: {
      progressAria: 'Haladás a következő szintig',
      remaining: (gp: string, nextName: string) => `Még ${gp} a következő szintig · ${nextName}`,
      maxReached: 'A legmagasabb szinten vagy — innen már csak a terület nő.',
      higher: 'Magasabb szinten:',
      lower: 'Alacsonyabb szinten:',
      standingUnavailable: 'A globális szinthelyezés most nem tölthető be.',
      standingLoading: 'Globális szinthelyezés betöltése…',
      viewLevels: 'Szintek megtekintése',
    },
    week: {
      unavailable: 'A heti összesítő nem tölthető be.',
      loading: 'Heti összesítő betöltése…',
      title: 'Ez a hét',
      activity: 'aktivitás',
      time: 'idő',
      averagePace: 'átlagtempó',
      points: 'pont',
      dayLabel: (index: number) => ['H', 'K', 'Sz', 'Cs', 'P', 'Szo', 'V'][index] ?? '',
    },
    streak: {
      ariaLabel: 'Heti sorozat',
      title: 'Heti sorozat',
      week: (weeks: number) => `${weeks}. hét`,
      securedBefore: 'Ez a hét ',
      securedEmphasis: 'megvan',
      securedAfter: (required: number, nextWeeks: number) =>
        ` — ${required} aktív nap teljesítve. Hétfőn a sorozatod ${nextWeeks} hétre nő.`,
      missingBefore: 'Még ',
      activeDays: (count: number) => `${count} aktív nap`,
      missingAfter: (keepStreak: boolean) =>
        ` kell ezen a héten, hogy a hét beszámítson${keepStreak ? ' és a sorozat ne vesszen el' : ''}.`,
      rule: (required: number) => `Egy nap egyszer számít — ${required} különböző napon kell mozogni.`,
    },
    grid: {
      territory: 'Grundod mérete',
      cells: 'Birtokolt mező',
      distance: 'Megtett táv',
      streak: 'Sorozat',
      days: (_count: number, formatted: string) => `${formatted} nap`,
    },
    badges: 'Jelvények',
    activities: 'Aktivitásaid',
    levelSheet: {
      ladder: 'Szintlétra',
      title: 'Az összes szint',
      legend: 'Jelmagyarázat',
      earned: 'Megszerezve',
      current: 'Aktuális',
      ahead: 'Még előtted',
      firstLevel: 'Kezdőszint',
      totalGp: (gp: string) => `${gp} összesen`,
      standingLoading: 'Globális helyezés betöltése…',
      standingUnavailable: 'A globális helyezés most nem tölthető be.',
      standingAria: 'Helyed a szintmezőnyben',
      higherLevel: 'magasabb szinten',
      lowerLevel: 'alacsonyabb szinten',
      basedOnProfiles: (_count: number, formatted: string) => `${formatted} profil alapján`,
      sameLevel: (percent: string, profiles: string) => `Azonos szinten: ${percent} · ${profiles}`,
    },
  },

  activity: {
    types: {
      run: 'Futás',
      walk: 'Séta',
      ride: 'Bringa',
    },
    autoTitle: {
      night: 'Éjszakai',
      morning: 'Reggeli',
      forenoon: 'Délelőtti',
      afternoon: 'Délutáni',
      evening: 'Esti',
      weekday: (index: number) =>
        ['Vasárnapi', 'Hétfői', 'Keddi', 'Szerdai', 'Csütörtöki', 'Pénteki', 'Szombati'][index] ?? '',
      movement: {
        run: 'futás',
        walk: 'séta',
        ride: 'bringázás',
      },
    },
    detail: {
      loading: 'Betöltés…',
      title: 'Aktivitás',
      unavailable: 'Ez az aktivitás nem érhető el.',
      retry: 'Újrapróbálom',
      map: {
        routeHidden: 'Az útvonal rejtve',
        noSavedRoute: 'Nincs elmentett útvonal',
        unavailable: 'A térkép nem elérhető',
        distance: 'táv',
        movingTime: 'mozgásidő',
        exitFullscreen: 'Kilépés a teljes képernyőből',
        fullscreen: 'Térkép teljes képernyőn',
      },
      openProfile: (username: string) => `${username} profiljának megnyitása`,
      cancelEditing: 'Szerkesztés megszakítása',
      edit: 'Aktivitás szerkesztése',
      delete: 'Aktivitás törlése',
      report: 'Aktivitás jelentése',
      deleteDialog: {
        title: 'Biztosan törlöd ezt az aktivitást?',
        description:
          'Az aktivitás azonnal eltűnik, az adatait 30 napig visszaállíthatóan megőrizzük. A már kiosztott GP és a területek történeti állapota nem változik.',
        cancel: 'Mégse',
        failed: 'Nem sikerült törölni az aktivitást.',
      },
      openPhoto: (index: number) => `${index}. kép megnyitása`,
      trust: {
        rejected: 'Ez az aktivitás nem számított bele a játékba.',
        pending: 'Ez az aktivitás ellenőrzésre vár.',
        detailsPrivate: 'A részletes ellenőrzési adatok biztonsági okból nem nyilvánosak.',
        appeal: {
          open: 'Fellebbezés',
          title: 'Fellebbezés',
          hint: 'Írd le röviden, mi történt — például alagút, rossz GPS-jel. Egy ember nézi meg.',
          placeholder: 'Mi történt az aktivitás közben?',
          send: 'Fellebbezés küldése',
          cancel: 'Mégse',
          sent: 'Megkaptuk a fellebbezésedet. Egy moderátor 24 órán belül megnézi.',
          resolved: 'A fellebbezésedet elbíráltuk.',
          failed: 'Nem sikerült elküldeni a fellebbezést.',
        },
      },
      stats: {
        title: 'Statisztikák',
        distance: 'táv',
        movingTime: 'mozgásidő',
        totalTime: 'teljes idő',
        elevation: 'emelkedés',
        routeCells: 'útvonalmező',
      },
      game: {
        ariaLabel: 'Játékbeli eredmény',
        title: 'Grund',
        claimedArea: 'szerzett terület',
        closedLoop: 'bezárt kör',
        claimedCells: 'foglalt mező',
        gp: {
          base: 'alappont',
          claim: 'bezárt terület',
          steal: 'idegentől elvéve',
          breakthrough: 'áttörés',
          streakMultiplier: 'sorozat-szorzó',
        },
      },
      fates: {
        title: 'A mezők megoszlása',
        cells: (_count: number) => 'mező',
        free: { label: 'új', hint: 'gazdátlan mező volt' },
        stolen: { label: 'elvett', hint: 'valaki mástól' },
        reclaimed: { label: 'megerősített', hint: 'már a tiéd volt, erősödött' },
        breakthrough: { label: 'áttört', hint: 'védett mező, nem cserélt gazdát' },
        summary: (parts: string) => `A mezők megoszlása — ${parts}.`,
      },
      splits: {
        noteMiles: 'A rövidebb oszlop a gyorsabb kör. A felirat a tempó (perc/mi).',
        title: 'Részidők',
        note: 'A rövidebb oszlop a gyorsabb kör. A felirat a tempó (perc/km).',
        fullLabel: (index: number) => `${index}.`,
      },
      privacy:
        'Te a teljes útvonalat látod. Másoknak az eleje és a vége rejtve marad — a védőkör méretét a Beállításokban módosíthatod.',
      /** COROS condition (DECISIONS #124): the attribution stays in English, word for word. */
      poweredByCoros: (device: string | null) => (device ? `Powered by COROS · ${device}` : 'Powered by COROS'),
    },
  },

  feed: {
    viewLabel: 'Feed nézet',
    everyone: 'Mindenki',
    following: 'Követed',
    areaFilter: 'Területi szűrés',
    global: 'Globális',
    local: 'Helyi',
    typeFilter: 'Mozgásforma szerinti szűrés',
    dateFilter: 'Dátumszűrés',
    period: 'Időszak',
    dates: {
      today: 'Ma',
      week: 'Hét',
      month: 'Hónap',
      always: 'Mindig',
      custom: 'Egyedi',
      from: 'Ettől',
      until: 'Eddig',
    },
    radiusLabel: 'Ekkora körzetben:',
    invalidDateRange: 'Az egyedi időszak kezdete nem lehet később a végénél.',
    locationRequired:
      'A helyi nézethez tudnunk kell, hol vagy. Engedélyezd a helyhozzáférést, vagy válts a Globális nézetre.',
    locating: 'Helymeghatározás…',
    filteredEmpty: 'Van itt aktivitás, csak nem ilyen mozgásformában. Kapcsold vissza a többit a szűrőn.',
    retry: 'Újrapróbálom',
    loading: 'Betöltés…',
    loadMore: 'Továbbiak betöltése',
    empty: {
      mine: {
        title: 'Még nincs aktivitásod',
        description:
          'Zárj be egy kört, és a közrezárt terület a tiéd lesz. Minden méter pontot ér — akkor is, ha nem zárul a kör.',
        action: 'Kezdd az első aktivitásod',
      },
      following: {
        title: 'Üres a követett feed',
        description:
          'Vagy még nem követsz senkit, vagy akiket követsz, épp nem mozogtak. A Globális feeden találsz embereket — koppints valakinek a nevére, és onnan követheted.',
      },
      user: {
        title: 'Még nincs látható aktivitás',
        description:
          'Ez a felhasználó vagy még nem rögzített aktivitást, vagy nem osztja meg őket nyilvánosan.',
      },
      local: {
        title: 'Nincs itt még senki',
        description: (radiusKm: number) =>
          `${radiusKm} km-es körzetben senki nem rögzített még aktivitást. Próbálj nagyobb sugarat, vagy legyél te az első.`,
      },
      world: {
        title: 'Nincs mit mutatni',
        description: 'Még senki nem rögzített aktivitást.',
      },
    },
    card: {
      mapAlt: 'Az aktivitás útvonala a térképen',
      hideHexes: 'Hexagonok elrejtése',
      showHexes: 'Hexagonok megjelenítése',
      routeHidden: 'Az útvonal rejtve',
      noSavedRoute: 'Nincs elmentett útvonal',
      trustPending: 'Ellenőrzés alatt',
      trustRejected: 'Elutasítva',
      mapUnavailable: 'A térkép nem elérhető',
      openProfile: (username: string) => `${username} profiljának megnyitása`,
      moreOptions: 'További lehetőségek',
      openActivity: (title: string) => `${title} megnyitása`,
      openPhoto: (index: number) => `${index}. fotó megnyitása`,
      distance: 'táv',
      duration: 'idő',
      you: 'Te',
      cellsTaken: (_count: number, formatted: string) => `Ebben az aktivitásban ${formatted} mezőt vett el tőle.`,
    },
    social: {
      openLikers: (_count: number, formatted: string) => `${formatted} kedvelő — a lista megnyitása`,
      noLikers: 'Még senki nem kedvelte — a lista megnyitása',
      unlike: 'Kedvelés visszavonása',
      like: 'Kedvelem',
      comments: 'Hozzászólások',
    },
    menu: {
      report: 'Aktivitás jelentése',
      dialogLabel: 'További lehetőségek',
      close: 'Bezárás',
      confirm: 'Rendben',
      options: 'Lehetőségek',
      mutedTitle: (username: string) => `${username} némítva`,
      mutedLead:
        'Az aktivitásai nem jelennek meg a feededben. A követés megmarad, és ő nem értesül róla. Feloldás: a profilján vagy a Beállítások → Némított felhasználók listában.',
      mute: (username: string) => `${username} némítása`,
      unfollowedTitle: (username: string) => `Már nem követed: ${username}`,
      unfollowedLead:
        'Az aktivitásai nem jelennek meg a Követed feedben. Újra követni a profiljáról tudod.',
      unfollow: 'Követés vége',
      blockedTitle: (username: string) => `${username} letiltva`,
      blockedLead:
        'Nem látjátok egymás aktivitásait, és a követés mindkét irányban megszűnt. Feloldás: Beállítások → Tiltott felhasználók.',
      block: (username: string) => `${username} letiltása`,
      failed: 'A művelet nem sikerült.',
      cancel: 'Mégse',
    },
  },

  comments: {
    dialogLabel: 'Hozzászólások',
    title: (count: number | null) => count === null ? 'Hozzászólások' : `Hozzászólások (${count})`,
    reportTitle: (username: string) => `${username} hozzászólásának jelentése`,
    loading: 'Betöltés…',
    empty: 'Még nincs hozzászólás. Legyél te az első.',
    openProfile: (username: string) => `${username} profiljának megnyitása`,
    delete: 'Hozzászólás törlése',
    deleted: 'törölt komment vagy tag',
    replyTo: (username: string) => `Válasz — ${username}`,
    replyingToPrefix: 'Válasz — ',
    reply: 'Válasz',
    report: 'Jelentés',
    emojiPicker: 'Emoji választó',
    cancelReply: 'Válasz megszakítása',
    emoji: 'Emoji',
    send: 'Küldés',
    unknownUser: 'ismeretlen',
    errors: {
      load: 'Nem sikerült betölteni.',
      send: 'A hozzászólás nem ment el.',
      delete: 'A törlés nem sikerült.',
    },
  },

  reportSheet: {
    lead: 'Mi a gond? A bejelentést moderátor nézi át. A bejelentő kiléte a bejelentett felé nem derül ki.',
    categories: {
      gpsSpoof: {
        label: 'Hamisított helyadat',
        help: 'A nyomvonal nem valódi mozgásból származik.',
      },
      vehicle: {
        label: 'Járművel tette meg',
        help: 'Autóval, motorral vagy más járművel rögzített futást vagy sétát.',
      },
      wrongType: {
        label: 'Rossz aktivitástípus',
        help: 'Például bringázást rögzített futásként.',
      },
      offensive: {
        label: 'Sértő tartalom',
        help: 'Bántó név, kép vagy szöveg.',
      },
      privacy: {
        label: 'Adatvédelmi aggály',
        help: 'Más ember adatait vagy képét osztja meg engedély nélkül.',
      },
      other: {
        label: 'Egyéb',
        help: 'Ami a fenti okok közé nem fér bele.',
      },
    },
    noteLabel: 'Leírás (nem kötelező)',
    notePlaceholder: 'Ha van olyan részlet, ami segít, írd ide.',
    cancel: 'Mégse',
    submit: 'Bejelentés elküldése',
    submitFailed: 'A bejelentést nem sikerült elküldeni.',
    sentTitle: 'Köszönjük, megkaptuk a bejelentést',
    sentLead: 'Moderátor megvizsgálja. A kiléted a bejelentett felé nem derül ki.',
    done: 'Rendben',
  },

  connections: {
    titles: {
      followers: 'Követők',
      following: 'Követettek',
      likers: 'Kedvelők',
    },
    empty: {
      followers: 'Még nincs követőd.',
      following: 'Még nem követsz senkit.',
      likers: 'Még senki nem kedvelte.',
    },
    loading: 'Betöltés…',
    loadMore: 'Továbbiak betöltése',
    truncated: (count: number) => `A legutóbbi ${count} látszik. A teljes lista lapozása még nincs kész.`,
    errors: {
      load: 'A lista most nem tölthető be. Próbáld később.',
      loadMore: 'A további sorok most nem tölthetők be. Próbáld újra.',
    },
  },

  photoLightbox: {
    dialogLabel: 'Aktivitás képei',
    label: 'Képek',
    close: 'Képnézegető bezárása',
    alt: (index: number, total: number) => `Aktivitás képe, ${index}/${total}`,
    previous: 'Előző kép',
    next: 'Következő kép',
    panHint: 'Húzd a kép mozgatásához',
    zoomHint: 'Csippents vagy görgess a nagyításhoz',
    zoomControls: 'Nagyítás vezérlése',
    zoomOut: 'Kicsinyítés',
    resetZoom: 'Nagyítás visszaállítása',
    zoomIn: 'Nagyítás',
  },

  appError: {
    title: 'Nem sikerült elindítani a GRUNDO-t',
    feedbackPrompt: 'Indítási hiba történt. Kérjük, küldj visszajelzést ezzel a szöveggel:',
    retry: 'Újrapróbálom',
  },

  apiServerErrors: {
    profile_missing: "Még nincs GRUNDO-profilod.",
    deletion_running: "A fiókod végleges törlése már elkezdődött, nem vonható vissza.",
    export_in_progress: "Az adatcsomagod épp készül.",
    activity_conflict: "Ez az azonosító már foglalt.",
    trust_missing: "Ehhez az aktivitáshoz nincs bizalmi rekord.",
    app_check_required: "Az alkalmazás hitelesítése nem sikerült. Frissítsd a GRUNDO-t, majd próbáld újra.",
    invalid_mute_duration: "A némítás 1 órára, 24 órára vagy a feloldásig szólhat.",
    content_missing: "Ez a tartalom már nem érhető el.",
    invalid_cursor: "Érvénytelen lapozási azonosító.",
    rate_limit_unavailable: "A szolgáltatás átmenetileg nem elérhető.",
    invalid_activity_id: "Hiányzó vagy hibás aktivitás-azonosító.",
    invalid_time: "Hibás időadatok.",
    too_old: "Ez az aktivitás túl régi ahhoz, hogy feldolgozzuk.",
    missing_position: "A helyi nézethez meg kell adni a pozíciót.",
    missing_user: "Hiányzik a felhasználó azonosítója.",
    invalid_scope: "Ismeretlen nézet.",
    photo_missing: "Nincs ilyen kép.",
    invalid_photos: "Hibás fotólista.",
    invalid_photo_path: "Ez a kép nem ehhez az aktivitáshoz tartozik.",
    self_like: "A saját aktivitásodat nem kedvelheted.",
    comment_missing: "Nincs ilyen hozzászólás.",
    too_many_points: "A nyomvonal túl hosszú.",
    assistant_failed: "Az asszisztens most nem válaszolt. Próbáld újra, vagy állítsd be kézzel az útvonalat.",
    conversation_missing: "Ez a beszélgetés már nincs meg.",
    forget_kind: "Ismeretlen memóriatétel.",
    place_invalid: "Érvénytelen hely.",
    invalid_push_token: "Érvénytelen értesítési azonosító.",
    invalid_account: "A fiók nyilvános vagy privát lehet.",
    invalid_visibility: "A láthatóság csak Mindenki, Követők vagy Csak én lehet.",
    reauth_required: "A fiók törléséhez lépj be újra.",
    invalid_privacy: "Mindkét privátzóna állapotát meg kell adni.",
    invalid_privacy_radius: "A védőkör 50, 100 vagy 200 méter lehet.",
    invalid_avatar_url: "A profilkép nem a saját feltöltésedre mutat.",
    missing_credentials: "Add meg a felhasználóneved és a jelszavad.",
    login_unavailable: "A felhasználónévvel belépés most nem elérhető. Lépj be az e-mail-címeddel.",
    user_disabled: "Ez a fiók fel van függesztve. Írj nekünk, ha szerinted tévedés.",
    profile_exists: "Ehhez a fiókhoz már tartozik profil.",
    username_taken: "Ez a felhasználónév már foglalt.",
    no_email: "Ehhez a fiókhoz nem tartozik e-mail-cím.",
    banda_not_found: "Nincs ilyen banda.",
    message_not_found: "Nincs ilyen üzenet.",
    cannot_mute_self: "Saját magadat nem némíthatod.",
    invite_code_exhausted: "Nem sikerült egyedi meghívókódot generálni. Próbáld újra.",
    invalid_sort: "A rendezés csak popular vagy new lehet.",
    join_request_pending: "A csatlakozási kérésed már jóváhagyásra vár.",
    missing_code: "Adj meg egy meghívókódot.",
    invalid_code: "Érvénytelen meghívókód.",
    user_not_found: "Nincs ilyen felhasználó.",
    already_invited: "Már meghívtad ezt a felhasználót.",
    invite_not_found: "Nincs függő meghívód ehhez a bandához.",
    invalid_enabled: "Az `enabled` mező kötelező, igaz/hamis értékkel.",
    invalid_banda_image_url: "A kép nem ehhez a bandához feltöltött fájlra mutat.",
    missing_branding: "Válassz profil- vagy borítóképet.",
    empty_settings: "Válassz legalább egy módosítandó beállítást.",
    invalid_permission: "Érvénytelen banda-jogosultság.",
    invalid_join_mode: "A publikus belépés módja csak azonnali vagy jóváhagyásos lehet.",
    join_request_not_found: "Ez a csatlakozási kérés már nem található.",
    invalid_role: "A szerepkör csak tag vagy moderátor lehet.",
    owner_role_locked: "Az alapító szerepköre itt nem módosítható.",
    cannot_kick_self: "Saját magadat nem rúghatod ki a bandából.",
    already_owner: "Már te vagy a banda alapítója.",
    owner_must_transfer: "Alapítóként csak akkor léphetsz ki, ha előbb átadod valakinek az alapítói rangot.",
    empty_post: "Írj valamit a posztba.",
    post_not_found: "Nincs ilyen poszt.",
    comment_not_found: "Nincs ilyen komment.",
    feed_image_missing: "Nincs ilyen posztkép.",
    invalid_note: "A megjegyzés csak szöveg lehet.",
    invalid_kind: "Ismeretlen bejelentés-típus.",
    invalid_severity: "Ismeretlen súlyosság.",
    note_required: "Írd le pár szóban, mi történt.",
    invalid_media_path: "A melléklet útvonala nem a bejelentéshez tartozik.",
    report_not_found: "Nincs ilyen bejelentés.",
    too_many_media: "Ehhez a bejelentéshez már nem fér több melléklet.",
    invalid_status: "Ismeretlen státusz.",
    empty_patch: "Nincs mit módosítani a bejelentésen.",
    invalid_live_delay: "Ismeretlen csúszás.",
    invalid_activity_type: "Ismeretlen aktivitástípus.",
    missing_username: "Élő adáshoz felhasználónév kell.",
    live_session_invalid: "Az adás lezárult vagy a kulcs érvénytelen.",
    live_session_expired: "Az adáskulcs lejárt.",
    invalid_players: "A játékoslista hiányzik.",
    self_message: "Magadnak nem küldhetsz üzenetet.",
    conversation_not_found: "Nincs ilyen beszélgetés.",
    invalid_recipient: "Hiányzik a címzett.",
    not_a_request: "Ez nem üzenetkérelem.",
    image_expired: "A kép lejárt.",
    message_image_missing: "Ehhez az üzenethez nincs kép.",
    team_report: "Az Admin üzenete nem jelenthető.",
    loop_needs_stop: "Kört egy megállón át tudunk tervezni: adj hozzá legalább egy megállót.",
    invalid_pace: "A megadott átlagtempó nem reális.",
    invalid_bearing: "A választott irány hibás.",
    invalid_refhex_mode: "Ismeretlen REFHEX játékmód.",
    invalid_refhex_score: "Érvénytelen REFHEX eredmény.",
    evidence_missing: "Ehhez a bejelentéshez nincs mentett kép.",
    already_resolved: "Ez a bejelentés már el van bírálva.",
    too_close: "A rajt és a cél gyakorlatilag egy helyen van.",
    no_route: "Erre a két pontra nem találtunk útvonalat. Próbálj másik célt, vagy tegyél be egy megállót.",
    invalid_layer: "Ismeretlen réteg.",
    invalid_bbox: "Hibás térképszakasz.",
    owner_missing: "Ez a játékos már nincs meg.",
    invalid_window: "Ismeretlen ranglista-nézet.",
    invalid_usage_session: "Érvénytelen használati munkamenet.",
    invalid_usage_duration: "Érvénytelen használati idő.",
    self_follow: "Magadat nem követheted.",
    virtual_profile_follow: "Virtuális profilt nem lehet követni.",
    snapshot_missing: "A térkép-pillanatkép még nem készült el.",
    blocked: "Előbb oldd fel a tiltást, utána követheted.",
    self_block: "Magadat nem tilthatod le.",
    request_missing: "Ez a kérés már nem aktuális.",
    self_mute: "Magadat nem némíthatod.",
    account_deletion_pending: "A fiókod törlésre vár. A folytatáshoz vond vissza a törlést.",
  },
  apiErrors: {
    apiUnconfigured: 'A háttérszolgáltatás még nincs beállítva.',
    network: 'Nincs kapcsolat a szerverrel. Ellenőrizd az internetet.',
    notJson:
      'A háttérszolgáltatás nem JSON-t adott vissza. Valószínűleg a ' +
      'VITE_API_BASE_URL a frontend címére mutat a Cloud Run URL helyett.',
    requestFailed: 'A művelet nem sikerült. Próbáld újra.',
    emptyResponse: 'A szerver üres választ adott.',
    imageLoadFailed: 'A képet nem sikerült betölteni.',
    notImage: 'A szerver nem képet adott vissza.',
  },

  common: {
    or: 'vagy',
    back: 'Vissza',
    close: 'Bezárás',
    newItem: 'új',
    signOut: 'Kijelentkezés',
  },

  locationPrimer: {
    title: 'Helyadat a rögzítéshez',
    lead: 'Az utat és a területet a helyadatodból számoljuk. A telefon kétszer kérdez:',
    stepWhileUsing: 'most, az app használatára;',
    stepBackground: 'rögzítéskor, zárolt képernyőre — enélkül a mérés megáll, ha elteszed a telefont.',
    note: 'Csak a saját aktivitásaidhoz használjuk. A Beállításokban bármikor módosíthatod.',
    continueButton: 'Tovább',
  },

  password: {
    show: 'Jelszó megjelenítése',
    hide: 'Jelszó elrejtése',
  },

  layerSwitch: {
    ariaLabel: 'Réteg: gyalogos vagy bringás',
    foot: 'Séta/Futás',
    bike: 'Bringa',
  },

  resumePrompt: {
    title: 'Bezártad az appot?',
    activitySummary: (points: number, distance: string) =>
      `Félbemaradt egy aktivitásod — ${points} pont, ${distance}. Folytatod, vagy újat kezdünk?`,
    resume: 'Folytatom',
    startNew: 'Újat kezdek',
    causes: {
      reload: 'Az oldal újratöltődött, ezért a mérés helyi mentésből állítható helyre.',
      pagehide: 'Az oldal bezárult vagy újraindult, ezért a mérés helyi mentésből állítható helyre.',
      hidden:
        'A böngésző háttérbe került; ha közben újraindult, a mérés helyi mentésből állítható helyre.',
      restarted: 'A rögzítő oldala újraindult, ezért a mérés helyi mentésből állítható helyre.',
    },
  },

  emailVerification: {
    dialogLabel: 'E-mail hitelesítés',
    lockPrompt: {
      title: 'Még nem hitelesítetted az e-mail-címedet',
      verifyNow: 'Hitelesítés most',
      hint:
        'A hitelesítést bármikor elvégezheted a Beállítások → Adatvédelem és közösség → E-mail-hitelesítés menüpont alatt.',
    },
    otp: {
      title: 'Hitelesítsd az e-mail-címed',
      leadBeforeEmail: 'Elküldtük a 6 jegyű kódot a ',
      leadAfterEmail: ' címre. A kód 15 percig érvényes.',
      digitLabel: (index: number) => `${index}. számjegy`,
      verify: 'Hitelesítés',
      resend: 'Kód újraküldése',
      resendIn: (seconds: number) => `Újraküldés ${seconds} mp múlva`,
      sent: 'Elküldtük a kódot. Nézd meg a levélszemetet is.',
      devCode: (code: string) =>
        `Fejlesztői mód — nincs beállítva e-mail-szolgáltató. A kód: ${code}`,
      sendFailed: 'Nem sikerült elküldeni a kódot.',
      invalidCode: 'A kód nem érvényes.',
      wrongAddress: 'Hibás cím? Másik e-mail-cím megadása.',
      changeLabel: 'Új e-mail-cím',
      changeHint: 'Ide küldjük az új kódot. Jelszavas belépésnél ezután ezt a címet használd.',
      changeSubmit: 'Kód küldése az új címre',
      changeCancel: 'Mégse',
      changeFailed: 'Nem sikerült módosítani a címet.',
      changed: 'Módosítottuk a címet, és elküldtük rá az új kódot.',
    },
  },

  appLock: {
    systemTitle: 'GRUNDO feloldása',
    unlockPrompt: 'Oldd fel a GRUNDO-t.',
    dialogLabel: 'A GRUNDO zárolva',
    title: 'A GRUNDO zárolva',
    description: 'Oldd fel arccal, ujjlenyomattal, mintával vagy jelkóddal.',
    unlock: 'Feloldás',
    signOut: 'Kijelentkezés',
    errors: {
      cancelled: 'A feloldás megszakadt.',
      failed: 'A feloldás nem sikerült. Próbáld újra.',
      noHardware: 'Ez az eszköz nem ismer arc-, ujjlenyomat- vagy jelkód-feloldást.',
      noneEnrolled: 'Nincs beállítva képernyőzár az eszközön.',
      notInstalled: 'Ez az alkalmazásverzió még nem tartalmazza a feloldást.',
      unavailable: 'A feloldás most nem érhető el.',
    },
  },

  profileError: {
    timeout: 'A profil betöltése időtúllépés miatt megszakadt. Ellenőrizd az internetkapcsolatot, majd próbáld újra.',
    loadFailed: 'Nem sikerült betölteni a profilt.',
    title: 'Nem sikerült betölteni a profilodat',
    unknown: 'Ismeretlen hiba történt.',
    retry: 'Újrapróbálom',
  },

  deletionPending: {
    cancelFailed: 'A visszavonás nem sikerült. Próbáld újra.',
    title: 'Visszavonod a törlést?',
    pendingBeforeDate: 'A fiókod törlését kérted. A végleges törlés ',
    pendingAfterDate:
      '-kor esedékes — utána nincs visszaút. Ha most nem vonod vissza, a belépés kijelentkeztet.',
    cancel: 'Törlés visszavonása',
    running: 'A fiókod végleges törlése már folyamatban van, ez már nem vonható vissza.',
  },

  territory: {
    title: 'Grund',
    leaderboardTitle: 'Toplista',
    controls: {
      hideCells: 'Mezők elrejtése',
      showCells: 'Mezők megjelenítése',
      mapOnly: 'Csak a térkép',
      showData: 'Adatok megjelenítése',
      closeLeaderboard: 'Toplista bezárása',
      viewVersus: "Nézet: Én vs. mindenki",
      viewHistory: "Nézet: Saját történet",
      viewNormal: "Nézet: mindenki a saját színében",
    },
    stats: {
      yourGrund: 'a Te grundod',
      yourCells: 'mezőid',
      otherCells: 'másoké',
    },
    mapUnavailable: 'A térképhez Mapbox-token kell. Enélkül a birtokviszony nem jeleníthető meg.',
    closeHelp: 'Magyarázat bezárása',
    help: {
      showingFree: 'A halvány mezők szabadok — bárkié lehetnek, aki bezár egy kört körülöttük.',
      zoomIn: 'Közelíts rá, és a szabad mezők is megjelennek.',
      defense:
        'A védelem naponta egy szintet veszít, de sosem esik 1 alá: a terület a tiéd marad, csak egyre könnyebb elvenni.',
    },
    partialZoomHint: 'Közelíts rá a térképre, hogy lásd a mezőket!',
    legend: {
      title: 'Jelmagyarázat',
      own: 'A te területed',
      rival1: 'Másé · 1-es',
      rival2: 'Másé · 2-es',
      rival3: 'Másé · 3-as',
      rival4: 'Másé · 4-es',
      rival5: 'Másé · 5-ös',
      free: 'Szabad',
      enclosed: "Bekerítetted",
      visited: "Itt jártál",
    },
  },

  leaderboard: {
    windows: {
      day: 'Napi',
      week: 'Heti',
      month: 'Havi',
      alltime: 'Mindenkori',
    },
    empty: {
      alltime: 'Még senkinek nincs területe. Légy te az első.',
      day: 'Ma még senki nem szerzett területet.',
      week: 'Ezen a héten még senki nem szerzett területet.',
      month: 'Ebben a hónapban még senki nem szerzett területet.',
    },
    periodLabel: 'Ranglista időszaka',
    loading: 'Betöltés…',
    openProfile: (username: string) => `${username} profiljának megnyitása`,
    podiumAria: (rank: number, username: string, area: string, cells: string) =>
      `${rank}. hely: ${username}, ${area}, ${cells} mező`,
    loadMore: 'Továbbiak betöltése',
    loadMoreFailed: 'Nem sikerült betölteni. Próbáld újra.',
    crewTitle: 'Banda-toplista',
    closeCrew: 'Banda-toplista bezárása',
    crewEmpty: {
      alltime: 'Még egyik bandának sincs területe.',
      day: 'Ma még egyik banda sem szerzett területet.',
      week: 'Ezen a héten még egyik banda sem szerzett területet.',
      month: 'Ebben a hónapban még egyik banda sem szerzett területet.',
    },
    openCrew: (name: string) => `${name} megnyitása`,
    privateCrew: 'Privát banda',
    crewPodiumAria: (rank: number, name: string, area: string) => `${rank}. hely: ${name}, ${area}`,
  },

  cellOwnerCard: {
    dialogLabel: 'A mező tulajdonosa',
    loading: 'Betöltés…',
  },

  mapControls: {
    zoomGroup: 'Térkép nagyítása',
    zoomIn: 'Nagyítás',
    zoomOut: 'Kicsinyítés',
    hideHexes: 'Hexagonok elrejtése',
    showHexes: 'Hexagonok megjelenítése',
    hexesMode: {
      all: 'Mezők: mind látszik',
      own: 'Mezők: csak a most szerzettek',
      off: 'Mezők: rejtve',
    },
    disableTerrain: 'Domborzat kikapcsolása',
    enableTerrain: 'Domborzat bekapcsolása',
    terrainOff: 'Domborzat ki',
    terrainOn: 'Domborzat be',
    view3dTo2d: '3D nézet, váltás 2D-re',
    view2dTo3d: '2D nézet, váltás 3D-re',
    view3dTo2dTitle: '3D nézet · váltás 2D-re',
    view2dTo3dTitle: '2D nézet · váltás 3D-re',
    northUp: 'Észak legyen felfelé',
    headingUp: 'Térkép forgatása a haladási irányba',
    northUpTitle: 'Észak felfelé',
    headingUpTitle: 'Haladási irány felfelé',
    recenter: 'Vissza a pozíciómra',
    findPosition: 'Helyzet keresése',
  },

  rivalBadge: {
    label: 'RIVÁLIS',
    ariaLabel: 'rivális játékos',
  },

  settings: {
    localeRow: {
      label: 'Nyelv és mértékegység',
      description: 'A felület nyelve, a távolság és a hőmérséklet egysége',
    },
    locale: {
      title: 'Nyelv és mértékegység',
      languageLabel: 'Nyelv',
      languageAuto: 'Automatikus',
      languageAutoHint:
        'A telefon nyelvét követi: magyar telefonon magyar, minden más nyelvnél angol.',
      languageAutoHintRollout:
        'Amíg az angol fordítás készül, az automatikus beállítás magyar marad.',
      unitsLabel: 'Mértékegység',
      unitsAuto: 'Automatikus',
      unitsMetric: 'Metrikus',
      unitsImperial: 'Angolszász',
      unitsAutoHint: 'Az USA-ban angolszász, máshol metrikus.',
      unitsAutoHintRollout: 'Amíg az angol fordítás készül, az automatikus beállítás metrikus marad.',
      unitsMetricHint: 'Kilométer, méter, km/h, °C.',
      unitsImperialHint: 'Mérföld, láb, mph, °F.',
      areaNote: 'A terület mindig km²-ben látszik, hogy mindenkié összemérhető legyen.',
      active: 'Most érvényes',
    },
  },
};

/** The shape every language must provide. */
export type Messages = typeof hu;
